import React, { useState, useEffect, useCallback, useRef } from 'react';
import { useOutletContext } from 'react-router-dom';
import axios from 'axios';
import { API } from '../../utils/api';
import {
  DndContext,
  closestCenter,
  KeyboardSensor,
  PointerSensor,
  useSensor,
  useSensors,
  useDroppable,
} from '@dnd-kit/core';
import {
  arrayMove,
  SortableContext,
  sortableKeyboardCoordinates,
  useSortable,
  verticalListSortingStrategy,
} from '@dnd-kit/sortable';
import { CSS } from '@dnd-kit/utilities';

// Helper functions
const getTypeLabel = (type) => {
  const labels = { 'advertisement': 'Advertisements', 'event': 'Events & Expos', 'deal': 'Deals & Offers', 'local': 'Local' };
  return labels[type] || type;
};

// Region mapping - mirrors backend extract_region_from_address logic
const REGION_MAPPING = {
  "london": "London",
  "manchester": "North West",
  "liverpool": "North West",
  "birmingham": "West Midlands",
  "leeds": "Yorkshire and the Humber",
  "sheffield": "Yorkshire and the Humber",
  "bristol": "South West",
  "newcastle": "North East",
  "nottingham": "East Midlands",
  "leicester": "East Midlands",
  "cambridge": "East of England",
  "norwich": "East of England",
  "ipswich": "East of England",
  "reading": "South East",
  "brighton": "South East",
  "southampton": "South East",
  "oxford": "South East",
  "cardiff": "Wales",
  "swansea": "Wales",
  "edinburgh": "Scotland",
  "glasgow": "Scotland",
  "belfast": "Northern Ireland"
};

const extractRegionFromAddress = (address) => {
  if (!address) return "";
  
  const addressLower = address.toLowerCase();
  
  // Check for known cities
  for (const [city, region] of Object.entries(REGION_MAPPING)) {
    if (addressLower.includes(city)) {
      return region;
    }
  }
  
  // Try to extract from address parts (second-to-last part is usually city)
  const parts = address.split(',').map(p => p.trim());
  if (parts.length >= 2) {
    return parts[parts.length - 2];
  }
  
  return "";
};

const getStatusBadge = (status) => {
  const badges = {
    'active': 'bg-green-500/20 text-green-400',
    'draft': 'bg-slate-500/20 text-slate-400',
    'scheduled': 'bg-blue-500/20 text-blue-400',
    'expired': 'bg-red-500/20 text-red-400',
    'archived': 'bg-gray-500/20 text-gray-400'
  };
  return badges[status] || badges.draft;
};

const getTypeIcon = (type) => {
  const icons = {
    'advertisement': 'M11 5.882V19.24a1.76 1.76 0 01-3.417.592l-2.147-6.15M18 13a3 3 0 100-6M5.436 13.683A4.001 4.001 0 017 6h1.832c4.1 0 7.625-1.234 9.168-3v14c-1.543-1.766-5.067-3-9.168-3H7a3.988 3.988 0 01-1.564-.317z',
    'event': 'M8 7V3m8 4V3m-9 8h10M5 21h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v12a2 2 0 002 2z',
    'deal': 'M12 8c-1.657 0-3 .895-3 2s1.343 2 3 2 3 .895 3 2-1.343 2-3 2m0-8c1.11 0 2.08.402 2.599 1M12 8V7m0 1v8m0 0v1m0-1c-1.11 0-2.08-.402-2.599-1M21 12a9 9 0 11-18 0 9 9 0 0118 0z',
    'local': 'M17.657 16.657L13.414 20.9a1.998 1.998 0 01-2.827 0l-4.244-4.243a8 8 0 1111.314 0z'
  };
  return icons[type] || icons.advertisement;
};

// Sortable Table Row Component
// Tooltip wrapper component for action buttons
const ActionButton = ({ onClick, className, tooltip, children }) => (
  <div className="relative group/tooltip">
    <button onClick={onClick} className={className}>
      {children}
    </button>
    <div className="absolute bottom-full left-1/2 -translate-x-1/2 mb-2 px-2 py-1 text-xs font-medium text-white bg-slate-900 rounded shadow-lg opacity-0 invisible group-hover/tooltip:opacity-100 group-hover/tooltip:visible transition-all whitespace-nowrap z-50 pointer-events-none">
      {tooltip}
      <div className="absolute top-full left-1/2 -translate-x-1/2 border-4 border-transparent border-t-slate-900"></div>
    </div>
  </div>
);

const SortableTableRow = ({ item, onEdit, onActivate, onDeactivate, onArchive, onRestore, onDelete, onDuplicate, showActions = true }) => {
  const {
    attributes,
    listeners,
    setNodeRef,
    transform,
    transition,
    isDragging,
  } = useSortable({ id: item.id });

  const style = {
    transform: CSS.Transform.toString(transform),
    transition,
    opacity: isDragging ? 0.5 : 1,
    zIndex: isDragging ? 1000 : 'auto',
  };

  const siteLocaleEnabled = item.targeting?.site_locale?.enabled === true;
  const aroundMeEnabled = item.targeting?.around_me?.enabled === true;

  return (
    <tr
      ref={setNodeRef}
      style={style}
      className={`hover:bg-slate-700/30 ${isDragging ? 'bg-slate-700/50' : ''}`}
    >
      {/* Drag Handle */}
      <td className="py-4 w-10">
        <div className="relative group/tooltip">
          <button
            {...attributes}
            {...listeners}
            className="p-1.5 text-slate-500 hover:text-slate-300 cursor-grab active:cursor-grabbing touch-none"
          >
            <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 8h16M4 16h16" />
            </svg>
          </button>
          <div className="absolute bottom-full left-1/2 -translate-x-1/2 mb-2 px-2 py-1 text-xs font-medium text-white bg-slate-900 rounded shadow-lg opacity-0 invisible group-hover/tooltip:opacity-100 group-hover/tooltip:visible transition-all whitespace-nowrap z-50 pointer-events-none">
            Drag to reorder
            <div className="absolute top-full left-1/2 -translate-x-1/2 border-4 border-transparent border-t-slate-900"></div>
          </div>
        </div>
      </td>
      <td className="py-4 cursor-pointer" onClick={() => onEdit(item)}>
        <p className="text-white font-medium">{item.title}</p>
        <p className="text-slate-400 text-sm truncate max-w-xs">{item.short_description}</p>
      </td>
      <td className="py-4">
        <span className={`px-2 py-1 rounded-full text-xs font-medium ${getStatusBadge(item.status)}`}>
          {item.status}
        </span>
      </td>
      <td className="py-4 text-center">
        {siteLocaleEnabled ? (
          <span className="text-green-400" title="Site Locale enabled">●</span>
        ) : (
          <span className="text-slate-600" title="Site Locale disabled">○</span>
        )}
      </td>
      <td className="py-4 text-center">
        {aroundMeEnabled ? (
          <span className="text-green-400" title="Around Me enabled">●</span>
        ) : (
          <span className="text-slate-600" title="Around Me disabled">○</span>
        )}
      </td>
      <td className="py-4">
        {item.is_featured ? <span className="text-amber-400">★</span> : <span className="text-slate-600">☆</span>}
      </td>
      <td className="py-4 text-slate-300">{item.views}</td>
      <td className="py-4 text-slate-300">{item.clicks}</td>
      {showActions && (
      <td className="py-4">
        <div className="flex items-center gap-1">
          {(item.status === 'draft' || item.status === 'scheduled') && (
            <ActionButton onClick={() => onActivate(item.id)} className="p-1.5 text-green-400 hover:text-green-300 hover:bg-green-400/10 rounded" tooltip="Activate">
              <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
              </svg>
            </ActionButton>
          )}
          {item.status === 'active' && (
            <ActionButton onClick={() => onDeactivate(item.id)} className="p-1.5 text-yellow-400 hover:text-yellow-300 hover:bg-yellow-400/10 rounded" tooltip="Deactivate">
              <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M10 9v6m4-6v6m7-3a9 9 0 11-18 0 9 9 0 0118 0z" />
              </svg>
            </ActionButton>
          )}
          {item.status !== 'archived' && (
            <ActionButton onClick={() => onArchive(item.id)} className="p-1.5 text-amber-400 hover:text-amber-300 hover:bg-amber-400/10 rounded" tooltip="Archive">
              <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 8h14M5 8a2 2 0 110-4h14a2 2 0 110 4M5 8v10a2 2 0 002 2h10a2 2 0 002-2V8m-9 4h4" />
              </svg>
            </ActionButton>
          )}
          {item.status === 'archived' && (
            <ActionButton onClick={() => onRestore(item.id)} className="p-1.5 text-blue-400 hover:text-blue-300 hover:bg-blue-400/10 rounded" tooltip="Restore">
              <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 4v5h.582m15.356 2A8.001 8.001 0 004.582 9m0 0H9m11 11v-5h-.581m0 0a8.003 8.003 0 01-15.357-2m15.357 2H15" />
              </svg>
            </ActionButton>
          )}
          <ActionButton onClick={() => onDuplicate(item.id)} className="p-1.5 text-cyan-400 hover:text-cyan-300 hover:bg-cyan-400/10 rounded" tooltip="Duplicate">
            <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 16H6a2 2 0 01-2-2V6a2 2 0 012-2h8a2 2 0 012 2v2m-6 12h8a2 2 0 002-2v-8a2 2 0 00-2-2h-8a2 2 0 00-2 2v8a2 2 0 002 2z" />
            </svg>
          </ActionButton>
          <ActionButton onClick={() => onDelete(item.id)} className="p-1.5 text-red-400 hover:text-red-300 hover:bg-red-400/10 rounded" tooltip="Delete">
            <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16" />
            </svg>
          </ActionButton>
        </div>
      </td>
      )}
    </tr>
  );
};

const getTypeColor = (type) => {
  const colors = {
    'advertisement': { bg: 'bg-blue-500/20', text: 'text-blue-400' },
    'event': { bg: 'bg-green-500/20', text: 'text-green-400' },
    'deal': { bg: 'bg-amber-500/20', text: 'text-amber-400' },
    'local': { bg: 'bg-purple-500/20', text: 'text-purple-400' }
  };
  return colors[type] || colors.advertisement;
};

// Droppable Group Zone - allows dropping items into groups (even empty ones)
const DroppableGroupZone = ({ groupId, children, isEmpty }) => {
  const { isOver, setNodeRef } = useDroppable({
    id: `group-${groupId}`,
    data: { type: 'group', groupId }
  });

  return (
    <tr 
      ref={setNodeRef}
      className={`${isEmpty ? '' : ''} ${isOver ? 'bg-purple-500/20' : ''}`}
    >
      {children}
    </tr>
  );
};

// Mobile Detail Modal - Clean content view for mobile app preview
const MobileDetailModal = ({ content, onClose }) => {
  const modalRef = useRef(null);

  useEffect(() => {
    const handleClickOutside = (e) => {
      if (modalRef.current && !modalRef.current.contains(e.target)) {
        onClose();
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, [onClose]);

  if (!content) return null;
  
  return (
    <div className="fixed inset-0 bg-black/70 flex items-center justify-center z-50 p-4">
      <div ref={modalRef} className="bg-slate-800 rounded-2xl w-full max-w-md overflow-hidden shadow-2xl">
        {/* Header Image */}
        {content.image_url && (
          <div className="relative h-48 bg-slate-700">
            <img src={content.image_url} alt={content.title} className="w-full h-full object-cover" />
            {content.is_featured && (
              <span className="absolute top-3 left-3 bg-amber-500 text-white px-3 py-1 rounded-full text-xs font-semibold">
                Featured
              </span>
            )}
          </div>
        )}
        
        {/* Content */}
        <div className="p-5">
          {!content.image_url && content.is_featured && (
            <span className="inline-block bg-amber-500 text-white px-3 py-1 rounded-full text-xs font-semibold mb-3">
              Featured
            </span>
          )}
          
          <h2 className="text-xl font-bold text-white mb-3">{content.title}</h2>
          <p className="text-slate-300 text-sm leading-relaxed mb-5">
            {content.long_description || content.short_description}
          </p>
          
          {/* Action Button */}
          {content.link_url ? (
            <a
              href={content.link_url}
              target="_blank"
              rel="noopener noreferrer"
              className="block w-full py-3.5 bg-purple-600 text-white text-center rounded-xl font-semibold hover:bg-purple-700 transition-colors"
            >
              {content.link_label || 'View Details'}
            </a>
          ) : (
            <button
              onClick={onClose}
              className="block w-full py-3.5 bg-slate-700 text-white text-center rounded-xl font-semibold hover:bg-slate-600 transition-colors"
            >
              Close
            </button>
          )}
        </div>
      </div>
    </div>
  );
};

// Helper function to calculate distance between two coordinates (Haversine formula)
const calculateDistanceHelper = (lat1, lon1, lat2, lon2) => {
  const R = 6371; // Earth's radius in km
  const dLat = (lat2 - lat1) * Math.PI / 180;
  const dLon = (lon2 - lon1) * Math.PI / 180;
  const a = Math.sin(dLat/2) * Math.sin(dLat/2) +
            Math.cos(lat1 * Math.PI / 180) * Math.cos(lat2 * Math.PI / 180) *
            Math.sin(dLon/2) * Math.sin(dLon/2);
  const c = 2 * Math.atan2(Math.sqrt(a), Math.sqrt(1-a));
  return R * c;
};

// Format distance for display
const formatDistance = (distanceKm) => {
  if (distanceKm === null || distanceKm === undefined) return null;
  if (distanceKm < 1) {
    return `${Math.round(distanceKm * 1000)} m`;
  } else if (distanceKm < 10) {
    return `${distanceKm.toFixed(1)} km`;
  } else {
    return `${Math.round(distanceKm)} km`;
  }
};

// Phone Mockup Component - Now Interactive
const PhoneMockup = ({ content, activeTab, onTabChange, simulationData }) => {
  const [featuredIndex, setFeaturedIndex] = React.useState(0);
  const [selectedItem, setSelectedItem] = React.useState(null);
  
  // Check if simulation is active (simulationData is passed when active)
  const isSimulationActive = simulationData !== null;
  
  // Filter content based on active tab
  // When simulation is active, content already has _passesTargeting, _siteLocaleMatch, _aroundMeMatch flags set
  const getFilteredContent = () => {
    if (activeTab === 'site_locale') {
      // Site Locale: local content with site_locale targeting enabled AND within radius of a site
      return content.filter(c => 
        c.type === 'local' && 
        c.targeting?.site_locale?.enabled === true &&
        c.status === 'active' &&
        c._siteLocaleMatch === true
      );
    } else if (activeTab === 'around_me') {
      // Around Me: local content with around_me targeting enabled AND within radius of current location
      return content.filter(c => 
        c.type === 'local' && 
        c.targeting?.around_me?.enabled === true &&
        c.status === 'active' &&
        c._aroundMeMatch === true
      );
    } else if (activeTab === 'deal') {
      // Deals: when simulation active, only show items that pass targeting
      // When simulation is active, content is already filtered by getSimulatedContent
      // So we just need to filter by type and status
      return content.filter(c => c.type === 'deal' && c.status === 'active');
    } else if (activeTab === 'event') {
      // Events: when simulation active, only show items that pass targeting
      return content.filter(c => c.type === 'event' && c.status === 'active');
    }
    return content.filter(c => c.status === 'active');
  };

  // Featured content: any type with is_featured = true
  // When simulation is active, content is already filtered by getSimulatedContent (targeting applied)
  const featuredContent = content.filter(c => c.is_featured && c.status === 'active');
  const activeContent = getFilteredContent();

  // Auto-scroll featured carousel
  React.useEffect(() => {
    if (featuredContent.length <= 1) return;
    const interval = setInterval(() => {
      setFeaturedIndex(prev => (prev + 1) % featuredContent.length);
    }, 5000); // 5 second interval
    return () => clearInterval(interval);
  }, [featuredContent.length]);

  // Reset featured index when content changes to prevent stale index
  React.useEffect(() => {
    setFeaturedIndex(0);
  }, [featuredContent.length]);

  // Group local content by site for Site Locale tab
  const groupedBySite = activeTab === 'site_locale' && simulationData
    ? (() => {
        const groups = {};
        const checkSite1 = simulationData.userType === 'admin' || simulationData.assignedSites?.includes('site1');
        const checkSite2 = simulationData.userType === 'admin' || simulationData.assignedSites?.includes('site2');
        
        activeContent.forEach(item => {
          const contentCoords = item.geocoded_coordinates || item.targeting?.site_locale?.coordinates;
          if (!contentCoords) return;
          
          const radiusKm = item.targeting?.site_locale?.radius_km || 2;
          
          // Check each site
          if (checkSite1 && simulationData.site1?.coordinates) {
            const dist = calculateDistanceHelper(
              simulationData.site1.coordinates.lat, simulationData.site1.coordinates.lng,
              contentCoords.lat, contentCoords.lng
            );
            if (dist <= radiusKm) {
              const siteName = simulationData.site1.name || 'Site 1';
              if (!groups[siteName]) groups[siteName] = [];
              groups[siteName].push({ ...item, _distanceFromSite: dist });
            }
          }
          
          if (checkSite2 && simulationData.site2?.coordinates) {
            const dist = calculateDistanceHelper(
              simulationData.site2.coordinates.lat, simulationData.site2.coordinates.lng,
              contentCoords.lat, contentCoords.lng
            );
            if (dist <= radiusKm) {
              const siteName = simulationData.site2.name || 'Site 2';
              if (!groups[siteName]) groups[siteName] = [];
              // Only add if not already added to this site
              if (!groups[siteName].find(i => i.id === item.id)) {
                groups[siteName].push({ ...item, _distanceFromSite: dist });
              }
            }
          }
        });
        
        // Sort items within each group by distance
        Object.keys(groups).forEach(site => {
          groups[site].sort((a, b) => a._distanceFromSite - b._distanceFromSite);
        });
        
        return groups;
      })()
    : null;

  // Group local content by subtype for Around Me tab
  const groupedBySubtype = activeTab === 'around_me' 
    ? {
        supplier: activeContent.filter(c => c.local_subtype === 'supplier'),
        event: activeContent.filter(c => c.local_subtype === 'event'),
        service: activeContent.filter(c => c.local_subtype === 'service'),
        news: activeContent.filter(c => c.local_subtype === 'news'),
      }
    : null;

  // Calculate distance for display
  const getDistanceText = (item) => {
    if (!simulationData) return null;
    
    const contentCoords = item.geocoded_coordinates || item.targeting?.site_locale?.coordinates || item.targeting?.around_me?.coordinates;
    if (!contentCoords) return null;

    if (activeTab === 'site_locale') {
      // Use pre-calculated distance if available
      if (item._distanceFromSite !== undefined) {
        return `${formatDistance(item._distanceFromSite)} away`;
      }
      // Fallback: find nearest site
      let nearestDist = Infinity;
      
      const checkSite1 = simulationData.userType === 'admin' || simulationData.assignedSites?.includes('site1');
      const checkSite2 = simulationData.userType === 'admin' || simulationData.assignedSites?.includes('site2');
      
      if (checkSite1 && simulationData.site1?.coordinates) {
        const dist = calculateDistanceHelper(
          simulationData.site1.coordinates.lat, simulationData.site1.coordinates.lng,
          contentCoords.lat, contentCoords.lng
        );
        if (dist < nearestDist) nearestDist = dist;
      }
      if (checkSite2 && simulationData.site2?.coordinates) {
        const dist = calculateDistanceHelper(
          simulationData.site2.coordinates.lat, simulationData.site2.coordinates.lng,
          contentCoords.lat, contentCoords.lng
        );
        if (dist < nearestDist) nearestDist = dist;
      }
      
      if (nearestDist !== Infinity) {
        return `${formatDistance(nearestDist)} away`;
      }
    } else if (activeTab === 'around_me') {
      if (simulationData.currentLocation?.coordinates) {
        const dist = calculateDistanceHelper(
          simulationData.currentLocation.coordinates.lat, simulationData.currentLocation.coordinates.lng,
          contentCoords.lat, contentCoords.lng
        );
        return `${formatDistance(dist)} away`;
      }
    }
    return null;
  };

  return (
    <div className="relative mx-auto" style={{ width: '432px', height: '888px' }}>
      <div className="absolute inset-0 bg-slate-900 rounded-[3rem] shadow-2xl border-4 border-slate-800">
        <div className="absolute top-0 left-1/2 transform -translate-x-1/2 w-32 h-7 bg-slate-900 rounded-b-2xl z-10">
          <div className="absolute top-2 left-1/2 transform -translate-x-1/2 w-16 h-1.5 bg-slate-700 rounded-full"></div>
        </div>
        <div className="absolute top-2 left-2 right-2 bottom-2 bg-slate-800 rounded-[2.5rem] overflow-hidden">
          <div className="h-14 bg-slate-900/50 flex items-center justify-between px-7 pt-2">
            <span className="text-white text-sm font-medium">9:41</span>
          </div>
          <div className="px-5 py-4 bg-gradient-to-r from-purple-600 to-indigo-600">
            <h2 className="text-white font-bold text-xl">Explore</h2>
            <p className="text-purple-200 text-sm">Discover deals, events &amp; more</p>
          </div>
          <div className="p-3 space-y-3 overflow-y-auto phone-mockup-scroll" style={{ height: 'calc(100% - 140px)' }}>
            {/* Featured Carousel - Auto-scrolling with pagination dots */}
            {featuredContent.length > 0 ? (
              <div className="relative">
                {(() => {
                  // Ensure index is within bounds
                  const safeIndex = featuredIndex < featuredContent.length ? featuredIndex : 0;
                  const feat = featuredContent[safeIndex];
                  if (!feat) return null; // Safety check
                  const hasCustomBg = feat.featured_background_url || feat.background_image_url || feat.background_color;
                  const featuredStyle = hasCustomBg ? {
                    backgroundColor: (!feat.featured_background_url && !feat.background_image_url) ? feat.background_color : undefined,
                    backgroundImage: feat.featured_background_url 
                      ? `url(${feat.featured_background_url})` 
                      : feat.background_image_url 
                        ? `url(${feat.background_image_url})` 
                        : undefined,
                    backgroundSize: 'cover',
                    backgroundPosition: 'center'
                  } : {};
                  
                  return (
                    <div 
                      onClick={() => setSelectedItem(feat)}
                      className={`rounded-xl p-4 text-white cursor-pointer transition-all overflow-hidden ${
                        hasCustomBg ? 'hover:opacity-90' : 'bg-gradient-to-br from-purple-600 to-indigo-600 hover:from-purple-500 hover:to-indigo-500'
                      }`}
                      style={{ ...featuredStyle, height: '150px' }}
                    >
                      <div className={hasCustomBg ? 'bg-black/30 rounded-lg p-2 -m-1' : ''}>
                        <span className="bg-white/20 px-2.5 py-1 rounded-full text-xs font-medium">★ FEATURED</span>
                        <h3 className="font-bold text-base truncate mt-2">{feat.title}</h3>
                        <p className="text-xs text-white/80 mt-1 line-clamp-2">{feat.short_description}</p>
                      </div>
                    </div>
                  );
                })()}
                {/* Pagination Dots */}
                {featuredContent.length > 1 && (
                  <div className="flex justify-center gap-1.5 mt-2.5">
                    {featuredContent.map((_, idx) => (
                      <button
                        key={idx}
                        onClick={() => setFeaturedIndex(idx)}
                        className={`transition-all ${
                          idx === featuredIndex 
                            ? 'w-5 h-2 bg-purple-500 rounded-full' 
                            : 'w-2 h-2 bg-slate-600 rounded-full hover:bg-slate-500'
                        }`}
                      />
                    ))}
                  </div>
                )}
              </div>
            ) : (
              <div className="bg-gradient-to-br from-slate-600 to-slate-700 rounded-xl p-3 text-white">
                <span className="bg-white/20 px-2 py-0.5 rounded-full text-[10px] font-medium">FEATURED</span>
                <h3 className="font-bold text-sm mt-2">No Featured Content</h3>
                <p className="text-[10px] text-white/80 mt-1">Add featured content to highlight here</p>
              </div>
            )}
            
            {/* Category Tabs - Mobile app order: Site Locale, Around Me, Deals, Events */}
            <div className="flex gap-3 overflow-x-auto pb-1">
              {[
                { key: 'site_locale', label: 'Site Locale' },
                { key: 'around_me', label: 'Around Me' },
                { key: 'deal', label: 'Deals' },
                { key: 'event', label: 'Events' }
              ].map(({ key, label }) => (
                <button
                  key={key}
                  onClick={() => onTabChange(key)}
                  className={`px-5 py-2 rounded-full text-xs font-medium whitespace-nowrap transition-colors ${
                    activeTab === key ? 'bg-purple-600 text-white' : 'bg-slate-700 text-slate-300 hover:bg-slate-600'
                  }`}
                >
                  {label}
                </button>
              ))}
            </div>
            
            {/* Content Cards - Different layout for Site Locale/Around Me vs Deals/Events */}
            <div className="space-y-3">
              {activeTab === 'site_locale' ? (
                // Site Locale: Group by subtype first, then by site within each subtype
                <>
                  {groupedBySite && Object.entries(groupedBySite).length > 0 ? (
                    (() => {
                      const subtypeLabels = {
                        supplier: '📦 Suppliers Near Your Sites',
                        event: '📅 Events Near Your Sites',
                        service: '🔧 Services Near Your Sites',
                        news: '📰 News Near Your Sites',
                      };
                      // Restructure: subtype → site → items
                      const bySubtypeThenSite = {};
                      Object.entries(groupedBySite).forEach(([siteName, items]) => {
                        items.forEach(item => {
                          const sub = item.local_subtype || 'supplier';
                          if (!bySubtypeThenSite[sub]) bySubtypeThenSite[sub] = {};
                          if (!bySubtypeThenSite[sub][siteName]) bySubtypeThenSite[sub][siteName] = [];
                          bySubtypeThenSite[sub][siteName].push(item);
                        });
                      });
                      return Object.entries(subtypeLabels).map(([subtype, label]) => {
                        const sitesForSubtype = bySubtypeThenSite[subtype];
                        if (!sitesForSubtype || Object.keys(sitesForSubtype).length === 0) return null;
                        return (
                          <div key={subtype} className="mb-4">
                            <h5 className="text-sm font-medium text-slate-400 mb-2">{label}</h5>
                            {Object.entries(sitesForSubtype).map(([siteName, items]) => (
                              <div key={siteName} className="mb-3">
                                <div className="flex items-center gap-2 mb-1.5">
                                  <span className="text-slate-500">🏢</span>
                                  <h6 className="text-xs font-medium text-slate-300">{siteName}</h6>
                                </div>
                                <div className="flex gap-2.5 overflow-x-auto pb-1">
                                  {items.map((item) => {
                                    const distanceText = getDistanceText(item);
                                    return (
                                      <div 
                                        key={item.id}
                                        onClick={() => setSelectedItem(item)}
                                        className="flex-shrink-0 w-36 bg-slate-700/50 rounded-lg overflow-hidden cursor-pointer hover:bg-slate-700 transition-colors"
                                      >
                                        {item.image_url ? (
                                          <img src={item.image_url} alt="" className="w-full h-20 object-cover" />
                                        ) : (
                                          <div className={`w-full h-20 flex items-center justify-center ${getTypeColor('local').bg}`}>
                                            <svg className={`w-8 h-8 ${getTypeColor('local').text}`} fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d={getTypeIcon('local')} />
                                            </svg>
                                          </div>
                                        )}
                                        <div className="p-2">
                                          <h4 className="text-white font-medium text-xs truncate">{item.title}</h4>
                                          <p className="text-slate-400 text-[11px] truncate">{item.short_description}</p>
                                          {distanceText && (
                                            <p className="text-purple-400 text-[10px] mt-0.5">📍 {distanceText}</p>
                                          )}
                                        </div>
                                      </div>
                                    );
                                  })}
                                </div>
                              </div>
                            ))}
                          </div>
                        );
                      });
                    })()
                  ) : (
                    <div className="text-center py-5">
                      <p className="text-slate-500 text-sm">No content near your sites</p>
                    </div>
                  )}
                </>
              ) : activeTab === 'around_me' ? (
                // Around Me: Group by subtype with horizontal scroll
                <>
                  {Object.entries(groupedBySubtype || {}).map(([subtype, items]) => {
                    if (items.length === 0) return null;
                    const subtypeLabels = {
                      supplier: '📦 Suppliers Around You',
                      event: '📅 Events Around You',
                      service: '🔧 Services Around You',
                      news: '📰 News Around You',
                    };
                    return (
                      <div key={subtype} className="mb-4">
                        <h5 className="text-sm font-medium text-slate-400 mb-2">{subtypeLabels[subtype]}</h5>
                        <div className="flex gap-2.5 overflow-x-auto pb-1">
                          {items.map((item) => {
                            const distanceText = getDistanceText(item);
                            return (
                              <div 
                                key={item.id}
                                onClick={() => setSelectedItem(item)}
                                className="flex-shrink-0 w-36 bg-slate-700/50 rounded-lg overflow-hidden cursor-pointer hover:bg-slate-700 transition-colors"
                              >
                                {item.image_url ? (
                                  <img src={item.image_url} alt="" className="w-full h-20 object-cover" />
                                ) : (
                                  <div className={`w-full h-20 flex items-center justify-center ${getTypeColor('local').bg}`}>
                                    <svg className={`w-8 h-8 ${getTypeColor('local').text}`} fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d={getTypeIcon('local')} />
                                    </svg>
                                  </div>
                                )}
                                <div className="p-2">
                                  <h4 className="text-white font-medium text-xs truncate">{item.title}</h4>
                                  <p className="text-slate-400 text-[11px] truncate">{item.short_description}</p>
                                  {distanceText && (
                                    <p className="text-purple-400 text-[10px] mt-0.5 truncate">📍 {distanceText}</p>
                                  )}
                                </div>
                              </div>
                            );
                          })}
                        </div>
                      </div>
                    );
                  })}
                  {activeContent.length === 0 && (
                    <div className="text-center py-5">
                      <p className="text-slate-500 text-sm">No content around you</p>
                    </div>
                  )}
                </>
              ) : (
                // Vertical list for Deals/Events
                <>
                  {activeContent.map((item) => {
                    const hasCustomBg = item.background_image_url || item.background_color;
                    const cardStyle = hasCustomBg ? {
                      backgroundColor: item.background_image_url ? undefined : item.background_color,
                      backgroundImage: item.background_image_url ? `url(${item.background_image_url})` : undefined,
                      backgroundSize: 'cover',
                      backgroundPosition: 'center'
                    } : {};
                    
                    return (
                      <div 
                        key={item.id} 
                        onClick={() => setSelectedItem(item)}
                        className={`rounded-lg p-3 flex gap-2.5 cursor-pointer transition-colors ${
                          hasCustomBg ? 'hover:opacity-90' : 'bg-slate-700/50 hover:bg-slate-700'
                        }`}
                        style={cardStyle}
                      >
                        {item.image_url ? (
                          <img 
                            src={item.image_url} 
                            alt="" 
                            className="w-18 h-18 rounded-lg object-cover flex-shrink-0"
                            style={{ width: '72px', height: '72px' }}
                          />
                        ) : (
                          <div className={`rounded-lg flex items-center justify-center flex-shrink-0 ${
                            hasCustomBg ? 'bg-white/20' : getTypeColor(item.type).bg
                          }`} style={{ width: '72px', height: '72px' }}>
                            <svg className={`w-8 h-8 ${hasCustomBg ? 'text-white' : getTypeColor(item.type).text}`} fill="none" stroke="currentColor" viewBox="0 0 24 24">
                              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d={getTypeIcon(item.type)} />
                            </svg>
                          </div>
                        )}
                        <div className={`flex-1 min-w-0 ${hasCustomBg ? 'bg-black/30 rounded px-2 py-1' : ''}`}>
                          <h4 className="text-white font-medium text-sm truncate">{item.title}</h4>
                          <p className={`text-xs line-clamp-2 ${hasCustomBg ? 'text-white/80' : 'text-slate-400'}`}>{item.short_description}</p>
                        </div>
                      </div>
                    );
                  })}
                  {activeContent.length === 0 && (
                    <div className="text-center py-5">
                      <p className="text-slate-500 text-sm">No {activeTab === 'deal' ? 'deals' : 'events'} available</p>
                    </div>
                  )}
                </>
              )}
            </div>
          </div>
          <div className="absolute bottom-2 left-1/2 transform -translate-x-1/2 w-32 h-1 bg-white/30 rounded-full"></div>
          
          {/* In-Phone Detail Modal */}
          {selectedItem && (
            <div className="absolute inset-0 bg-black/80 z-20 flex items-center justify-center p-4 rounded-[2.5rem]">
              <div className="bg-slate-800 rounded-xl w-full max-h-full overflow-y-auto mx-2">
                {selectedItem.image_url && (
                  <div className="relative h-32 bg-slate-700 rounded-t-xl overflow-hidden">
                    <img src={selectedItem.image_url} alt={selectedItem.title} className="w-full h-full object-cover" />
                    {selectedItem.is_featured && (
                      <span className="absolute top-2 left-2 bg-amber-500 text-white px-2 py-0.5 rounded-full text-[10px] font-semibold">
                        Featured
                      </span>
                    )}
                  </div>
                )}
                <div className="p-4">
                  {!selectedItem.image_url && selectedItem.is_featured && (
                    <span className="inline-block bg-amber-500 text-white px-2 py-0.5 rounded-full text-[10px] font-semibold mb-2">
                      Featured
                    </span>
                  )}
                  <h2 className="text-base font-bold text-white mb-2">{selectedItem.title}</h2>
                  <p className="text-slate-300 text-xs leading-relaxed mb-4">
                    {selectedItem.long_description || selectedItem.short_description}
                  </p>
                  {selectedItem.link_url ? (
                    <a
                      href={selectedItem.link_url}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="block w-full py-2.5 bg-purple-600 text-white text-center rounded-lg text-sm font-semibold hover:bg-purple-700 transition-colors"
                    >
                      {selectedItem.link_label || 'View Details'}
                    </a>
                  ) : null}
                  <button
                    onClick={() => setSelectedItem(null)}
                    className="block w-full py-2.5 mt-2 bg-slate-700 text-white text-center rounded-lg text-sm font-semibold hover:bg-slate-600 transition-colors"
                  >
                    Close
                  </button>
                </div>
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

// Image Upload Component
const ImageUploadField = ({ label, value, onChange, helpText }) => {
  const [uploading, setUploading] = useState(false);
  const [error, setError] = useState('');
  const fileInputRef = useRef(null);

  const handleFileSelect = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;

    // Validate file type
    const validTypes = ['image/jpeg', 'image/png', 'image/gif', 'image/webp'];
    if (!validTypes.includes(file.type)) {
      setError('Invalid file type. Use JPG, PNG, GIF, or WebP');
      return;
    }

    // Validate size (5MB)
    if (file.size > 5 * 1024 * 1024) {
      setError('File too large. Max 5MB');
      return;
    }

    setError('');
    setUploading(true);

    try {
      const token = localStorage.getItem('token');
      const formData = new FormData();
      formData.append('file', file);

      const response = await axios.post(`${API}/explore/admin/upload-image`, formData, {
        headers: {
          Authorization: `Bearer ${token}`,
          'Content-Type': 'multipart/form-data'
        }
      });

      onChange(response.data.url);
    } catch (err) {
      console.error('Upload failed:', err);
      setError(err.response?.data?.detail || 'Upload failed');
    } finally {
      setUploading(false);
    }
  };

  return (
    <div>
      <label className="block text-sm font-medium text-slate-300 mb-2">{label}</label>
      <div className="space-y-2">
        <div className="flex gap-2">
          <input
            type="url"
            value={value || ''}
            onChange={e => onChange(e.target.value)}
            className="flex-1 px-4 py-2 bg-slate-700 border border-slate-600 rounded-lg text-white placeholder-slate-400 focus:outline-none focus:border-purple-500"
            placeholder="https://example.com/image.jpg"
          />
          <input
            ref={fileInputRef}
            type="file"
            accept="image/*"
            onChange={handleFileSelect}
            className="hidden"
          />
          <button
            type="button"
            onClick={() => fileInputRef.current?.click()}
            disabled={uploading}
            className="px-4 py-2 bg-purple-600 text-white rounded-lg hover:bg-purple-700 disabled:opacity-50 disabled:cursor-not-allowed flex items-center gap-2"
          >
            {uploading ? (
              <>
                <svg className="w-4 h-4 animate-spin" fill="none" viewBox="0 0 24 24">
                  <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" />
                  <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4z" />
                </svg>
                <span>Uploading...</span>
              </>
            ) : (
              <>
                <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 16l4.586-4.586a2 2 0 012.828 0L16 16m-2-2l1.586-1.586a2 2 0 012.828 0L20 14m-6-6h.01M6 20h12a2 2 0 002-2V6a2 2 0 00-2-2H6a2 2 0 00-2 2v12a2 2 0 002 2z" />
                </svg>
                <span>Upload</span>
              </>
            )}
          </button>
        </div>
        {helpText && <p className="text-xs text-slate-500">{helpText}</p>}
        {error && <p className="text-xs text-red-400">{error}</p>}
        {value && (
          <div className="flex items-center gap-2 p-2 bg-slate-700/50 rounded-lg">
            <img src={value} alt="Preview" className="w-16 h-16 object-cover rounded" />
            <button
              type="button"
              onClick={() => onChange('')}
              className="p-1 text-red-400 hover:text-red-300"
              title="Remove image"
            >
              <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16" />
              </svg>
            </button>
          </div>
        )}
      </div>
    </div>
  );
};

// Live Preview Card Component for Modal
const LivePreviewCard = ({ formData, viewMode }) => {
  const hasCustomBg = formData.background_image_url || formData.background_color;
  
  const cardStyle = hasCustomBg ? {
    backgroundColor: formData.background_image_url ? undefined : formData.background_color,
    backgroundImage: formData.background_image_url ? `url(${formData.background_image_url})` : undefined,
    backgroundSize: 'cover',
    backgroundPosition: 'center'
  } : {};

  if (viewMode === 'detail') {
    // Detail View
    return (
      <div className="bg-slate-700 rounded-xl overflow-hidden">
        {/* Header Image */}
        {formData.image_url ? (
          <div className="relative h-28 bg-slate-600">
            <img src={formData.image_url} alt="" className="w-full h-full object-cover" />
            {formData.is_featured && (
              <span className="absolute top-2 left-2 bg-amber-500 text-white px-2 py-0.5 rounded-full text-[8px] font-semibold">
                Featured
              </span>
            )}
          </div>
        ) : (
          <div 
            className="relative h-28 flex items-center justify-center"
            style={hasCustomBg ? cardStyle : { background: 'linear-gradient(135deg, #6366f1, #8b5cf6)' }}
          >
            {formData.is_featured && (
              <span className="absolute top-2 left-2 bg-amber-500 text-white px-2 py-0.5 rounded-full text-[8px] font-semibold">
                Featured
              </span>
            )}
            <svg className="w-8 h-8 text-white/50" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d={getTypeIcon(formData.type)} />
            </svg>
          </div>
        )}
        
        {/* Content */}
        <div className="p-3">
          <h3 className="text-white font-bold text-sm mb-1 truncate">
            {formData.title || 'Content Title'}
          </h3>
          <p className="text-slate-300 text-[10px] leading-relaxed line-clamp-4 mb-3">
            {formData.long_description || formData.short_description || 'Your content description will appear here...'}
          </p>
          
          {/* Action Button */}
          <div className="bg-purple-600 text-white text-center py-2 rounded-lg text-[10px] font-semibold">
            {formData.link_label || 'View Details'}
          </div>
        </div>
      </div>
    );
  }

  // Card View (default)
  return (
    <div 
      className={`rounded-lg p-2.5 flex gap-2 ${
        hasCustomBg ? '' : 'bg-slate-700/50'
      }`}
      style={cardStyle}
    >
      {formData.image_url ? (
        <img 
          src={formData.image_url} 
          alt="" 
          className="w-12 h-12 rounded-lg object-cover flex-shrink-0"
        />
      ) : (
        <div className={`w-12 h-12 rounded-lg flex items-center justify-center flex-shrink-0 ${
          hasCustomBg ? 'bg-white/20' : getTypeColor(formData.type).bg
        }`}>
          <svg className={`w-5 h-5 ${hasCustomBg ? 'text-white' : getTypeColor(formData.type).text}`} fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d={getTypeIcon(formData.type)} />
          </svg>
        </div>
      )}
      <div className={`flex-1 min-w-0 ${hasCustomBg ? 'bg-black/30 rounded px-1.5 py-0.5' : ''}`}>
        <div className="flex items-center gap-1">
          <h4 className="text-white font-medium text-[10px] truncate">
            {formData.title || 'Content Title'}
          </h4>
          {formData.is_featured && (
            <span className="bg-amber-500 text-white px-1 py-0.5 rounded text-[6px] font-bold flex-shrink-0">
              FEATURED
            </span>
          )}
        </div>
        <p className={`text-[8px] line-clamp-2 ${hasCustomBg ? 'text-white/80' : 'text-slate-400'}`}>
          {formData.short_description || 'Short description preview...'}
        </p>
      </div>
    </div>
  );
};

// Content Form Modal - Used for both Create and Edit (with Live Preview)
const ContentFormModal = ({ 
  formData, 
  setFormData, 
  categories, 
  regions,
  countries = ['United Kingdom'],
  onClose, 
  onSave, 
  saving,
  isEdit = false,
  googleMapsConfigured = false
}) => {
  const modalRef = useRef(null);
  const [previewMode, setPreviewMode] = useState('card'); // 'card' or 'detail'
  const [geocoding, setGeocoding] = useState(false);
  const [geocodeError, setGeocodeError] = useState('');
  const [addressModified, setAddressModified] = useState(false); // Track if address was changed

  useEffect(() => {
    const handleClickOutside = (e) => {
      if (modalRef.current && !modalRef.current.contains(e.target)) {
        onClose();
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, [onClose]);

  // Check if geo-targeting is enabled
  const isGeoTargetingEnabled = formData.targeting.site_locale.enabled || formData.targeting.around_me.enabled;
  
  // Check if coordinates are present
  const hasCoordinates = formData.geocoded_coordinates?.lat && formData.geocoded_coordinates?.lng;
  
  // Check if address is complete
  const isAddressComplete = formData.address?.address_line_1 && 
                            formData.address?.town_city && 
                            formData.address?.postcode && 
                            formData.address?.country;
  
  // Validation: can save?
  const canSave = () => {
    if (!formData.title || !formData.short_description || !formData.long_description) return false;
    if (isGeoTargetingEnabled) {
      if (!hasCoordinates) return false;
      if (!isAddressComplete) return false;
      if (addressModified && hasCoordinates) return false; // Force re-geocode if address changed
    }
    return true;
  };
  
  // Get validation message
  const getValidationMessage = () => {
    if (isGeoTargetingEnabled) {
      if (!isAddressComplete) return "Please complete all required address fields";
      if (!hasCoordinates) return "Please geocode the address to get coordinates";
      if (addressModified) return "Address changed - please re-geocode to update coordinates";
    }
    return null;
  };

  // Handle address field change
  const handleAddressChange = (field, value) => {
    setFormData(prev => ({
      ...prev,
      address: { ...prev.address, [field]: value }
    }));
    // Mark address as modified if we already had coordinates
    if (hasCoordinates) {
      setAddressModified(true);
    }
    setGeocodeError('');
  };

  // Geocode address
  const handleGeocode = async () => {
    if (!isAddressComplete) {
      setGeocodeError('Please fill in all required address fields');
      return;
    }
    
    if (!googleMapsConfigured) {
      setGeocodeError('Google Maps API key not configured. Add it in Quick Actions.');
      return;
    }

    setGeocoding(true);
    setGeocodeError('');

    try {
      const token = localStorage.getItem('token');
      const response = await axios.post(
        `${API}/explore/admin/geocode`,
        formData.address,
        { headers: { Authorization: `Bearer ${token}` } }
      );

      if (response.data.success) {
        setFormData(prev => ({
          ...prev,
          geocoded_coordinates: response.data.coordinates
        }));
        setAddressModified(false); // Reset modification flag after successful geocode
      }
    } catch (err) {
      console.error('Geocoding failed:', err);
      setGeocodeError(err.response?.data?.detail || 'Geocoding failed. Please check the address.');
    } finally {
      setGeocoding(false);
    }
  };

  // Handle toggling geo-targeting off - clear geolocation
  const handleGeoTargetingToggle = (targetType, enabled) => {
    const newTargeting = {
      ...formData.targeting,
      [targetType]: { ...formData.targeting[targetType], enabled }
    };
    
    // Check if both are now disabled
    const bothDisabled = !newTargeting.site_locale.enabled && !newTargeting.around_me.enabled;
    
    if (bothDisabled && (formData.geocoded_coordinates || formData.address?.address_line_1)) {
      // Both disabled - clear address and coordinates
      setFormData(prev => ({
        ...prev,
        targeting: newTargeting,
        address: {
          address_line_1: '',
          address_line_2: '',
          town_city: '',
          postcode: '',
          country: 'United Kingdom'
        },
        geocoded_coordinates: null
      }));
    } else {
      setFormData(prev => ({
        ...prev,
        targeting: newTargeting
      }));
    }
  };

  const toggleCategory = (category) => {
    const current = formData.targeting.business_categories;
    const updated = current.includes(category)
      ? current.filter(c => c !== category)
      : [...current, category];
    setFormData(prev => ({
      ...prev,
      targeting: { ...prev.targeting, business_categories: updated }
    }));
  };

  const toggleRegion = (region) => {
    const current = formData.targeting.regions;
    const updated = current.includes(region)
      ? current.filter(r => r !== region)
      : [...current, region];
    setFormData(prev => ({
      ...prev,
      targeting: { ...prev.targeting, regions: updated }
    }));
  };

  return (
    <div className="fixed inset-0 bg-black/70 flex items-center justify-center z-50 p-4">
      <div ref={modalRef} className="bg-slate-800 rounded-xl w-full max-w-6xl max-h-[95vh] flex flex-col">
        {/* Header */}
        <div className="flex-shrink-0 bg-slate-800 border-b border-slate-700 px-6 py-4 flex items-center justify-between">
          <h2 className="text-xl font-bold text-white">{isEdit ? 'Edit Content' : 'Create New Content'}</h2>
          <button onClick={onClose} className="text-slate-400 hover:text-white">
            <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
            </svg>
          </button>
        </div>
        
        {/* Content Area - Split Layout */}
        <div className="flex-1 flex overflow-hidden">
          {/* Left Side - Phone Mockup Preview (25%) */}
          <div className="w-1/4 flex-shrink-0 bg-slate-900/50 border-r border-slate-700 p-4 flex flex-col">
            <div className="text-center mb-3">
              <h3 className="text-sm font-medium text-slate-300">Live Preview</h3>
              <p className="text-xs text-slate-500">See how it looks on mobile</p>
            </div>
            
            {/* Mini Phone Frame - iPhone 14 Style (matches main page) */}
            <div className="flex-1 flex items-center justify-center">
              <div className="relative mx-auto" style={{ width: '200px', height: '410px' }}>
                <div className="absolute inset-0 bg-slate-900 rounded-[3rem] shadow-2xl border-4 border-slate-800">
                  {/* Dynamic Island Notch */}
                  <div className="absolute top-0 left-1/2 transform -translate-x-1/2 w-24 h-5 bg-slate-900 rounded-b-2xl z-10">
                    <div className="absolute top-1.5 left-1/2 transform -translate-x-1/2 w-12 h-1 bg-slate-700 rounded-full"></div>
                  </div>
                  
                  {/* Phone Screen */}
                  <div className="absolute top-1.5 left-1.5 right-1.5 bottom-1.5 bg-slate-800 rounded-[2.5rem] overflow-hidden">
                    {/* Status Bar */}
                    <div className="h-8 bg-slate-900/50 flex items-center justify-between px-4 pt-1">
                      <span className="text-white text-[9px] font-medium">9:41</span>
                      <div className="flex items-center gap-1 text-white text-[9px]">
                        <span>5G</span>
                        <span>100%</span>
                      </div>
                    </div>
                    
                    {/* App Header - Purple gradient like main page */}
                    <div className="px-3 py-2 bg-gradient-to-r from-purple-600 to-indigo-600">
                      <h4 className="text-white font-bold text-sm">Explore</h4>
                      <p className="text-purple-200 text-[9px]">
                        {previewMode === 'card' ? 'Card Preview' : 'Detail Preview'}
                      </p>
                    </div>
                    
                    {/* Preview Content */}
                    <div className="p-3 space-y-2 overflow-y-auto" style={{ height: 'calc(100% - 80px)' }}>
                      <LivePreviewCard formData={formData} viewMode={previewMode} />
                    </div>
                  </div>
                </div>
              </div>
            </div>
            
            {/* View Toggle */}
            <div className="mt-3 flex justify-center">
              <div className="bg-slate-700 rounded-lg p-1 flex">
                <button
                  onClick={() => setPreviewMode('card')}
                  className={`px-3 py-1.5 rounded text-xs font-medium transition-colors ${
                    previewMode === 'card'
                      ? 'bg-purple-600 text-white'
                      : 'text-slate-400 hover:text-white'
                  }`}
                >
                  Card View
                </button>
                <button
                  onClick={() => setPreviewMode('detail')}
                  className={`px-3 py-1.5 rounded text-xs font-medium transition-colors ${
                    previewMode === 'detail'
                      ? 'bg-purple-600 text-white'
                      : 'text-slate-400 hover:text-white'
                  }`}
                >
                  Detail View
                </button>
              </div>
            </div>
          </div>
          
          {/* Right Side - Form (75%) */}
          <div className="flex-1 overflow-y-auto p-6 space-y-5">
            {/* Content Type */}
            <div>
              <label className="block text-sm font-medium text-slate-300 mb-2">Content Type</label>
              <div className="grid grid-cols-4 gap-2">
                {['advertisement', 'event', 'deal', 'local'].map(type => (
                  <button
                    key={type}
                    onClick={() => setFormData(prev => ({ 
                      ...prev, 
                      type,
                      // Auto-enable featured for advertisements
                      is_featured: type === 'advertisement' ? true : prev.is_featured
                    }))}
                    className={`px-4 py-2.5 rounded-lg border text-sm font-medium transition-colors ${
                      formData.type === type
                        ? 'bg-purple-600 border-purple-500 text-white'
                        : 'bg-slate-700 border-slate-600 text-slate-300 hover:border-slate-500'
                    }`}
                  >
                    {type === 'advertisement' ? 'Ad' : type.charAt(0).toUpperCase() + type.slice(1)}
                  </button>
                ))}
              </div>
            </div>

            {/* Local Subtype */}
            {formData.type === 'local' && (
              <div>
                <label className="block text-sm font-medium text-slate-300 mb-2">Local Category</label>
                <div className="grid grid-cols-4 gap-2">
                  {['supplier', 'event', 'service', 'news'].map(subtype => (
                    <button
                      key={subtype}
                      onClick={() => setFormData(prev => ({ ...prev, local_subtype: subtype }))}
                      className={`px-4 py-2 rounded-lg border text-sm transition-colors ${
                        formData.local_subtype === subtype
                          ? 'bg-purple-600 border-purple-500 text-white'
                          : 'bg-slate-700 border-slate-600 text-slate-300 hover:border-slate-500'
                      }`}
                    >
                      {subtype.charAt(0).toUpperCase() + subtype.slice(1)}
                    </button>
                  ))}
                </div>
              </div>
            )}

            {/* Basic Info - 2 Column Grid */}
            <div className="grid grid-cols-2 gap-4">
              <div className="col-span-2">
                <label className="block text-sm font-medium text-slate-300 mb-2">Title *</label>
                <input
                  type="text"
                  value={formData.title}
                  onChange={e => setFormData(prev => ({ ...prev, title: e.target.value }))}
                  className="w-full px-4 py-2 bg-slate-700 border border-slate-600 rounded-lg text-white placeholder-slate-400 focus:outline-none focus:border-purple-500"
                  placeholder="Enter content title"
                />
              </div>
              <div className="col-span-2">
                <label className="block text-sm font-medium text-slate-300 mb-2">Short Description * <span className="text-slate-500 font-normal">(shown on cards)</span></label>
                <input
                  type="text"
                  value={formData.short_description}
                  onChange={e => setFormData(prev => ({ ...prev, short_description: e.target.value }))}
                  className="w-full px-4 py-2 bg-slate-700 border border-slate-600 rounded-lg text-white placeholder-slate-400 focus:outline-none focus:border-purple-500"
                  placeholder="Brief description for card preview"
                  maxLength={150}
                />
                <p className="text-xs text-slate-500 mt-1">{formData.short_description?.length || 0}/150 characters</p>
              </div>
              <div className="col-span-2">
                <label className="block text-sm font-medium text-slate-300 mb-2">Long Description * <span className="text-slate-500 font-normal">(shown in detail view)</span></label>
                <textarea
                  value={formData.long_description}
                  onChange={e => setFormData(prev => ({ ...prev, long_description: e.target.value }))}
                  rows={3}
                  className="w-full px-4 py-2 bg-slate-700 border border-slate-600 rounded-lg text-white placeholder-slate-400 focus:outline-none focus:border-purple-500"
                  placeholder="Full description with all details"
                />
              </div>
            </div>

            {/* Images & Background - Collapsible Section */}
            <div className="border border-slate-700 rounded-lg">
              <div className="px-4 py-3 bg-slate-700/30 border-b border-slate-700">
                <h3 className="text-sm font-semibold text-white">Images & Background</h3>
              </div>
              <div className="p-4 space-y-4">
                <ImageUploadField
                  label="Card Image"
                  value={formData.image_url}
                  onChange={(url) => setFormData(prev => ({ ...prev, image_url: url }))}
                  helpText="Main image displayed on the content card"
                />
                
                <div className="grid grid-cols-2 gap-4">
                  {/* Background Color */}
                  <div>
                    <label className="block text-sm font-medium text-slate-300 mb-2">Background Color</label>
                    <div className="flex gap-2 items-center">
                      <input
                        type="text"
                        value={formData.background_color || ''}
                        onChange={e => setFormData(prev => ({ ...prev, background_color: e.target.value }))}
                        className="flex-1 px-3 py-2 bg-slate-700 border border-slate-600 rounded-lg text-white placeholder-slate-400 focus:outline-none focus:border-purple-500 text-sm"
                        placeholder="#FF5733"
                        maxLength={7}
                      />
                      <input
                        type="color"
                        value={formData.background_color && formData.background_color.startsWith('#') ? formData.background_color : '#808080'}
                        onChange={e => setFormData(prev => ({ ...prev, background_color: e.target.value }))}
                        className="w-9 h-9 rounded-lg border border-slate-600 cursor-pointer"
                        title="Pick a color"
                      />
                      {formData.background_color && (
                        <button
                          type="button"
                          onClick={() => setFormData(prev => ({ ...prev, background_color: '' }))}
                          className="p-1.5 text-slate-400 hover:text-red-400"
                          title="Clear color"
                        >
                          <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                          </svg>
                        </button>
                      )}
                    </div>
                  </div>

                  {/* Background Image */}
                  <ImageUploadField
                    label="Background Image"
                    value={formData.background_image_url}
                    onChange={(url) => setFormData(prev => ({ ...prev, background_image_url: url }))}
                    helpText="Overrides color if set"
                  />
                </div>
              </div>
            </div>

            {/* Action Button & Scheduling */}
            <div className="grid grid-cols-2 gap-4">
              <div>
                <label className="block text-sm font-medium text-slate-300 mb-2">Action Button Text</label>
                <input
                  type="text"
                  value={formData.link_label}
                  onChange={e => setFormData(prev => ({ ...prev, link_label: e.target.value }))}
                  className="w-full px-4 py-2 bg-slate-700 border border-slate-600 rounded-lg text-white placeholder-slate-400 focus:outline-none focus:border-purple-500"
                  placeholder="e.g., View Details, Shop Now"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-slate-300 mb-2">Action Button URL</label>
                <input
                  type="url"
                  value={formData.link_url}
                  onChange={e => setFormData(prev => ({ ...prev, link_url: e.target.value }))}
                  className="w-full px-4 py-2 bg-slate-700 border border-slate-600 rounded-lg text-white placeholder-slate-400 focus:outline-none focus:border-purple-500"
                  placeholder="https://example.com"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-slate-300 mb-2">Start Date *</label>
                <input
                  type="datetime-local"
                  value={formData.start_date}
                  onChange={e => setFormData(prev => ({ ...prev, start_date: e.target.value }))}
                  className="w-full px-4 py-2 bg-slate-700 border border-slate-600 rounded-lg text-white focus:outline-none focus:border-purple-500"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-slate-300 mb-2">End Date (Optional)</label>
                <input
                  type="datetime-local"
                  value={formData.end_date}
                  onChange={e => setFormData(prev => ({ ...prev, end_date: e.target.value }))}
                  className="w-full px-4 py-2 bg-slate-700 border border-slate-600 rounded-lg text-white focus:outline-none focus:border-purple-500"
                />
              </div>
            </div>

            {/* Targeting Section - Collapsible */}
            <div className="border border-slate-700 rounded-lg">
              <div className="px-4 py-3 bg-slate-700/30 border-b border-slate-700">
                <h3 className="text-sm font-semibold text-white">Targeting</h3>
                <p className="text-xs text-slate-500">Leave empty for ALL. Multiple selections use AND logic.</p>
              </div>
              <div className="p-4 space-y-4">
                {/* Business Categories */}
                <div>
                  <label className="block text-sm font-medium text-slate-300 mb-2">
                    Business Categories 
                    <span className="text-slate-500 ml-2">
                      ({formData.targeting.business_categories.length === 0 ? 'ALL' : `${formData.targeting.business_categories.length} selected`})
                    </span>
                  </label>
                  <div className="flex flex-wrap gap-1.5 max-h-24 overflow-y-auto p-2 bg-slate-700/50 rounded-lg">
                    {categories.map(cat => (
                      <button
                        key={cat}
                        onClick={() => toggleCategory(cat)}
                        className={`px-2 py-1 rounded-full text-xs font-medium transition-colors ${
                          formData.targeting.business_categories.includes(cat)
                            ? 'bg-purple-600 text-white'
                            : 'bg-slate-600 text-slate-300 hover:bg-slate-500'
                        }`}
                      >
                        {cat}
                      </button>
                    ))}
                  </div>
                </div>

                {/* Regions */}
                <div>
                  <label className="block text-sm font-medium text-slate-300 mb-2">
                    Regions 
                    <span className="text-slate-500 ml-2">
                      ({formData.targeting.regions.length === 0 ? 'ALL' : `${formData.targeting.regions.length} selected`})
                    </span>
                  </label>
                  <div className="flex flex-wrap gap-1.5 p-2 bg-slate-700/50 rounded-lg">
                    {regions.map(region => (
                      <button
                        key={region}
                        onClick={() => toggleRegion(region)}
                        className={`px-2 py-1 rounded-full text-xs font-medium transition-colors ${
                          formData.targeting.regions.includes(region)
                            ? 'bg-purple-600 text-white'
                            : 'bg-slate-600 text-slate-300 hover:bg-slate-500'
                        }`}
                      >
                        {region}
                      </button>
                    ))}
                  </div>
                </div>

                {/* Site Locale & Around Me - Side by Side */}
                <div className="grid grid-cols-2 gap-4">
                  {/* Site Locale */}
                  <div className="p-3 bg-slate-700/50 rounded-lg">
                    <div className="flex items-center justify-between mb-2">
                      <div>
                        <label className="text-xs font-medium text-slate-300">Site Locale</label>
                        <p className="text-[10px] text-slate-500">Users with sites nearby</p>
                      </div>
                      <button
                        onClick={() => handleGeoTargetingToggle('site_locale', !formData.targeting.site_locale.enabled)}
                        className={`w-10 h-5 rounded-full transition-colors ${formData.targeting.site_locale.enabled ? 'bg-purple-600' : 'bg-slate-600'}`}
                      >
                        <div className={`w-4 h-4 bg-white rounded-full transition-transform ${formData.targeting.site_locale.enabled ? 'translate-x-5' : 'translate-x-0.5'}`} />
                      </button>
                    </div>
                    {formData.targeting.site_locale.enabled && (
                      <div className="mt-2">
                        <label className="block text-[10px] text-slate-400 mb-1">Radius (km)</label>
                        <input
                          type="number"
                          value={formData.targeting.site_locale.radius_km}
                          onChange={e => setFormData(prev => ({
                            ...prev,
                            targeting: {
                              ...prev.targeting,
                              site_locale: { ...prev.targeting.site_locale, radius_km: parseFloat(e.target.value) || 2 }
                            }
                          }))}
                          className="w-full px-2 py-1.5 bg-slate-600 border border-slate-500 rounded text-white text-xs"
                          placeholder="5"
                        />
                      </div>
                    )}
                  </div>

                  {/* Around Me */}
                  <div className="p-3 bg-slate-700/50 rounded-lg">
                    <div className="flex items-center justify-between mb-2">
                      <div>
                        <label className="text-xs font-medium text-slate-300">Around Me</label>
                        <p className="text-[10px] text-slate-500">Users currently nearby</p>
                      </div>
                      <button
                        onClick={() => handleGeoTargetingToggle('around_me', !formData.targeting.around_me.enabled)}
                        className={`w-10 h-5 rounded-full transition-colors ${formData.targeting.around_me.enabled ? 'bg-purple-600' : 'bg-slate-600'}`}
                      >
                        <div className={`w-4 h-4 bg-white rounded-full transition-transform ${formData.targeting.around_me.enabled ? 'translate-x-5' : 'translate-x-0.5'}`} />
                      </button>
                    </div>
                    {formData.targeting.around_me.enabled && (
                      <div className="mt-2">
                        <label className="block text-[10px] text-slate-400 mb-1">Radius (km)</label>
                        <input
                          type="number"
                          value={formData.targeting.around_me.radius_km}
                          onChange={e => setFormData(prev => ({
                            ...prev,
                            targeting: {
                              ...prev.targeting,
                              around_me: { ...prev.targeting.around_me, radius_km: parseFloat(e.target.value) || 2 }
                            }
                          }))}
                          className="w-full px-2 py-1.5 bg-slate-600 border border-slate-500 rounded text-white text-xs"
                          placeholder="10"
                        />
                      </div>
                    )}
                  </div>
                </div>

                {/* Address Fields - Shown when geo-targeting is enabled */}
                {isGeoTargetingEnabled && (
                  <div className="p-4 bg-blue-900/20 border border-blue-700/50 rounded-lg space-y-3">
                    <div className="flex items-center gap-2 mb-2">
                      <svg className="w-5 h-5 text-blue-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17.657 16.657L13.414 20.9a1.998 1.998 0 01-2.827 0l-4.244-4.243a8 8 0 1111.314 0z" />
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 11a3 3 0 11-6 0 3 3 0 016 0z" />
                      </svg>
                      <h4 className="text-sm font-medium text-blue-300">Location Address</h4>
                      <span className="text-xs text-blue-400/70">(Required for geo-targeting)</span>
                    </div>
                    
                    <div className="grid grid-cols-2 gap-3">
                      <div className="col-span-2">
                        <label className="block text-xs text-slate-400 mb-1">Address Line 1 *</label>
                        <input
                          type="text"
                          value={formData.address?.address_line_1 || ''}
                          onChange={e => handleAddressChange('address_line_1', e.target.value)}
                          className="w-full px-3 py-2 bg-slate-700 border border-slate-600 rounded text-white text-sm placeholder-slate-400 focus:outline-none focus:border-blue-500"
                          placeholder="Street address"
                        />
                      </div>
                      <div className="col-span-2">
                        <label className="block text-xs text-slate-400 mb-1">Address Line 2</label>
                        <input
                          type="text"
                          value={formData.address?.address_line_2 || ''}
                          onChange={e => handleAddressChange('address_line_2', e.target.value)}
                          className="w-full px-3 py-2 bg-slate-700 border border-slate-600 rounded text-white text-sm placeholder-slate-400 focus:outline-none focus:border-blue-500"
                          placeholder="Building, floor, etc. (optional)"
                        />
                      </div>
                      <div>
                        <label className="block text-xs text-slate-400 mb-1">Town / City *</label>
                        <input
                          type="text"
                          value={formData.address?.town_city || ''}
                          onChange={e => handleAddressChange('town_city', e.target.value)}
                          className="w-full px-3 py-2 bg-slate-700 border border-slate-600 rounded text-white text-sm placeholder-slate-400 focus:outline-none focus:border-blue-500"
                          placeholder="City or town"
                        />
                      </div>
                      <div>
                        <label className="block text-xs text-slate-400 mb-1">Postcode *</label>
                        <input
                          type="text"
                          value={formData.address?.postcode || ''}
                          onChange={e => handleAddressChange('postcode', e.target.value.toUpperCase())}
                          className="w-full px-3 py-2 bg-slate-700 border border-slate-600 rounded text-white text-sm placeholder-slate-400 focus:outline-none focus:border-blue-500"
                          placeholder="e.g., SW1A 1AA"
                        />
                      </div>
                      <div className="col-span-2">
                        <label className="block text-xs text-slate-400 mb-1">Country *</label>
                        <select
                          value={formData.address?.country || 'United Kingdom'}
                          onChange={e => handleAddressChange('country', e.target.value)}
                          className="w-full px-3 py-2 bg-slate-700 border border-slate-600 rounded text-white text-sm focus:outline-none focus:border-blue-500"
                        >
                          {countries.map(country => (
                            <option key={country} value={country}>{country}</option>
                          ))}
                        </select>
                      </div>
                    </div>

                    {/* Coordinates and Geocode Button */}
                    <div className="flex items-end gap-3 pt-2 border-t border-slate-700">
                      <div className="flex-1">
                        <label className="block text-xs text-slate-400 mb-1">Coordinates</label>
                        <input
                          type="text"
                          value={hasCoordinates ? `${formData.geocoded_coordinates.lat.toFixed(6)}, ${formData.geocoded_coordinates.lng.toFixed(6)}` : ''}
                          readOnly
                          className="w-full px-3 py-2 bg-slate-800 border border-slate-600 rounded text-slate-300 text-sm cursor-not-allowed"
                          placeholder="Click 'Geocode' to calculate"
                        />
                      </div>
                      <button
                        type="button"
                        onClick={handleGeocode}
                        disabled={geocoding || !isAddressComplete}
                        className={`px-4 py-2 rounded text-sm font-medium transition-colors flex items-center gap-2 ${
                          geocoding || !isAddressComplete
                            ? 'bg-slate-600 text-slate-400 cursor-not-allowed'
                            : addressModified
                              ? 'bg-amber-600 hover:bg-amber-700 text-white'
                              : 'bg-blue-600 hover:bg-blue-700 text-white'
                        }`}
                      >
                        {geocoding ? (
                          <>
                            <svg className="w-4 h-4 animate-spin" fill="none" viewBox="0 0 24 24">
                              <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                              <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                            </svg>
                            Geocoding...
                          </>
                        ) : (
                          <>
                            <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z" />
                            </svg>
                            {addressModified ? 'Re-Geocode' : 'Geocode'}
                          </>
                        )}
                      </button>
                    </div>

                    {/* Status Messages */}
                    {geocodeError && (
                      <div className="flex items-center gap-2 text-red-400 text-xs">
                        <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8v4m0 4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
                        </svg>
                        {geocodeError}
                      </div>
                    )}
                    {hasCoordinates && !addressModified && (
                      <div className="flex items-center gap-2 text-green-400 text-xs">
                        <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                        </svg>
                        Address geocoded successfully
                      </div>
                    )}
                    {addressModified && hasCoordinates && (
                      <div className="flex items-center gap-2 text-amber-400 text-xs">
                        <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z" />
                        </svg>
                        Address changed - please re-geocode before saving
                      </div>
                    )}
                  </div>
                )}
              </div>
            </div>

            {/* Display Options */}
            <div className="flex items-center gap-6 p-4 bg-slate-700/30 rounded-lg">
              <label className={`flex items-center gap-2 ${formData.type === 'advertisement' ? 'cursor-not-allowed' : 'cursor-pointer'}`}>
                <input
                  type="checkbox"
                  checked={formData.type === 'advertisement' ? true : formData.is_featured}
                  onChange={e => setFormData(prev => ({ ...prev, is_featured: e.target.checked }))}
                  disabled={formData.type === 'advertisement'}
                  className={`w-4 h-4 rounded bg-slate-700 border-slate-600 text-purple-600 focus:ring-purple-500 ${formData.type === 'advertisement' ? 'opacity-60' : ''}`}
                />
                <span className="text-slate-300 text-sm">Featured Content</span>
                {formData.type === 'advertisement' && (
                  <span className="text-xs text-amber-400">(Always on for Ads)</span>
                )}
              </label>
              <div className="flex items-center gap-2">
                <label className="text-slate-300 text-sm">Priority:</label>
                <input
                  type="number"
                  value={formData.priority}
                  onChange={e => setFormData(prev => ({ ...prev, priority: parseInt(e.target.value) || 0 }))}
                  className="w-16 px-2 py-1.5 bg-slate-700 border border-slate-600 rounded text-white text-sm"
                  min="0"
                  max="100"
                />
              </div>
            </div>

            {/* Featured Background Image - Only shown when Featured is checked */}
            {(formData.is_featured || formData.type === 'advertisement') && (
              <div className="p-4 bg-amber-500/10 border border-amber-500/30 rounded-lg">
                <ImageUploadField
                  label="Featured Carousel Background"
                  value={formData.featured_background_url}
                  onChange={(url) => setFormData(prev => ({ ...prev, featured_background_url: url }))}
                  helpText="Background image for the featured carousel card"
                />
              </div>
            )}
          </div>
        </div>

        {/* Footer */}
        <div className="flex-shrink-0 bg-slate-800 border-t border-slate-700 px-6 py-4">
          {/* Validation Message */}
          {getValidationMessage() && (
            <div className="mb-3 flex items-center gap-2 text-amber-400 text-sm">
              <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z" />
              </svg>
              {getValidationMessage()}
            </div>
          )}
          <div className="flex justify-end gap-3">
            <button
              onClick={onClose}
              className="px-6 py-2 bg-slate-700 text-slate-300 rounded-lg hover:bg-slate-600 transition-colors"
            >
              Cancel
            </button>
            <button
              onClick={onSave}
              disabled={saving || !canSave()}
              className="px-6 py-2 bg-purple-600 text-white rounded-lg hover:bg-purple-700 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
            >
              {saving ? (isEdit ? 'Updating...' : 'Creating...') : (isEdit ? 'Update Content' : 'Create Content')}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

// Main Component
const ExploreHubPage = () => {
  const { currentUser } = useOutletContext();
  const isLevel1User = currentUser?.access_level === 1 || currentUser?.access_level === undefined;
  
  const [activeTab, setActiveTab] = useState('local');
  const [mockupTab, setMockupTab] = useState('site_locale'); // Separate state for mockup - matches mobile app default
  const [stats, setStats] = useState(null);
  const [content, setContent] = useState([]);
  const [allContent, setAllContent] = useState([]); // For mockup - all types
  const [loading, setLoading] = useState(true);
  const [categories, setCategories] = useState([]);
  const [regions, setRegions] = useState([]);
  const [countries, setCountries] = useState(['United Kingdom']);
  const [typeColors, setTypeColors] = useState({
    advertisement: '#3b82f6',
    event: '#22c55e',
    deal: '#f59e0b',
    local: '#a855f7'
  });
  
  // Groups state
  const [groups, setGroups] = useState([]);
  const [collapsedGroups, setCollapsedGroups] = useState({});
  const [newGroupName, setNewGroupName] = useState('');
  const [creatingGroup, setCreatingGroup] = useState(false);
  const [editingGroupId, setEditingGroupId] = useState(null);
  const [editingGroupName, setEditingGroupName] = useState('');
  
  // Lazy loading state for group items
  const [groupItems, setGroupItems] = useState({}); // { groupId: { items: [], loaded: false, loading: false, total: 0 } }
  const [loadingGroupItems, setLoadingGroupItems] = useState({});  
  // Google Maps API Key state
  const [googleMapsKey, setGoogleMapsKey] = useState('');
  const [googleMapsKeyMasked, setGoogleMapsKeyMasked] = useState('');
  const [googleMapsConfigured, setGoogleMapsConfigured] = useState(false);
  const [savingApiKey, setSavingApiKey] = useState(false);
  
  // Simulation state for preview
  const [simulationData, setSimulationData] = useState({
    userType: 'admin', // 'admin' or 'team_member'
    orgCategory: 'General Construction',
    orgAddress: '13 Myers Lane, London, SE14 5RZ', // Organisation address for region targeting
    site1: {
      name: 'Zaria Court',
      address: '70 Ravensbourne Road, London, SE6 4UT',
      coordinates: null
    },
    site2: {
      name: 'Adeline Road',
      address: '40 Princes Street, Ipswich, IP1 1RJ',
      coordinates: null
    },
    assignedSites: ['site1', 'site2'], // For team members - which sites they're assigned to
    currentLocation: {
      address: '21 Reynard Way, Brentford, TW8 9GF',
      coordinates: null
    }
  });
  const [simulationActive, setSimulationActive] = useState(false);
  const [geocodingField, setGeocodingField] = useState(null); // Track which field is being geocoded
  const [businessCategories, setBusinessCategories] = useState([]);
  
  // Modal states
  const [showCreateModal, setShowCreateModal] = useState(false);
  const [showEditModal, setShowEditModal] = useState(false);
  const [showMobileDetailModal, setShowMobileDetailModal] = useState(false);
  const [selectedContent, setSelectedContent] = useState(null);
  const [saving, setSaving] = useState(false);
  
  // Backup/Restore/Import/Export states
  const [showBackupModal, setShowBackupModal] = useState(false);
  const [showClearModal, setShowClearModal] = useState(false);
  const [showRestoreModal, setShowRestoreModal] = useState(false);
  const [showImportModal, setShowImportModal] = useState(false);
  const [showLogsModal, setShowLogsModal] = useState(false);
  const [dataLogs, setDataLogs] = useState([]);
  const [logsLoading, setLogsLoading] = useState(false);
  const [backups, setBackups] = useState([]);
  const [systemStatus, setSystemStatus] = useState({ is_empty: true, content_count: 0, groups_count: 0 });
  const [backupLoading, setBackupLoading] = useState(false);
  const [restoreLoading, setRestoreLoading] = useState(false);
  const [importLoading, setImportLoading] = useState(false);
  const [exportLoading, setExportLoading] = useState(false);
  const [importAnalysis, setImportAnalysis] = useState(null);
  const [restoreAnalysis, setRestoreAnalysis] = useState(null);
  const [selectedBackupId, setSelectedBackupId] = useState(null);
  const [importFile, setImportFile] = useState(null);
  const [mergeMode, setMergeMode] = useState('add_new_only');
  const [conflictResolutions, setConflictResolutions] = useState({});
  
  // CSV Import/Export states
  const [showCsvImportModal, setShowCsvImportModal] = useState(false);
  const [showCsvExportModal, setShowCsvExportModal] = useState(false);
  const [csvImportFile, setCsvImportFile] = useState(null);
  const [csvImportAnalysis, setCsvImportAnalysis] = useState(null);
  const [csvImportLoading, setCsvImportLoading] = useState(false);
  const [csvExportLoading, setCsvExportLoading] = useState(false);
  const [selectedExportGroups, setSelectedExportGroups] = useState([]);
  
  // Content filter state
  const [contentFilters, setContentFilters] = useState({
    titleSearch: '',
    descriptionSearch: '',
    featuredFilter: 'all' // 'all', 'featured', 'not_featured'
  });
  
  // Default form state
  const getDefaultFormData = () => ({
    type: 'advertisement',
    title: '',
    short_description: '',
    long_description: '',
    image_url: '',
    link_url: '',
    link_label: '',
    local_subtype: 'supplier',
    start_date: new Date().toISOString().slice(0, 16),
    end_date: '',
    targeting: {
      business_categories: [],
      regions: [],
      site_locale: { enabled: false, radius_km: 2, coordinates: null },
      around_me: { enabled: false, radius_km: 2, coordinates: null }
    },
    is_featured: false,
    priority: 0,
    background_color: '',
    background_image_url: '',
    featured_background_url: '',
    // Address fields for geo-targeting
    address: {
      address_line_1: '',
      address_line_2: '',
      town_city: '',
      postcode: '',
      country: 'United Kingdom'
    },
    geocoded_coordinates: null
  });

  // Drag and drop sensors
  const sensors = useSensors(
    useSensor(PointerSensor, {
      activationConstraint: {
        distance: 8,
      },
    }),
    useSensor(KeyboardSensor, {
      coordinateGetter: sortableKeyboardCoordinates,
    })
  );

  // Handle drag end - reorder items and handle cross-group moves
  const handleDragEnd = async (event) => {
    const { active, over } = event;

    if (!over) return;

    // Find the active item from groupItems instead of content
    let activeItem = null;
    let sourceGroupId = null;
    
    for (const [gId, gData] of Object.entries(groupItems)) {
      const found = gData.items?.find(item => item.id === active.id);
      if (found) {
        activeItem = found;
        sourceGroupId = gId;
        break;
      }
    }
    
    if (!activeItem) return;

    // Check if dropped on a group zone
    const isGroupDrop = over.id.toString().startsWith('group-');
    
    if (isGroupDrop) {
      // Extract group ID from the droppable ID
      const targetGroupId = over.id.replace('group-', '');
      const newGroupId = targetGroupId === 'ungrouped' ? null : targetGroupId;
      const currentGroupId = sourceGroupId === 'ungrouped' ? null : sourceGroupId;
      
      // Only move if dropping to a different group
      if (newGroupId !== currentGroupId) {
        // Save to backend and refresh
        try {
          await moveContentToGroup(active.id, newGroupId);
          console.log('✅ Moved to group zone:', targetGroupId);
        } catch (error) {
          console.error('Failed to move to group:', error);
        }
      }
      return;
    }

    // Regular item drop - find the over item
    if (active.id === over.id) return;

    let overItem = null;
    let targetGroupId = null;
    
    for (const [gId, gData] of Object.entries(groupItems)) {
      const found = gData.items?.find(item => item.id === over.id);
      if (found) {
        overItem = found;
        targetGroupId = gId;
        break;
      }
    }
    
    if (!overItem) return;
    
    // Check if moving to a different group
    if (sourceGroupId !== targetGroupId) {
      // Cross-group move
      const newGroupId = targetGroupId === 'ungrouped' ? null : targetGroupId;
      
      // Save to backend
      try {
        await moveContentToGroup(active.id, newGroupId);
        console.log('✅ Moved to different group');
      } catch (error) {
        console.error('Failed to move to group:', error);
      }
    } else {
      // Same group reorder
      const groupData = groupItems[sourceGroupId];
      if (!groupData?.items) return;
      
      const items = [...groupData.items];
      const oldIndex = items.findIndex((item) => item.id === active.id);
      const newIndex = items.findIndex((item) => item.id === over.id);

      const newItems = arrayMove(items, oldIndex, newIndex);
      
      // Update local state immediately
      setGroupItems(prev => ({
        ...prev,
        [sourceGroupId]: {
          ...prev[sourceGroupId],
          items: newItems
        }
      }));

      // Update priorities based on new order (higher index = lower priority)
      const reorderItems = newItems.map((item, index) => ({
        id: item.id,
        priority: newItems.length - index // Reverse so first item has highest priority
      }));

      try {
        const token = localStorage.getItem('token');
        await axios.post(`${API}/explore/admin/reorder`, 
          { items: reorderItems },
          { headers: { Authorization: `Bearer ${token}` } }
        );
        console.log('✅ Reorder saved');
      } catch (error) {
        console.error('Failed to save reorder:', error);
        // Refresh on error
        fetchGroupItems(sourceGroupId, true);
      }
    }
  };
  
  const [formData, setFormData] = useState(getDefaultFormData());

  const fetchData = useCallback(async () => {
    try {
      const token = localStorage.getItem('token');
      const headers = { Authorization: `Bearer ${token}` };
      const statsRes = await axios.get(`${API}/explore/admin/stats`, { headers });
      setStats(statsRes.data);
    } catch (err) {
      console.error('Failed to fetch stats:', err);
    } finally {
      setLoading(false);
    }
  }, []);

  const fetchContent = useCallback(async () => {
    try {
      const token = localStorage.getItem('token');
      const headers = { Authorization: `Bearer ${token}` };
      const contentRes = await axios.get(`${API}/explore/admin/content?type=${activeTab}&limit=100000`, { headers });
      setContent(contentRes.data);
    } catch (err) {
      console.error('Failed to fetch content:', err);
    }
  }, [activeTab]);

  const fetchAllContent = useCallback(async () => {
    try {
      const token = localStorage.getItem('token');
      const headers = { Authorization: `Bearer ${token}` };
      // Fetch ALL content types for the mockup preview
      const contentRes = await axios.get(`${API}/explore/admin/content?limit=100000`, { headers });
      setAllContent(contentRes.data);
    } catch (err) {
      console.error('Failed to fetch all content:', err);
    }
  }, []);

  useEffect(() => {
    fetchData();
    const fetchCategories = async () => {
      try {
        const res = await axios.get(`${API}/explore/categories`);
        setCategories(res.data.categories || []);
      } catch (err) {
        console.error('Failed to fetch categories:', err);
      }
    };
    const fetchRegions = async () => {
      try {
        const res = await axios.get(`${API}/explore/regions`);
        setRegions(res.data.regions || []);
      } catch (err) {
        console.error('Failed to fetch regions:', err);
      }
    };
    const fetchTypeColors = async () => {
      try {
        const res = await axios.get(`${API}/explore/type-colors`);
        setTypeColors(res.data.type_colors || {});
      } catch (err) {
        console.error('Failed to fetch type colors:', err);
      }
    };
    const fetchCountries = async () => {
      try {
        const res = await axios.get(`${API}/explore/countries`);
        setCountries(res.data.countries || ['United Kingdom']);
      } catch (err) {
        console.error('Failed to fetch countries:', err);
      }
    };
    const fetchGoogleMapsKey = async () => {
      try {
        const token = localStorage.getItem('token');
        const res = await axios.get(`${API}/explore/admin/google-maps-key`, {
          headers: { Authorization: `Bearer ${token}` }
        });
        setGoogleMapsKeyMasked(res.data.api_key_masked || '');
        setGoogleMapsConfigured(res.data.is_configured || false);
      } catch (err) {
        console.error('Failed to fetch Google Maps key status:', err);
      }
    };
    fetchCategories();
    fetchRegions();
    fetchTypeColors();
    fetchCountries();
    fetchGoogleMapsKey();
    fetchBusinessCategories();
  }, [fetchData]);

  // Fetch business categories for simulation dropdown
  const fetchBusinessCategories = async () => {
    try {
      const token = localStorage.getItem('token');
      const res = await axios.get(`${API}/super-admin/business-categories`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setBusinessCategories(res.data || []);
    } catch (err) {
      console.error('Failed to fetch business categories:', err);
    }
  };

  // Geocode an address for simulation
  const geocodeSimulationAddress = async (field) => {
    let address = '';
    if (field === 'site1') address = simulationData.site1.address;
    else if (field === 'site2') address = simulationData.site2.address;
    else if (field === 'currentLocation') address = simulationData.currentLocation.address;
    
    if (!address) {
      alert('Please enter an address first');
      return;
    }
    
    setGeocodingField(field);
    try {
      const token = localStorage.getItem('token');
      const res = await axios.post(`${API}/explore/admin/geocode-simple`, 
        { address },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      
      if (res.data.lat && res.data.lng) {
        setSimulationData(prev => {
          if (field === 'site1') {
            return { ...prev, site1: { ...prev.site1, coordinates: { lat: res.data.lat, lng: res.data.lng } } };
          } else if (field === 'site2') {
            return { ...prev, site2: { ...prev.site2, coordinates: { lat: res.data.lat, lng: res.data.lng } } };
          } else if (field === 'currentLocation') {
            return { ...prev, currentLocation: { ...prev.currentLocation, coordinates: { lat: res.data.lat, lng: res.data.lng } } };
          }
          return prev;
        });
      } else {
        alert('Could not geocode address. Please check the address and try again.');
      }
    } catch (err) {
      console.error('Geocoding failed:', err);
      alert(err.response?.data?.detail || 'Geocoding failed. Please check the address.');
    } finally {
      setGeocodingField(null);
    }
  };

  // Apply simulation filter to content - mirrors backend check_content_targeting logic exactly
  const getSimulatedContent = useCallback(() => {
    if (!simulationActive) return allContent;
    
    // Extract region from organisation address (not site addresses)
    const userRegions = [];
    if (simulationData.orgAddress) {
      const orgRegion = extractRegionFromAddress(simulationData.orgAddress);
      if (orgRegion) userRegions.push(orgRegion);
    }
    
    return allContent.filter(item => {
      // Reset match flags for this filter pass
      item._siteLocaleMatch = false;
      item._aroundMeMatch = false;
      item._passesTargeting = false;
      
      const targeting = item.targeting || {};
      
      // 1. Check Business Category (empty list = ALL)
      const targetCategories = targeting.business_categories || [];
      if (targetCategories.length > 0 && !targetCategories.includes(simulationData.orgCategory)) {
        return false;
      }
      
      // 2. Check Region (empty list = ALL)
      const targetRegions = targeting.regions || [];
      if (targetRegions.length > 0) {
        const hasRegionMatch = userRegions.some(region => targetRegions.includes(region));
        if (!hasRegionMatch) {
          return false;
        }
      }
      
      // 3. Check Site Locale and Around Me (OR logic - either can match)
      const siteLocale = targeting.site_locale || {};
      const aroundMe = targeting.around_me || {};
      
      const siteLocaleEnabled = siteLocale.enabled && siteLocale.coordinates;
      const aroundMeEnabled = aroundMe.enabled && aroundMe.coordinates;
      
      // If neither geo-targeting is enabled, pass this check
      if (!siteLocaleEnabled && !aroundMeEnabled) {
        // For local content, require at least one geo-targeting option
        if (item.type === 'local') {
          return false;
        }
        item._passesTargeting = true;
        return true;
      }
      
      // Check site_locale match
      let siteLocaleMatch = false;
      if (siteLocaleEnabled) {
        const radiusKm = siteLocale.radius_km || 2;
        const contentCoords = item.geocoded_coordinates || siteLocale.coordinates;
        
        if (contentCoords) {
          const checkSite1 = simulationData.userType === 'admin' || simulationData.assignedSites.includes('site1');
          const checkSite2 = simulationData.userType === 'admin' || simulationData.assignedSites.includes('site2');
          
          if (checkSite1 && simulationData.site1.coordinates) {
            const dist = calculateDistance(
              simulationData.site1.coordinates.lat, simulationData.site1.coordinates.lng,
              contentCoords.lat, contentCoords.lng
            );
            if (dist <= radiusKm) siteLocaleMatch = true;
          }
          if (checkSite2 && simulationData.site2.coordinates) {
            const dist = calculateDistance(
              simulationData.site2.coordinates.lat, simulationData.site2.coordinates.lng,
              contentCoords.lat, contentCoords.lng
            );
            if (dist <= radiusKm) siteLocaleMatch = true;
          }
        }
      }
      
      // Check around_me match
      let aroundMeMatch = false;
      if (aroundMeEnabled && simulationData.currentLocation.coordinates) {
        const radiusKm = aroundMe.radius_km || 2;
        const contentCoords = item.geocoded_coordinates || aroundMe.coordinates;
        
        if (contentCoords) {
          const dist = calculateDistance(
            simulationData.currentLocation.coordinates.lat, simulationData.currentLocation.coordinates.lng,
            contentCoords.lat, contentCoords.lng
          );
          if (dist <= radiusKm) aroundMeMatch = true;
        }
      }
      
      // OR logic: pass if either matches
      const visible = siteLocaleMatch || aroundMeMatch;
      
      // Set match flags for tab filtering
      item._siteLocaleMatch = siteLocaleMatch;
      item._aroundMeMatch = aroundMeMatch;
      item._passesTargeting = visible;
      
      return visible;
    });
  }, [allContent, simulationActive, simulationData]);

  // Haversine formula to calculate distance between two coordinates
  const calculateDistance = (lat1, lon1, lat2, lon2) => {
    const R = 6371; // Earth's radius in km
    const dLat = (lat2 - lat1) * Math.PI / 180;
    const dLon = (lon2 - lon1) * Math.PI / 180;
    const a = Math.sin(dLat/2) * Math.sin(dLat/2) +
              Math.cos(lat1 * Math.PI / 180) * Math.cos(lat2 * Math.PI / 180) *
              Math.sin(dLon/2) * Math.sin(dLon/2);
    const c = 2 * Math.atan2(Math.sqrt(a), Math.sqrt(1-a));
    return R * c;
  };

  const updateTypeColor = async (type, color) => {
    setTypeColors(prev => ({ ...prev, [type]: color }));
  };

  const saveTypeColor = async (type, color) => {
    if (!color) return;
    try {
      const token = localStorage.getItem('token');
      await axios.put(`${API}/explore/admin/type-colors`, 
        { [type]: color },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      console.log(`✅ Saved ${type} color: ${color}`);
    } catch (err) {
      console.error('Failed to update type color:', err);
    }
  };

  const saveGoogleMapsKey = async () => {
    if (!googleMapsKey || googleMapsKey.length < 10) {
      alert('Please enter a valid API key');
      return;
    }
    setSavingApiKey(true);
    try {
      const token = localStorage.getItem('token');
      const res = await axios.put(`${API}/explore/admin/google-maps-key`, 
        { api_key: googleMapsKey },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      setGoogleMapsKeyMasked(res.data.api_key_masked || '');
      setGoogleMapsConfigured(res.data.is_configured || false);
      setGoogleMapsKey(''); // Clear the input
      alert('Google Maps API key saved successfully!');
    } catch (err) {
      console.error('Failed to save Google Maps key:', err);
      alert(err.response?.data?.detail || 'Failed to save API key');
    } finally {
      setSavingApiKey(false);
    }
  };

  // Group management functions
  const fetchGroups = useCallback(async () => {
    try {
      const token = localStorage.getItem('token');
      const res = await axios.get(`${API}/explore/admin/groups-with-counts?type=${activeTab}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setGroups(res.data || []);
      // Initialize collapsed state for all groups (collapsed by default)
      const initialCollapsed = {};
      (res.data || []).forEach(g => {
        // Set all groups to collapsed by default if not already set
        if (collapsedGroups[g.id] === undefined) {
          initialCollapsed[g.id] = true; // All groups collapsed by default
        }
      });
      setCollapsedGroups(prev => ({ ...initialCollapsed, ...prev }));
    } catch (err) {
      console.error('Failed to fetch groups:', err);
    }
  }, [activeTab]);

  const createGroup = async () => {
    if (!newGroupName.trim()) return;
    setCreatingGroup(true);
    try {
      const token = localStorage.getItem('token');
      await axios.post(`${API}/explore/admin/groups`, 
        { name: newGroupName.trim(), type: activeTab },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      setNewGroupName('');
      fetchGroups();
    } catch (err) {
      console.error('Failed to create group:', err);
      alert(err.response?.data?.detail || 'Failed to create group');
    } finally {
      setCreatingGroup(false);
    }
  };

  const renameGroup = async (groupId) => {
    if (!editingGroupName.trim()) return;
    try {
      const token = localStorage.getItem('token');
      await axios.put(`${API}/explore/admin/groups/${groupId}`, 
        { name: editingGroupName.trim() },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      setEditingGroupId(null);
      setEditingGroupName('');
      fetchGroups();
    } catch (err) {
      console.error('Failed to rename group:', err);
      alert(err.response?.data?.detail || 'Failed to rename group');
    }
  };

  const deleteGroup = async (groupId, groupName) => {
    if (!window.confirm(`Delete group "${groupName}"? Items will be moved to Ungrouped.`)) return;
    try {
      const token = localStorage.getItem('token');
      await axios.delete(`${API}/explore/admin/groups/${groupId}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      // Clear loaded items for this group
      setGroupItems(prev => {
        const newState = { ...prev };
        delete newState[groupId];
        return newState;
      });
      fetchGroups();
    } catch (err) {
      console.error('Failed to delete group:', err);
      alert(err.response?.data?.detail || 'Failed to delete group');
    }
  };

  // Move group up (decrease priority number = higher in list)
  const moveGroupUp = async (groupId) => {
    const sortedGroups = groups.filter(g => !g.is_virtual).sort((a, b) => a.priority - b.priority);
    const currentIndex = sortedGroups.findIndex(g => g.id === groupId);
    
    if (currentIndex <= 0) return; // Already at top
    
    // Swap priorities with the group above
    const currentGroup = sortedGroups[currentIndex];
    const aboveGroup = sortedGroups[currentIndex - 1];
    
    try {
      const token = localStorage.getItem('token');
      await axios.post(`${API}/explore/admin/groups/reorder`, 
        { 
          items: [
            { id: currentGroup.id, priority: aboveGroup.priority },
            { id: aboveGroup.id, priority: currentGroup.priority }
          ]
        },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      fetchGroups();
    } catch (err) {
      console.error('Failed to move group up:', err);
    }
  };

  // Move group down (increase priority number = lower in list)
  const moveGroupDown = async (groupId) => {
    const sortedGroups = groups.filter(g => !g.is_virtual).sort((a, b) => a.priority - b.priority);
    const currentIndex = sortedGroups.findIndex(g => g.id === groupId);
    
    if (currentIndex >= sortedGroups.length - 1) return; // Already at bottom
    
    // Swap priorities with the group below
    const currentGroup = sortedGroups[currentIndex];
    const belowGroup = sortedGroups[currentIndex + 1];
    
    try {
      const token = localStorage.getItem('token');
      await axios.post(`${API}/explore/admin/groups/reorder`, 
        { 
          items: [
            { id: currentGroup.id, priority: belowGroup.priority },
            { id: belowGroup.id, priority: currentGroup.priority }
          ]
        },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      fetchGroups();
    } catch (err) {
      console.error('Failed to move group down:', err);
    }
  };

  // Check if group can move up/down
  const canMoveGroupUp = (groupId) => {
    const sortedGroups = groups.filter(g => !g.is_virtual).sort((a, b) => a.priority - b.priority);
    const currentIndex = sortedGroups.findIndex(g => g.id === groupId);
    return currentIndex > 0;
  };

  const canMoveGroupDown = (groupId) => {
    const sortedGroups = groups.filter(g => !g.is_virtual).sort((a, b) => a.priority - b.priority);
    const currentIndex = sortedGroups.findIndex(g => g.id === groupId);
    return currentIndex < sortedGroups.length - 1;
  };

  // Lazy load items for a specific group
  const fetchGroupItems = useCallback(async (groupId, forceRefresh = false) => {
    // Skip if already loaded and not forcing refresh
    if (!forceRefresh && groupItems[groupId]?.loaded) {
      return;
    }
    
    // Skip if already loading
    if (loadingGroupItems[groupId]) {
      return;
    }
    
    setLoadingGroupItems(prev => ({ ...prev, [groupId]: true }));
    
    try {
      const token = localStorage.getItem('token');
      const res = await axios.get(`${API}/explore/admin/groups/${groupId}/items?type=${activeTab}&limit=500`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      
      setGroupItems(prev => ({
        ...prev,
        [groupId]: {
          items: res.data.items || [],
          loaded: true,
          total: res.data.total || 0,
          hasMore: res.data.has_more || false
        }
      }));
    } catch (err) {
      console.error(`Failed to fetch items for group ${groupId}:`, err);
    } finally {
      setLoadingGroupItems(prev => ({ ...prev, [groupId]: false }));
    }
  }, [activeTab, groupItems, loadingGroupItems]);

  // Refresh items for a group after content changes
  const refreshGroupItems = useCallback(async (groupId) => {
    if (groupId) {
      await fetchGroupItems(groupId, true);
    }
    // Also refresh the ungrouped items
    await fetchGroupItems('ungrouped', true);
    // Refresh group counts
    fetchGroups();
  }, [fetchGroupItems, fetchGroups]);

  // Refresh all loaded groups (used after content operations)
  const refreshAllLoadedGroups = useCallback(async () => {
    const loadedGroupIds = Object.keys(groupItems).filter(gId => groupItems[gId]?.loaded);
    await Promise.all(loadedGroupIds.map(gId => fetchGroupItems(gId, true)));
    fetchGroups();
  }, [groupItems, fetchGroupItems, fetchGroups]);

  const moveContentToGroup = async (contentId, groupId, priority = null) => {
    try {
      const token = localStorage.getItem('token');
      // Find the item's current group
      let currentGroupId = null;
      Object.entries(groupItems).forEach(([gId, gData]) => {
        if (gData.items?.find(item => item.id === contentId)) {
          currentGroupId = gId;
        }
      });
      
      await axios.post(`${API}/explore/admin/content/move-to-group`, 
        { content_id: contentId, group_id: groupId, priority },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      
      // Refresh affected groups
      if (currentGroupId) {
        await fetchGroupItems(currentGroupId, true);
      }
      if (groupId) {
        await fetchGroupItems(groupId, true);
      }
      await fetchGroupItems('ungrouped', true);
      fetchGroups();
    } catch (err) {
      console.error('Failed to move content to group:', err);
    }
  };

  const toggleGroupCollapsed = async (groupId) => {
    const willExpand = collapsedGroups[groupId];
    setCollapsedGroups(prev => ({ ...prev, [groupId]: !prev[groupId] }));
    
    // If expanding, load items if not already loaded
    if (willExpand && !groupItems[groupId]?.loaded) {
      await fetchGroupItems(groupId);
    }
  };

  // Get grouped content - uses lazy-loaded items
  const getGroupedContent = useCallback(() => {
    // Use groups from the API which includes item_count
    return groups.map(group => {
      let items = groupItems[group.id]?.items || [];
      
      // Apply title filter
      if (contentFilters.titleSearch.trim()) {
        const searchLower = contentFilters.titleSearch.toLowerCase().trim();
        items = items.filter(item => 
          (item.title?.toLowerCase() || '').includes(searchLower)
        );
      }
      
      // Apply description filter
      if (contentFilters.descriptionSearch.trim()) {
        const searchLower = contentFilters.descriptionSearch.toLowerCase().trim();
        items = items.filter(item => 
          (item.short_description?.toLowerCase() || '').includes(searchLower)
        );
      }
      
      // Apply featured filter
      if (contentFilters.featuredFilter === 'featured') {
        items = items.filter(item => item.is_featured === true);
      } else if (contentFilters.featuredFilter === 'not_featured') {
        items = items.filter(item => item.is_featured !== true);
      }
      
      return {
        group: {
          ...group,
          // item_count comes from the API
        },
        items,
        loading: loadingGroupItems[group.id] || false,
        loaded: groupItems[group.id]?.loaded || false,
        total: group.item_count || 0,
        filteredCount: items.length
      };
    }).sort((a, b) => (a.group.priority || 0) - (b.group.priority || 0));
  }, [groups, groupItems, loadingGroupItems, contentFilters]);

  useEffect(() => {
    fetchGroups();
  }, [fetchGroups]);

  // Remove old fetchContent dependency for main content management
  // Content is now loaded per-group via lazy loading

  useEffect(() => {
    fetchAllContent();
  }, [fetchAllContent]);

  // Fetch system status on mount
  useEffect(() => {
    fetchSystemStatus();
  }, []);

  // Auto-load all group items when filters are applied
  const hasActiveFilters = contentFilters.titleSearch || contentFilters.descriptionSearch || contentFilters.featuredFilter !== 'all';
  
  useEffect(() => {
    if (hasActiveFilters && groups.length > 0) {
      // Load items for all groups that haven't been loaded yet
      groups.forEach(group => {
        if (!groupItems[group.id]?.loaded && !loadingGroupItems[group.id]) {
          fetchGroupItems(group.id);
        }
      });
    }
  }, [hasActiveFilters, groups, groupItems, loadingGroupItems, fetchGroupItems]);

  const handleCreateContent = async () => {
    setSaving(true);
    try {
      const token = localStorage.getItem('token');
      const headers = { Authorization: `Bearer ${token}`, 'Content-Type': 'application/json' };
      
      const payload = {
        ...formData,
        // Force is_featured to true for advertisements
        is_featured: formData.type === 'advertisement' ? true : formData.is_featured,
        start_date: new Date(formData.start_date).toISOString(),
        end_date: formData.end_date ? new Date(formData.end_date).toISOString() : null
      };
      
      await axios.post(`${API}/explore/admin/content`, payload, { headers });
      setShowCreateModal(false);
      setFormData(getDefaultFormData());
      // Refresh the group where content was created
      if (formData.group_id) {
        fetchGroupItems(formData.group_id, true);
      } else {
        fetchGroupItems('ungrouped', true);
      }
      fetchGroups();
      fetchAllContent();
      fetchData();
    } catch (err) {
      console.error('Failed to create content:', err);
      alert(err.response?.data?.detail || 'Failed to create content');
    } finally {
      setSaving(false);
    }
  };

  const handleUpdateContent = async () => {
    if (!selectedContent) return;
    setSaving(true);
    const scrollY = window.scrollY; // Capture scroll position immediately
    try {
      const token = localStorage.getItem('token');
      const headers = { Authorization: `Bearer ${token}`, 'Content-Type': 'application/json' };
      
      const payload = {
        ...formData,
        // Force is_featured to true for advertisements
        is_featured: formData.type === 'advertisement' ? true : formData.is_featured,
        start_date: new Date(formData.start_date).toISOString(),
        end_date: formData.end_date ? new Date(formData.end_date).toISOString() : null
      };
      
      const response = await axios.put(`${API}/explore/admin/content/${selectedContent.id}`, payload, { headers });
      const updatedItem = response.data;
      
      // Update the item in local state to avoid scroll jump from full refresh
      const groupId = formData.group_id || selectedContent.group_id || 'ungrouped';
      setGroupItems(prev => {
        const newState = { ...prev };
        if (newState[groupId]?.items) {
          newState[groupId] = {
            ...newState[groupId],
            items: newState[groupId].items.map(item => 
              item.id === selectedContent.id ? { ...item, ...updatedItem } : item
            )
          };
        }
        return newState;
      });
      
      setShowEditModal(false);
      setSelectedContent(null);
      
      // Restore scroll position immediately
      window.scrollTo(0, scrollY);
      
      // Background refresh for stats (non-blocking)
      fetchAllContent();
      fetchData();
    } catch (err) {
      console.error('Failed to update content:', err);
      alert(err.response?.data?.detail || 'Failed to update content');
    } finally {
      setSaving(false);
    }
  };

  const handleActivate = async (contentId) => {
    try {
      const token = localStorage.getItem('token');
      const headers = { Authorization: `Bearer ${token}` };
      await axios.post(`${API}/explore/admin/content/${contentId}/activate`, {}, { headers });
      refreshAllLoadedGroups();
      fetchAllContent();
      fetchData();
    } catch (err) {
      console.error('Failed to activate content:', err);
    }
  };

  const handleArchive = async (contentId) => {
    try {
      const token = localStorage.getItem('token');
      const headers = { Authorization: `Bearer ${token}` };
      await axios.post(`${API}/explore/admin/content/${contentId}/archive`, {}, { headers });
      refreshAllLoadedGroups();
      fetchAllContent();
      fetchData();
    } catch (err) {
      console.error('Failed to archive content:', err);
    }
  };

  const handleDeactivate = async (contentId) => {
    try {
      const token = localStorage.getItem('token');
      const headers = { Authorization: `Bearer ${token}` };
      await axios.post(`${API}/explore/admin/content/${contentId}/deactivate`, {}, { headers });
      refreshAllLoadedGroups();
      fetchAllContent();
      fetchData();
    } catch (err) {
      console.error('Failed to deactivate content:', err);
    }
  };

  const handleRestore = async (contentId) => {
    try {
      const token = localStorage.getItem('token');
      const headers = { Authorization: `Bearer ${token}` };
      await axios.post(`${API}/explore/admin/content/${contentId}/restore`, {}, { headers });
      refreshAllLoadedGroups();
      fetchAllContent();
      fetchData();
    } catch (err) {
      console.error('Failed to restore content:', err);
    }
  };

  const handleDelete = async (contentId) => {
    if (!window.confirm('Are you sure you want to delete this content?')) return;
    try {
      const token = localStorage.getItem('token');
      const headers = { Authorization: `Bearer ${token}` };
      await axios.delete(`${API}/explore/admin/content/${contentId}`, { headers });
      refreshAllLoadedGroups();
      fetchAllContent();
      fetchData();
    } catch (err) {
      console.error('Failed to delete content:', err);
    }
  };

  const handleDuplicateContent = async (contentId) => {
    try {
      const token = localStorage.getItem('token');
      const headers = { Authorization: `Bearer ${token}` };
      await axios.post(`${API}/explore/admin/content/${contentId}/duplicate`, {}, { headers });
      refreshAllLoadedGroups();
      fetchAllContent();
      fetchData();
    } catch (err) {
      console.error('Failed to duplicate content:', err);
      alert(err.response?.data?.detail || 'Failed to duplicate content');
    }
  };

  // ========== BACKUP / RESTORE / IMPORT / EXPORT HANDLERS ==========
  
  const fetchSystemStatus = async () => {
    try {
      const token = localStorage.getItem('token');
      const response = await axios.get(`${API}/explore/admin/system-status`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setSystemStatus(response.data);
    } catch (err) {
      console.error('Failed to fetch system status:', err);
    }
  };

  const fetchBackups = async () => {
    try {
      const token = localStorage.getItem('token');
      const response = await axios.get(`${API}/explore/admin/backups`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setBackups(response.data);
    } catch (err) {
      console.error('Failed to fetch backups:', err);
    }
  };

  const handleBackup = async () => {
    setBackupLoading(true);
    try {
      const token = localStorage.getItem('token');
      const response = await axios.post(`${API}/explore/admin/backup`, {}, {
        headers: { Authorization: `Bearer ${token}` }
      });
      
      alert(`✅ Backup created successfully!\n\nBackup ID: ${response.data.backup_id}\nContent backed up: ${response.data.content_backed_up}\nGroups backed up: ${response.data.groups_backed_up}`);
      
      // Refresh data
      await Promise.all([fetchSystemStatus(), fetchBackups()]);
      setShowBackupModal(false);
    } catch (err) {
      console.error('Backup failed:', err);
      alert(err.response?.data?.detail || 'Backup failed');
    } finally {
      setBackupLoading(false);
    }
  };

  const handleClear = async () => {
    // Double confirmation for destructive action
    const confirmText = window.prompt('Type "CLEAR" to confirm you want to delete all content:');
    if (confirmText !== 'CLEAR') {
      alert('Clear cancelled. You must type "CLEAR" exactly to confirm.');
      return;
    }
    
    setBackupLoading(true);
    try {
      const token = localStorage.getItem('token');
      const response = await axios.delete(`${API}/explore/admin/clear?confirm=true&create_backup=true`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      
      let message = `✅ System cleared!\n\nContent deleted: ${response.data.content_deleted}\nGroups deleted: ${response.data.groups_deleted}`;
      if (response.data.backup_created) {
        message += `\n\n📦 Auto-backup created: ${response.data.backup_id}`;
      }
      alert(message);
      
      // Refresh all data
      await Promise.all([fetchData(), fetchContent(), fetchAllContent(), fetchGroups(), fetchSystemStatus(), fetchBackups()]);
      setShowClearModal(false);
    } catch (err) {
      console.error('Clear failed:', err);
      alert(err.response?.data?.detail || 'Clear failed');
    } finally {
      setBackupLoading(false);
    }
  };

  const handleOpenRestoreModal = async () => {
    await Promise.all([fetchBackups(), fetchSystemStatus()]);
    setSelectedBackupId(null);
    setRestoreAnalysis(null);
    setMergeMode('add_new_only');
    setConflictResolutions({});
    setShowRestoreModal(true);
  };

  const handleAnalyzeRestore = async (backupId) => {
    setSelectedBackupId(backupId);
    setRestoreLoading(true);
    try {
      const token = localStorage.getItem('token');
      const response = await axios.post(`${API}/explore/admin/restore/${backupId}/analyze`, {}, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setRestoreAnalysis(response.data);
      // Reset conflict resolutions
      const defaultResolutions = {};
      response.data.fingerprint_conflicts?.forEach(conflict => {
        defaultResolutions[`${conflict.import_id}|${conflict.system_id}`] = 'keep_system';
      });
      setConflictResolutions(defaultResolutions);
    } catch (err) {
      console.error('Restore analysis failed:', err);
      alert(err.response?.data?.detail || 'Failed to analyze backup');
    } finally {
      setRestoreLoading(false);
    }
  };

  const handleRestore2 = async () => {
    if (!selectedBackupId) return;
    
    const modeLabels = {
      add_new_only: 'Add New Only',
      smart_merge: 'Smart Merge',
      force_update: 'Force Update',
      replace_all: 'Replace All'
    };
    
    if (!window.confirm(`Restore backup with mode: ${modeLabels[mergeMode]}?\n\nThis action cannot be undone.`)) {
      return;
    }
    
    setRestoreLoading(true);
    try {
      const token = localStorage.getItem('token');
      
      // Build conflict resolutions array
      const resolutions = Object.entries(conflictResolutions).map(([key, action]) => {
        const [import_id, system_id] = key.split('|');
        return { import_id, system_id, action };
      });
      
      const response = await axios.post(`${API}/explore/admin/restore/${selectedBackupId}`, {
        mode: mergeMode,
        conflict_resolutions: resolutions
      }, {
        headers: { Authorization: `Bearer ${token}` }
      });
      
      const s = response.data.stats;
      alert(`✅ Restore completed!\n\nContent added: ${s.content_added}\nContent updated: ${s.content_updated}\nContent skipped: ${s.content_skipped}\nGroups added: ${s.groups_added}\nGroups updated: ${s.groups_updated}`);
      
      await Promise.all([fetchData(), fetchContent(), fetchAllContent(), fetchGroups(), fetchSystemStatus()]);
      setShowRestoreModal(false);
    } catch (err) {
      console.error('Restore failed:', err);
      alert(err.response?.data?.detail || 'Restore failed');
    } finally {
      setRestoreLoading(false);
    }
  };

  const fetchDataLogs = async () => {
    setLogsLoading(true);
    try {
      const token = localStorage.getItem('token');
      const response = await axios.get(`${API}/explore/admin/data-logs?limit=100`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setDataLogs(response.data.logs || []);
    } catch (err) {
      console.error('Failed to fetch logs:', err);
      alert(err.response?.data?.detail || 'Failed to fetch logs');
    } finally {
      setLogsLoading(false);
    }
  };

  const handleClearLogs = async () => {
    if (!window.confirm('Are you sure you want to clear all data management logs?')) return;
    
    try {
      const token = localStorage.getItem('token');
      await axios.delete(`${API}/explore/admin/data-logs`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setDataLogs([]);
      alert('Logs cleared successfully');
    } catch (err) {
      console.error('Failed to clear logs:', err);
      alert(err.response?.data?.detail || 'Failed to clear logs');
    }
  };

  const handleExport = async () => {
    setExportLoading(true);
    try {
      const token = localStorage.getItem('token');
      const response = await axios.get(`${API}/explore/admin/export`, {
        headers: { Authorization: `Bearer ${token}` },
        responseType: 'blob'
      });
      
      // Get filename from Content-Disposition header
      const contentDisposition = response.headers['content-disposition'];
      let filename = 'explore_export.zip';
      if (contentDisposition) {
        const filenameMatch = contentDisposition.match(/filename=([^;]+)/);
        if (filenameMatch) {
          filename = filenameMatch[1].trim();
        }
      }
      
      // Create download link
      const blob = new Blob([response.data], { type: 'application/zip' });
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      link.download = filename;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      window.URL.revokeObjectURL(url);
    } catch (err) {
      console.error('Export failed:', err);
      alert(err.response?.data?.detail || 'Export failed');
    } finally {
      setExportLoading(false);
    }
  };

  const handleOpenImportModal = async () => {
    await fetchSystemStatus();
    setImportFile(null);
    setImportAnalysis(null);
    setMergeMode('add_new_only');
    setConflictResolutions({});
    setShowImportModal(true);
  };

  const handleImportFileSelect = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    
    if (!file.name.endsWith('.zip')) {
      alert('Please select a ZIP file exported from Explore Hub');
      return;
    }
    
    setImportFile(file);
    setImportLoading(true);
    
    try {
      const token = localStorage.getItem('token');
      const formData = new FormData();
      formData.append('file', file);
      
      const response = await axios.post(`${API}/explore/admin/import/analyze`, formData, {
        headers: { 
          Authorization: `Bearer ${token}`,
          'Content-Type': 'multipart/form-data'
        }
      });
      
      setImportAnalysis(response.data);
      // Initialize conflict resolutions
      const defaultResolutions = {};
      response.data.fingerprint_conflicts?.forEach(conflict => {
        defaultResolutions[`${conflict.import_id}|${conflict.system_id}`] = 'keep_system';
      });
      setConflictResolutions(defaultResolutions);
    } catch (err) {
      console.error('Import analysis failed:', err);
      alert(err.response?.data?.detail || 'Failed to analyze import file');
      setImportFile(null);
    } finally {
      setImportLoading(false);
    }
  };

  const handleImport = async () => {
    if (!importFile) return;
    
    const modeLabels = {
      add_new_only: 'Add New Only',
      smart_merge: 'Smart Merge',
      force_update: 'Force Update',
      replace_all: 'Replace All'
    };
    
    if (!window.confirm(`Import with mode: ${modeLabels[mergeMode]}?\n\nThis action cannot be undone.`)) {
      return;
    }
    
    setImportLoading(true);
    try {
      const token = localStorage.getItem('token');
      
      // Build conflict resolutions array
      const resolutions = Object.entries(conflictResolutions).map(([key, action]) => {
        const [import_id, system_id] = key.split('|');
        return { import_id, system_id, action };
      });
      
      const formData = new FormData();
      formData.append('file', importFile);
      
      const response = await axios.post(
        `${API}/explore/admin/import?mode=${mergeMode}&conflict_resolutions=${encodeURIComponent(JSON.stringify(resolutions))}`,
        formData,
        {
          headers: { 
            Authorization: `Bearer ${token}`,
            'Content-Type': 'multipart/form-data'
          }
        }
      );
      
      const s = response.data.stats;
      alert(`✅ Import completed!\n\nContent added: ${s.content_added}\nContent updated: ${s.content_updated}\nContent skipped: ${s.content_skipped}\nGroups added: ${s.groups_added}\nGroups updated: ${s.groups_updated}`);
      
      await Promise.all([fetchData(), fetchContent(), fetchAllContent(), fetchGroups(), fetchSystemStatus()]);
      setShowImportModal(false);
    } catch (err) {
      console.error('Import failed:', err);
      alert(err.response?.data?.detail || 'Import failed');
    } finally {
      setImportLoading(false);
    }
  };

  const handleDeleteBackup = async (backupId) => {
    if (!window.confirm('Delete this backup? This cannot be undone.')) return;
    
    try {
      const token = localStorage.getItem('token');
      await axios.delete(`${API}/explore/admin/backups/${backupId}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      await fetchBackups();
    } catch (err) {
      console.error('Failed to delete backup:', err);
      alert(err.response?.data?.detail || 'Failed to delete backup');
    }
  };

  // ========== CSV IMPORT/EXPORT HANDLERS ==========
  
  const handleCsvExport = async () => {
    setCsvExportLoading(true);
    try {
      const token = localStorage.getItem('token');
      const params = { content_type: activeTab };
      // Pass selected group IDs if not all are selected
      if (selectedExportGroups.length > 0) {
        params.group_ids = selectedExportGroups.join(',');
      }
      const response = await axios.get(`${API}/explore/admin/export-csv`, {
        params,
        headers: { Authorization: `Bearer ${token}` },
        responseType: 'blob'
      });
      
      // Create download link
      const blob = new Blob([response.data], { type: 'text/csv;charset=utf-8;' });
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      
      // Extract filename from content-disposition header or generate one
      const contentDisposition = response.headers['content-disposition'];
      let filename = `explore_${activeTab}_${new Date().toISOString().split('T')[0]}.csv`;
      if (contentDisposition) {
        const filenameMatch = contentDisposition.match(/filename[^;=\n]*=((['"]).*?\2|[^;\n]*)/);
        if (filenameMatch && filenameMatch[1]) {
          filename = filenameMatch[1].replace(/['"]/g, '');
        }
      }
      
      link.setAttribute('download', filename);
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);
      
    } catch (err) {
      console.error('CSV Export failed:', err);
      alert(err.response?.data?.detail || 'Export failed. Make sure there is content to export.');
    } finally {
      setCsvExportLoading(false);
    }
  };
  
  const handleOpenCsvImportModal = () => {
    setCsvImportFile(null);
    setCsvImportAnalysis(null);
    setShowCsvImportModal(true);
  };
  
  const handleCsvFileSelect = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    
    // Validate file type
    if (!file.name.endsWith('.csv')) {
      alert('Please select a CSV file');
      return;
    }
    
    setCsvImportFile(file);
    setCsvImportLoading(true);
    
    try {
      const token = localStorage.getItem('token');
      const formData = new FormData();
      formData.append('file', file);
      
      const response = await axios.post(
        `${API}/explore/admin/import-csv/analyze?content_type=${activeTab}`,
        formData,
        {
          headers: {
            Authorization: `Bearer ${token}`,
            'Content-Type': 'multipart/form-data'
          }
        }
      );
      
      setCsvImportAnalysis(response.data);
    } catch (err) {
      console.error('CSV analysis failed:', err);
      alert(err.response?.data?.detail || 'Failed to analyze CSV file');
      setCsvImportFile(null);
    } finally {
      setCsvImportLoading(false);
    }
  };
  
  const handleCsvImport = async () => {
    if (!csvImportFile || !csvImportAnalysis) return;
    
    const { valid_rows, rows_to_create, rows_to_update, rows_with_errors, groups_to_create } = csvImportAnalysis;
    
    if (valid_rows === 0) {
      alert('No valid rows to import. Please fix the errors and try again.');
      return;
    }
    
    let confirmMsg = `Import ${valid_rows} valid rows?\n\n`;
    confirmMsg += `• Create: ${rows_to_create} new items\n`;
    confirmMsg += `• Update: ${rows_to_update} existing items\n`;
    if (rows_with_errors > 0) {
      confirmMsg += `• Skip: ${rows_with_errors} rows with errors\n`;
    }
    if (groups_to_create?.length > 0) {
      confirmMsg += `\nNew groups to create: ${groups_to_create.join(', ')}\n`;
    }
    confirmMsg += '\nThis action cannot be undone.';
    
    if (!window.confirm(confirmMsg)) return;
    
    setCsvImportLoading(true);
    
    try {
      const token = localStorage.getItem('token');
      const formData = new FormData();
      formData.append('file', csvImportFile);
      
      const response = await axios.post(
        `${API}/explore/admin/import-csv?content_type=${activeTab}`,
        formData,
        {
          headers: {
            Authorization: `Bearer ${token}`,
            'Content-Type': 'multipart/form-data'
          }
        }
      );
      
      const result = response.data;
      let resultMsg = `✅ CSV Import Complete!\n\n`;
      resultMsg += `• Created: ${result.created} items\n`;
      resultMsg += `• Updated: ${result.updated} items\n`;
      resultMsg += `• Skipped: ${result.skipped} items\n`;
      if (result.groups_created?.length > 0) {
        resultMsg += `• Groups created: ${result.groups_created.join(', ')}\n`;
      }
      if (result.errors?.length > 0) {
        resultMsg += `\n⚠️ ${result.errors.length} rows had errors (check console for details)`;
        console.warn('Import errors:', result.errors);
      }
      if (result.geocoding_failures?.length > 0) {
        resultMsg += `\n⚠️ ${result.geocoding_failures.length} rows failed geocoding`;
        console.warn('Geocoding failures:', result.geocoding_failures);
      }
      
      alert(resultMsg);
      
      // Refresh data
      await Promise.all([fetchData(), fetchContent(), fetchAllContent(), fetchGroups()]);
      setShowCsvImportModal(false);
      
    } catch (err) {
      console.error('CSV Import failed:', err);
      alert(err.response?.data?.detail || 'Import failed');
    } finally {
      setCsvImportLoading(false);
    }
  };

  // ========== END BACKUP / RESTORE / IMPORT / EXPORT HANDLERS ==========

  const openCreateModal = () => {
    const defaultData = getDefaultFormData();
    setFormData({ 
      ...defaultData, 
      type: activeTab,
      // Auto-enable featured for advertisements
      is_featured: activeTab === 'advertisement' ? true : defaultData.is_featured
    });
    setShowCreateModal(true);
  };

  const openEditModal = (item) => {
    setSelectedContent(item);
    setFormData({
      type: item.type,
      title: item.title,
      short_description: item.short_description || '',
      long_description: item.long_description || '',
      image_url: item.image_url || '',
      link_url: item.link_url || '',
      link_label: item.link_label || '',
      local_subtype: item.local_subtype || 'supplier',
      start_date: item.start_date ? new Date(item.start_date).toISOString().slice(0, 16) : '',
      end_date: item.end_date ? new Date(item.end_date).toISOString().slice(0, 16) : '',
      targeting: item.targeting || {
        business_categories: [],
        regions: [],
        site_locale: { enabled: false, radius_km: 2, coordinates: null },
        around_me: { enabled: false, radius_km: 2, coordinates: null }
      },
      is_featured: item.is_featured || false,
      priority: item.priority || 0,
      background_color: item.background_color || '',
      background_image_url: item.background_image_url || '',
      featured_background_url: item.featured_background_url || '',
      address: item.address || {
        address_line_1: '',
        address_line_2: '',
        town_city: '',
        postcode: '',
        country: 'United Kingdom'
      },
      geocoded_coordinates: item.geocoded_coordinates || null
    });
    setShowEditModal(true);
  };

  const openMobileDetailModal = (item) => {
    setSelectedContent(item);
    setShowMobileDetailModal(true);
  };

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="text-center">
          <div className="inline-block animate-spin rounded-full h-8 w-8 border-b-2 border-purple-500"></div>
          <p className="mt-2 text-gray-400">Loading Explore Hub...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="bg-gradient-to-r from-purple-600 via-purple-700 to-indigo-700 rounded-xl p-6 text-white shadow-lg">
        <div className="flex items-center gap-3">
          <div className="w-12 h-12 bg-white/10 rounded-xl flex items-center justify-center">
            <svg className="w-7 h-7 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z" />
            </svg>
          </div>
          <div>
            <h1 className="text-2xl sm:text-3xl font-bold">Explore Hub</h1>
            <p className="text-purple-200">Manage content for the mobile app&apos;s Explore screen</p>
          </div>
        </div>
      </div>

      {/* Row 1: Phone Preview + Simulation Panel */}
      <div className="grid grid-cols-1 lg:grid-cols-5 gap-6">
        <div className="lg:col-span-3 bg-slate-800 rounded-xl p-6 shadow-lg">
          <div className="flex items-center justify-between mb-4">
            <div>
              <h2 className="text-lg font-semibold text-white">Interactive Preview</h2>
              <p className="text-slate-400 text-sm">Simulate what users see based on their profile</p>
            </div>
            <div className="flex items-center gap-2">
              {simulationActive && (
                <span className="flex items-center gap-1.5 text-amber-400 text-sm">
                  <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9.663 17h4.673M12 3v1m6.364 1.636l-.707.707M21 12h-1M4 12H3m3.343-5.657l-.707-.707m2.828 9.9a5 5 0 117.072 0l-.548.547A3.374 3.374 0 0014 18.469V19a2 2 0 11-4 0v-.531c0-.895-.356-1.754-.988-2.386l-.548-.547z" />
                  </svg>
                  Simulating
                </span>
              )}
              <span className="flex items-center gap-1.5 text-green-400 text-sm">
                <span className="w-2 h-2 bg-green-400 rounded-full animate-pulse"></span>
                Live
              </span>
            </div>
          </div>
          
          <div className="flex flex-col lg:flex-row gap-4 items-start">
            <div className="simulation-form-container flex-shrink-0">
          {/* Simulation Panel - Level 1 only */}
          {isLevel1User ? (
          <div className="bg-slate-900/50 rounded-lg p-6 border border-slate-700" style={{ width: '460px' }}>
            {/* Simulation Toggle & User Type - Pill Selectors */}
            <div className="flex items-center gap-6 mb-5">
              <div className="flex bg-slate-800 rounded-full p-0.5">
                <button
                  onClick={() => setSimulationActive(false)}
                  className={`px-4 py-1.5 rounded-full text-sm font-medium transition-colors ${
                    !simulationActive
                      ? 'bg-amber-500 text-white'
                      : 'text-slate-400 hover:text-white'
                  }`}
                >
                  Simulation OFF
                </button>
                <button
                  onClick={() => setSimulationActive(true)}
                  className={`px-4 py-1.5 rounded-full text-sm font-medium transition-colors ${
                    simulationActive
                      ? 'bg-amber-500 text-white'
                      : 'text-slate-400 hover:text-white'
                  }`}
                >
                  Simulation ON
                </button>
              </div>
              
              <div className="flex bg-slate-800 rounded-full p-0.5">
                <button
                  onClick={() => setSimulationData(prev => ({ ...prev, userType: 'admin' }))}
                  className={`px-4 py-1.5 rounded-full text-sm font-medium transition-colors ${
                    simulationData.userType === 'admin'
                      ? 'bg-purple-600 text-white'
                      : 'text-slate-400 hover:text-white'
                  }`}
                >
                  Admin
                </button>
                <button
                  onClick={() => setSimulationData(prev => ({ ...prev, userType: 'team_member' }))}
                  className={`px-4 py-1.5 rounded-full text-sm font-medium transition-colors ${
                    simulationData.userType === 'team_member'
                      ? 'bg-purple-600 text-white'
                      : 'text-slate-400 hover:text-white'
                  }`}
                >
                  Team Member
                </button>
              </div>
            </div>
            
            <div className="grid grid-cols-1 gap-5">
              {/* Organisation Category */}
              <div>
                <label className="block text-base text-slate-400 mb-2">Organisation Category</label>
                <select
                  value={simulationData.orgCategory}
                  onChange={(e) => setSimulationData(prev => ({ ...prev, orgCategory: e.target.value }))}
                  className="w-full px-3 py-2 bg-slate-800 border border-slate-600 rounded text-white text-sm"
                >
                  {businessCategories.map(cat => (
                    <option key={cat.name} value={cat.name}>{cat.name}</option>
                  ))}
                </select>
              </div>
              
              {/* Organisation Address (for region targeting) */}
              <div>
                <label className="block text-base text-slate-400 mb-2">Organisation Address <span className="text-slate-500 text-xs">(for region targeting)</span></label>
                <input
                  type="text"
                  value={simulationData.orgAddress}
                  onChange={(e) => setSimulationData(prev => ({ ...prev, orgAddress: e.target.value }))}
                  placeholder="Organisation address..."
                  className="w-full px-3 py-2 bg-slate-800 border border-slate-600 rounded text-white text-sm focus:outline-none focus:border-purple-500"
                />
                {simulationData.orgAddress && (
                  <p className="mt-1 text-xs text-slate-500">
                    Detected region: <span className="text-purple-400">{extractRegionFromAddress(simulationData.orgAddress) || 'Unknown'}</span>
                  </p>
                )}
              </div>
              
              {/* Site 1 */}
              <div>
                <label className="block text-base text-slate-400 mb-2">
                  Site 1
                </label>
                <input
                  type="text"
                  value={simulationData.site1.name}
                  onChange={(e) => setSimulationData(prev => ({ 
                    ...prev, 
                    site1: { ...prev.site1, name: e.target.value }
                  }))}
                  placeholder="Site name..."
                  className="w-full mb-2 px-3 py-2 bg-slate-800 border border-slate-600 rounded text-white text-sm focus:outline-none focus:border-purple-500"
                />
                <div className="flex gap-2">
                  <input
                    type="text"
                    value={simulationData.site1.address}
                    onChange={(e) => setSimulationData(prev => ({ 
                      ...prev, 
                      site1: { ...prev.site1, address: e.target.value, coordinates: null }
                    }))}
                    placeholder="Site address..."
                    className="flex-1 px-3 py-2 bg-slate-800 border border-slate-600 rounded text-white text-sm"
                  />
                  <button
                    onClick={() => geocodeSimulationAddress('site1')}
                    disabled={geocodingField === 'site1'}
                    className={`px-3 py-2 rounded text-sm font-medium ${
                      simulationData.site1.coordinates 
                        ? 'bg-green-600 text-white' 
                        : 'bg-slate-700 text-slate-300 hover:bg-slate-600'
                    }`}
                  >
                    {geocodingField === 'site1' ? '...' : simulationData.site1.coordinates ? '✓' : 'Geocode'}
                  </button>
                </div>
              </div>
              
              {/* Site 2 */}
              <div>
                <label className="block text-base text-slate-400 mb-2">
                  Site 2
                </label>
                <input
                  type="text"
                  value={simulationData.site2.name}
                  onChange={(e) => setSimulationData(prev => ({ 
                    ...prev, 
                    site2: { ...prev.site2, name: e.target.value }
                  }))}
                  placeholder="Site name..."
                  className="w-full mb-2 px-3 py-2 bg-slate-800 border border-slate-600 rounded text-white text-sm focus:outline-none focus:border-purple-500"
                />
                <div className="flex gap-2">
                  <input
                    type="text"
                    value={simulationData.site2.address}
                    onChange={(e) => setSimulationData(prev => ({ 
                      ...prev, 
                      site2: { ...prev.site2, address: e.target.value, coordinates: null }
                    }))}
                    placeholder="Site address..."
                    className="flex-1 px-3 py-2 bg-slate-800 border border-slate-600 rounded text-white text-sm"
                  />
                  <button
                    onClick={() => geocodeSimulationAddress('site2')}
                    disabled={geocodingField === 'site2'}
                    className={`px-3 py-2 rounded text-sm font-medium ${
                      simulationData.site2.coordinates 
                        ? 'bg-green-600 text-white' 
                        : 'bg-slate-700 text-slate-300 hover:bg-slate-600'
                    }`}
                  >
                    {geocodingField === 'site2' ? '...' : simulationData.site2.coordinates ? '✓' : 'Geocode'}
                  </button>
                </div>
              </div>
              
              {/* Current Location */}
              <div>
                <label className="block text-base text-slate-400 mb-2">Current Location</label>
                <div className="flex gap-2">
                  <input
                    type="text"
                    value={simulationData.currentLocation.address}
                    onChange={(e) => setSimulationData(prev => ({ 
                      ...prev, 
                      currentLocation: { ...prev.currentLocation, address: e.target.value, coordinates: null }
                    }))}
                    placeholder="Current location..."
                    className="flex-1 px-3 py-2 bg-slate-800 border border-slate-600 rounded text-white text-sm"
                  />
                  <button
                    onClick={() => geocodeSimulationAddress('currentLocation')}
                    disabled={geocodingField === 'currentLocation'}
                    className={`px-3 py-2 rounded text-sm font-medium ${
                      simulationData.currentLocation.coordinates 
                        ? 'bg-green-600 text-white' 
                        : 'bg-slate-700 text-slate-300 hover:bg-slate-600'
                    }`}
                  >
                    {geocodingField === 'currentLocation' ? '...' : simulationData.currentLocation.coordinates ? '✓' : 'Geocode'}
                  </button>
                </div>
              </div>
              
              {/* Assigned Sites - Only shown for Team Members */}
              {simulationData.userType === 'team_member' && (
                <div>
                  <label className="block text-base text-slate-400 mb-2">Assigned to Sites</label>
                  <div className="flex gap-3">
                    <label className="flex items-center gap-2 px-4 py-2 bg-slate-800 border border-slate-600 rounded cursor-pointer hover:bg-slate-700">
                      <input
                        type="checkbox"
                        checked={simulationData.assignedSites.includes('site1')}
                        onChange={(e) => {
                          setSimulationData(prev => ({
                            ...prev,
                            assignedSites: e.target.checked
                              ? [...prev.assignedSites, 'site1']
                              : prev.assignedSites.filter(s => s !== 'site1')
                          }));
                        }}
                        className="w-4 h-4 rounded border-slate-500 text-purple-600 focus:ring-purple-500 focus:ring-offset-0 bg-slate-700"
                      />
                      <span className="text-sm text-white">{simulationData.site1.name || 'Site 1'}</span>
                    </label>
                    <label className="flex items-center gap-2 px-4 py-2 bg-slate-800 border border-slate-600 rounded cursor-pointer hover:bg-slate-700">
                      <input
                        type="checkbox"
                        checked={simulationData.assignedSites.includes('site2')}
                        onChange={(e) => {
                          setSimulationData(prev => ({
                            ...prev,
                            assignedSites: e.target.checked
                              ? [...prev.assignedSites, 'site2']
                              : prev.assignedSites.filter(s => s !== 'site2')
                          }));
                        }}
                        className="w-4 h-4 rounded border-slate-500 text-purple-600 focus:ring-purple-500 focus:ring-offset-0 bg-slate-700"
                      />
                      <span className="text-sm text-white">{simulationData.site2.name || 'Site 2'}</span>
                    </label>
                  </div>
                  {simulationData.assignedSites.length === 0 && (
                    <p className="text-sm text-amber-400 mt-2">Team member not assigned to any sites</p>
                  )}
                </div>
              )}
            </div>
            
            {simulationActive && (
              <div className="mt-5 pt-4 border-t border-slate-700 flex items-center justify-between">
                <p className="text-sm text-slate-500">
                  Showing {getSimulatedContent().filter(c => c.status === 'active').length} of {allContent.filter(c => c.status === 'active').length} active items
                </p>
                <div className="flex gap-3">
                  {simulationData.site1.coordinates && (
                    <span className="text-sm text-green-400">Site 1 ✓</span>
                  )}
                  {simulationData.site2.coordinates && (
                    <span className="text-sm text-green-400">Site 2 ✓</span>
                  )}
                  {simulationData.currentLocation.coordinates && (
                    <span className="text-sm text-green-400">Location ✓</span>
                  )}
                </div>
              </div>
            )}
          </div>
          ) : (
            <div className="bg-slate-900/50 rounded-lg p-6 border border-slate-700" style={{ width: '460px' }}>
              <p className="text-slate-400 text-sm text-center">Simulation controls are restricted to Level 1 administrators.</p>
            </div>
          )}
          
          </div>
            <div className="flex-1 flex flex-col items-center justify-start min-w-0">

          <div className="flex justify-center w-full">
            <div className="phone-preview-container">
            <PhoneMockup 
              content={simulationActive ? getSimulatedContent() : allContent} 
              activeTab={mockupTab}
              onTabChange={setMockupTab}
              simulationData={simulationActive ? simulationData : null}
            />
            </div>
          </div>
          <p className="mt-4 text-center text-slate-500 text-sm">Mobile App Preview • Click to interact</p>
          </div>
          </div>
        </div>

        <div className="lg:col-span-2 space-y-4">
          <div className="grid grid-cols-2 gap-3">
            {[
              { key: 'local', label: 'Local Items', color: 'purple' },
              { key: 'deal', label: 'Active Deals', color: 'amber' },
              { key: 'event', label: 'Events', color: 'green' },
              { key: 'advertisement', label: 'Active Ads', color: 'blue' }
            ].map(({ key, label, color }) => (
              <div key={key} className="bg-slate-800 rounded-xl p-4 border border-slate-700">
                <div className="flex items-center justify-between">
                  <div className={`w-10 h-10 bg-${color}-500/20 rounded-lg flex items-center justify-center`}>
                    <svg className={`w-5 h-5 text-${color}-400`} fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d={getTypeIcon(key)} />
                    </svg>
                  </div>
                  <span className="text-2xl font-bold text-white">{stats?.by_type?.[key]?.active || 0}</span>
                </div>
                <p className="text-slate-400 text-sm mt-2">{label}</p>
              </div>
            ))}
          </div>

          <div className="bg-slate-800 rounded-xl p-5 border border-slate-700">
            <h3 className="text-white font-semibold mb-4">Quick Actions</h3>
            <button onClick={openCreateModal} className="w-full flex items-center gap-3 px-4 py-3 bg-purple-600 hover:bg-purple-700 text-white rounded-lg transition-colors">
              <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 6v6m0 0v6m0-6h6m-6 0H6" />
              </svg>
              <span>Create New Content</span>
            </button>
          </div>

          {/* Data Management - Level 1 only */}
          {isLevel1User && (
          <div className="bg-slate-800 rounded-xl p-5 border border-slate-700">
            <h3 className="text-white font-semibold mb-4 flex items-center gap-2">
              <svg className="w-5 h-5 text-cyan-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 7v10c0 2.21 3.582 4 8 4s8-1.79 8-4V7M4 7c0 2.21 3.582 4 8 4s8-1.79 8-4M4 7c0-2.21 3.582-4 8-4s8 1.79 8 4" />
              </svg>
              Data Management
            </h3>
            <div className="grid grid-cols-2 gap-2">
              <button 
                onClick={() => setShowBackupModal(true)} 
                disabled={systemStatus.is_empty}
                className="flex items-center justify-center gap-2 px-3 py-2.5 bg-emerald-600 hover:bg-emerald-700 disabled:bg-slate-600 disabled:cursor-not-allowed text-white text-sm rounded-lg transition-colors"
                title="Create Backup"
              >
                <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 7H5a2 2 0 00-2 2v9a2 2 0 002 2h14a2 2 0 002-2V9a2 2 0 00-2-2h-3m-1 4l-3 3m0 0l-3-3m3 3V4" />
                </svg>
                Backup
              </button>
              <button 
                onClick={() => setShowClearModal(true)}
                disabled={systemStatus.is_empty}
                className="flex items-center justify-center gap-2 px-3 py-2.5 bg-red-600 hover:bg-red-700 disabled:bg-slate-600 disabled:cursor-not-allowed text-white text-sm rounded-lg transition-colors"
                title="Clear All Content"
              >
                <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16" />
                </svg>
                Clear
              </button>
              <button 
                onClick={handleOpenRestoreModal}
                className="flex items-center justify-center gap-2 px-3 py-2.5 bg-teal-600 hover:bg-teal-700 text-white text-sm rounded-lg transition-colors"
                title="Restore from Backup"
              >
                <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 4v5h.582m15.356 2A8.001 8.001 0 004.582 9m0 0H9m11 11v-5h-.581m0 0a8.003 8.003 0 01-15.357-2m15.357 2H15" />
                </svg>
                Restore
              </button>
              <button 
                onClick={handleExport}
                disabled={exportLoading || systemStatus.is_empty}
                className="flex items-center justify-center gap-2 px-3 py-2.5 bg-blue-600 hover:bg-blue-700 disabled:bg-slate-600 disabled:cursor-not-allowed text-white text-sm rounded-lg transition-colors"
                title="Export to file"
              >
                <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 10v6m0 0l-3-3m3 3l3-3m2 8H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
                </svg>
                {exportLoading ? '...' : 'Export'}
              </button>
              <button 
                onClick={handleOpenImportModal}
                className="flex items-center justify-center gap-2 px-3 py-2.5 bg-indigo-600 hover:bg-indigo-700 text-white text-sm rounded-lg transition-colors"
                title="Import from file"
              >
                <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 16v1a3 3 0 003 3h10a3 3 0 003-3v-1m-4-8l-4-4m0 0L8 8m4-4v12" />
                </svg>
                Import
              </button>
              <button 
                onClick={() => { fetchDataLogs(); setShowLogsModal(true); }}
                className="flex items-center justify-center gap-2 px-3 py-2.5 bg-slate-600 hover:bg-slate-500 text-white text-sm rounded-lg transition-colors"
                title="View import/restore logs"
              >
                <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
                </svg>
                View Logs
              </button>
            </div>
            <p className="text-xs text-slate-500 mt-3">
              {systemStatus.content_count} content items • {systemStatus.groups_count} groups
            </p>
          </div>
          )}

          {/* Google Maps API Key - Level 1 only */}
          {isLevel1User && (
          <div className="bg-slate-800 rounded-xl p-5 border border-slate-700">
            <h3 className="text-white font-semibold mb-3 flex items-center gap-2">
              <svg className="w-5 h-5 text-blue-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17.657 16.657L13.414 20.9a1.998 1.998 0 01-2.827 0l-4.244-4.243a8 8 0 1111.314 0z" />
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 11a3 3 0 11-6 0 3 3 0 016 0z" />
              </svg>
              Google Maps API
            </h3>
            <div className="space-y-3">
              {googleMapsConfigured ? (
                <div className="flex items-center gap-2 text-green-400 text-sm">
                  <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                  </svg>
                  <span>Configured: {googleMapsKeyMasked}</span>
                </div>
              ) : (
                <div className="flex items-center gap-2 text-amber-400 text-sm">
                  <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z" />
                  </svg>
                  <span>Not configured</span>
                </div>
              )}
              <input
                type="password"
                value={googleMapsKey}
                onChange={e => setGoogleMapsKey(e.target.value)}
                className="w-full px-3 py-2 bg-slate-700 border border-slate-600 rounded-lg text-white text-sm placeholder-slate-400 focus:outline-none focus:border-purple-500"
                placeholder={googleMapsConfigured ? "Enter new key to update" : "Enter your Google Maps API key"}
              />
              <button
                onClick={saveGoogleMapsKey}
                disabled={savingApiKey || !googleMapsKey}
                className="w-full px-4 py-2 bg-blue-600 hover:bg-blue-700 disabled:bg-slate-600 disabled:cursor-not-allowed text-white text-sm rounded-lg transition-colors"
              >
                {savingApiKey ? 'Saving...' : 'Save API Key'}
              </button>
              <p className="text-xs text-slate-500">Required for geocoding addresses in content targeting.</p>
            </div>
          </div>
          )}

          <div className="bg-slate-800 rounded-xl p-5 border border-slate-700">
            <h3 className="text-white font-semibold mb-3">Summary</h3>
            <div className="space-y-3">
              <div className="flex justify-between items-center">
                <span className="text-slate-400">Total Content</span>
                <span className="text-white font-semibold">{stats?.total_content || 0}</span>
              </div>
              <div className="flex justify-between items-center">
                <span className="text-slate-400">Active Content</span>
                <span className="text-green-400 font-semibold">{stats?.total_active || 0}</span>
              </div>
              <div className="flex justify-between items-center">
                <span className="text-slate-400">Featured Items</span>
                <span className="text-amber-400 font-semibold">{stats?.featured_count || 0}</span>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Row 2: Content Table */}
      <div className="bg-slate-800 rounded-xl shadow-lg border border-slate-700">
        <div className="border-b border-slate-700">
          <div className="flex items-center justify-between">
            {/* Tabs */}
            <div className="flex overflow-x-auto">
              {[
                { id: 'local', label: 'Local' },
                { id: 'deal', label: 'Deals & Offers' },
                { id: 'event', label: 'Events & Expos' },
                { id: 'advertisement', label: 'Advertisements' }
              ].map((tab) => (
                <button
                  key={tab.id}
                  onClick={() => {
                    setActiveTab(tab.id);
                    setContentFilters({ titleSearch: '', descriptionSearch: '', featuredFilter: 'all' }); // Reset filters on tab change
                    setGroupItems({}); // Clear cached items - they're filtered by the old tab's type
                  }}
                  className={`flex items-center gap-2 px-6 py-4 text-sm font-medium whitespace-nowrap border-b-2 transition-colors ${
                    activeTab === tab.id ? 'border-purple-500 text-purple-400' : 'border-transparent text-slate-400 hover:text-slate-300'
                  }`}
                >
                  <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d={getTypeIcon(tab.id)} />
                  </svg>
                  {tab.label}
                  <span className={`ml-2 px-2 py-0.5 rounded-full text-xs ${activeTab === tab.id ? 'bg-purple-500/20 text-purple-400' : 'bg-slate-700 text-slate-400'}`}>
                    {stats?.by_type?.[tab.id]?.total || 0}
                  </span>
                </button>
              ))}
            </div>
            
            {/* Import/Export Buttons */}
            <div className="flex items-center gap-2 px-4">
              <button
                onClick={handleOpenCsvImportModal}
                disabled={!isLevel1User || csvImportLoading}
                className={`flex items-center gap-2 px-4 py-2 rounded-lg text-sm font-medium transition-colors ${
                  isLevel1User && !csvImportLoading
                    ? 'bg-slate-700 text-slate-300 hover:bg-slate-600 hover:text-white' 
                    : 'bg-slate-700/50 text-slate-500 cursor-not-allowed'
                }`}
                title={isLevel1User ? 'Import CSV' : 'Import restricted to Level 1 administrators'}
                data-testid="explore-import-btn"
              >
                <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 16v1a3 3 0 003 3h10a3 3 0 003-3v-1m-4-8l-4-4m0 0L8 8m4-4v12" />
                </svg>
                Import CSV
              </button>
              <button
                onClick={() => {
                  // Pre-select all groups for current tab + ungrouped
                  const tabGroups = groups.filter(g => g.type === activeTab && g.id !== 'ungrouped').map(g => g.id);
                  const ungroupedGroup = groups.find(g => g.id === 'ungrouped' && g.type === activeTab);
                  if (ungroupedGroup && (ungroupedGroup.item_count || 0) > 0) tabGroups.push('ungrouped');
                  setSelectedExportGroups(tabGroups);
                  setShowCsvExportModal(true);
                }}
                disabled={!isLevel1User}
                className={`flex items-center gap-2 px-4 py-2 rounded-lg text-sm font-medium transition-colors ${
                  isLevel1User
                    ? 'bg-slate-700 text-slate-300 hover:bg-slate-600 hover:text-white' 
                    : 'bg-slate-700/50 text-slate-500 cursor-not-allowed'
                }`}
                title={isLevel1User ? 'Export CSV' : 'Export restricted to Level 1 administrators'}
                data-testid="explore-export-btn"
              >
                <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 16v1a3 3 0 003 3h10a3 3 0 003-3v-1m-4-4l-4 4m0 0l-4-4m4 4V4" />
                </svg>
                Export CSV
              </button>
            </div>
          </div>
        </div>

        <div className="p-6">
          {/* Content Filters */}
          <div className="mb-4 p-4 bg-slate-700/30 rounded-lg">
            <div className="flex flex-wrap items-center gap-4">
              {/* Title Search */}
              <div className="min-w-[200px]">
                <label className="block text-slate-400 text-xs mb-1">Title</label>
                <div className="relative">
                  <svg className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z" />
                  </svg>
                  <input
                    type="text"
                    value={contentFilters.titleSearch}
                    onChange={(e) => setContentFilters(prev => ({ ...prev, titleSearch: e.target.value }))}
                    placeholder="Search title..."
                    className="w-full pl-10 pr-8 py-2 bg-slate-700 border border-slate-600 rounded-lg text-white text-sm placeholder-slate-400 focus:outline-none focus:border-purple-500"
                    data-testid="content-filter-title"
                  />
                  {contentFilters.titleSearch && (
                    <button
                      onClick={() => setContentFilters(prev => ({ ...prev, titleSearch: '' }))}
                      className="absolute right-2 top-1/2 -translate-y-1/2 p-1 text-slate-400 hover:text-white"
                    >
                      <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                      </svg>
                    </button>
                  )}
                </div>
              </div>
              
              {/* Description Search */}
              <div className="min-w-[200px]">
                <label className="block text-slate-400 text-xs mb-1">Description</label>
                <div className="relative">
                  <svg className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z" />
                  </svg>
                  <input
                    type="text"
                    value={contentFilters.descriptionSearch}
                    onChange={(e) => setContentFilters(prev => ({ ...prev, descriptionSearch: e.target.value }))}
                    placeholder="Search description..."
                    className="w-full pl-10 pr-8 py-2 bg-slate-700 border border-slate-600 rounded-lg text-white text-sm placeholder-slate-400 focus:outline-none focus:border-purple-500"
                    data-testid="content-filter-description"
                  />
                  {contentFilters.descriptionSearch && (
                    <button
                      onClick={() => setContentFilters(prev => ({ ...prev, descriptionSearch: '' }))}
                      className="absolute right-2 top-1/2 -translate-y-1/2 p-1 text-slate-400 hover:text-white"
                    >
                      <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                      </svg>
                    </button>
                  )}
                </div>
              </div>
              
              {/* Featured Filter */}
              <div>
                <label className="block text-slate-400 text-xs mb-1">Featured</label>
                <div className="flex bg-slate-700 rounded-lg p-1">
                  {[
                    { value: 'all', label: 'All' },
                    { value: 'featured', label: '★' },
                    { value: 'not_featured', label: '☆' }
                  ].map(option => (
                    <button
                      key={option.value}
                      onClick={() => setContentFilters(prev => ({ ...prev, featuredFilter: option.value }))}
                      className={`px-3 py-1.5 text-xs font-medium rounded transition-colors ${
                        contentFilters.featuredFilter === option.value
                          ? 'bg-purple-600 text-white'
                          : 'text-slate-400 hover:text-white'
                      }`}
                      data-testid={`content-filter-featured-${option.value}`}
                    >
                      {option.label}
                    </button>
                  ))}
                </div>
              </div>
              
              {/* Clear Filters */}
              {(contentFilters.titleSearch || contentFilters.descriptionSearch || contentFilters.featuredFilter !== 'all') && (
                <div className="self-end">
                  <button
                    onClick={() => setContentFilters({ titleSearch: '', descriptionSearch: '', featuredFilter: 'all' })}
                    className="flex items-center gap-1 px-3 py-2 text-xs font-medium text-slate-400 hover:text-white bg-slate-700 hover:bg-slate-600 rounded-lg transition-colors"
                    data-testid="content-filter-clear-all"
                  >
                    <svg className="w-3.5 h-3.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                    </svg>
                    Clear
                  </button>
                </div>
              )}
            </div>
            
            {/* Filter Results Count */}
            {(contentFilters.titleSearch || contentFilters.descriptionSearch || contentFilters.featuredFilter !== 'all') && (
              <div className="mt-3 text-xs text-slate-400">
                Showing {getGroupedContent().reduce((sum, g) => sum + g.filteredCount, 0)} filtered results
              </div>
            )}
          </div>

          {/* Default Type Color Picker */}
          <div className="mb-6 p-4 bg-slate-700/30 rounded-lg flex items-center gap-4">
            <label className="text-slate-300 text-sm font-medium">Default Type Colour:</label>
            <div className="flex items-center gap-2">
              <input
                type="color"
                value={typeColors[activeTab] || '#6366f1'}
                onChange={(e) => { updateTypeColor(activeTab, e.target.value); saveTypeColor(activeTab, e.target.value); }}
                className="w-8 h-8 rounded cursor-pointer border-0 bg-transparent"
              />
              <input
                type="text"
                value={typeColors[activeTab] || ''}
                onChange={(e) => updateTypeColor(activeTab, e.target.value)}
                onBlur={(e) => saveTypeColor(activeTab, e.target.value)}
                className="w-24 px-2 py-1.5 bg-slate-700 border border-slate-600 rounded text-white text-sm font-mono"
                placeholder="#000000"
              />
            </div>
            <div 
              className="w-24 h-8 rounded flex items-center justify-center text-white text-xs font-medium"
              style={{ backgroundColor: typeColors[activeTab] || '#6366f1' }}
            >
              Preview
            </div>
          </div>

          {content.length > 0 || groups.length > 0 ? (
            <div className="overflow-x-auto">
              <DndContext
                sensors={sensors}
                collisionDetection={closestCenter}
                onDragEnd={handleDragEnd}
              >
                <table className="w-full">
                  <thead>
                    <tr className="text-left border-b border-slate-700">
                      <th className="pb-3 w-10"></th>
                      <th className="pb-3 text-slate-400 font-medium text-sm" style={{width: isLevel1User ? '28%' : '42%'}}>Title</th>
                      <th className="pb-3 text-slate-400 font-medium text-sm" style={{width: '10%'}}>Status</th>
                      <th className="pb-3 text-slate-400 font-medium text-sm text-center" style={{width: '7%'}}>Site Locale</th>
                      <th className="pb-3 text-slate-400 font-medium text-sm text-center" style={{width: '7%'}}>Around Me</th>
                      <th className="pb-3 text-slate-400 font-medium text-sm" style={{width: '8%'}}>Featured</th>
                      <th className="pb-3 text-slate-400 font-medium text-sm" style={{width: '8%'}}>Views</th>
                      <th className="pb-3 text-slate-400 font-medium text-sm" style={{width: '8%'}}>Clicks</th>
                      {isLevel1User && <th className="pb-3 text-slate-400 font-medium text-sm" style={{width: '14%'}}>Actions</th>}
                    </tr>
                  </thead>
                  <SortableContext
                    items={getGroupedContent().flatMap(g => g.items.map(item => item.id))}
                    strategy={verticalListSortingStrategy}
                  >
                    <tbody>
                      {getGroupedContent().map(({ group, items, loading, loaded, total }) => (
                        <React.Fragment key={group.id}>
                          {/* Group Header Row - Droppable */}
                          {group.id !== 'ungrouped' && !group.is_virtual ? (
                            <DroppableGroupZone groupId={group.id} isEmpty={items.length === 0}>
                              <td colSpan={isLevel1User ? 9 : 8} className="py-2 px-3 bg-slate-700/50">
                                <div className="flex items-center gap-2">
                                  <button
                                    onClick={() => toggleGroupCollapsed(group.id)}
                                    className="p-1 hover:bg-slate-600 rounded"
                                  >
                                    <svg 
                                      className={`w-4 h-4 text-slate-400 transition-transform ${collapsedGroups[group.id] ? '' : 'rotate-90'}`} 
                                      fill="none" 
                                      stroke="currentColor" 
                                      viewBox="0 0 24 24"
                                    >
                                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
                                    </svg>
                                  </button>
                                  <svg className="w-4 h-4 text-purple-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 7v10a2 2 0 002 2h14a2 2 0 002-2V9a2 2 0 00-2-2h-6l-2-2H5a2 2 0 00-2 2z" />
                                  </svg>
                                  {editingGroupId === group.id ? (
                                    <input
                                      type="text"
                                      value={editingGroupName}
                                      onChange={(e) => setEditingGroupName(e.target.value)}
                                      onBlur={() => renameGroup(group.id)}
                                      onKeyDown={(e) => e.key === 'Enter' && renameGroup(group.id)}
                                      className="flex-1 px-2 py-1 bg-slate-600 border border-slate-500 rounded text-white text-sm"
                                      autoFocus
                                    />
                                  ) : (
                                    <span className="flex-1 text-white font-medium text-sm">{group.name}</span>
                                  )}
                                  <span className="text-slate-400 text-xs">({total})</span>
                                  {loading && (
                                    <span className="text-purple-400 text-xs animate-pulse">Loading...</span>
                                  )}
                                  {!loading && total === 0 && (
                                    <span className="text-slate-500 text-xs italic">Drop items here</span>
                                  )}
                                  {/* Group reorder arrows */}
                                  <div className="flex items-center gap-0.5 ml-2">
                                    <ActionButton 
                                      onClick={() => moveGroupUp(group.id)} 
                                      className={`p-1 rounded ${canMoveGroupUp(group.id) ? 'text-slate-400 hover:text-white hover:bg-slate-600' : 'text-slate-600 cursor-not-allowed'}`}
                                      tooltip="Move up"
                                    >
                                      <svg className="w-3.5 h-3.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 15l7-7 7 7" />
                                      </svg>
                                    </ActionButton>
                                    <ActionButton 
                                      onClick={() => moveGroupDown(group.id)} 
                                      className={`p-1 rounded ${canMoveGroupDown(group.id) ? 'text-slate-400 hover:text-white hover:bg-slate-600' : 'text-slate-600 cursor-not-allowed'}`}
                                      tooltip="Move down"
                                    >
                                      <svg className="w-3.5 h-3.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
                                      </svg>
                                    </ActionButton>
                                  </div>
                                  {isLevel1User && (
                                  <div className="relative group/menu">
                                    <button className="p-1 hover:bg-slate-600 rounded">
                                      <svg className="w-4 h-4 text-slate-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 5v.01M12 12v.01M12 19v.01M12 6a1 1 0 110-2 1 1 0 010 2zm0 7a1 1 0 110-2 1 1 0 010 2zm0 7a1 1 0 110-2 1 1 0 010 2z" />
                                      </svg>
                                    </button>
                                    <div className="absolute right-0 top-full mt-1 w-32 bg-slate-700 border border-slate-600 rounded-lg shadow-lg opacity-0 invisible group-hover/menu:opacity-100 group-hover/menu:visible transition-all z-10">
                                      <button
                                        onClick={() => { setEditingGroupId(group.id); setEditingGroupName(group.name); }}
                                        className="w-full px-3 py-2 text-left text-sm text-slate-300 hover:bg-slate-600 rounded-t-lg"
                                      >
                                        Rename
                                      </button>
                                      <button
                                        onClick={() => deleteGroup(group.id, group.name)}
                                        className="w-full px-3 py-2 text-left text-sm text-red-400 hover:bg-slate-600 rounded-b-lg"
                                      >
                                        Delete
                                      </button>
                                    </div>
                                  </div>
                                  )}
                                </div>
                              </td>
                            </DroppableGroupZone>
                          ) : (
                            <DroppableGroupZone groupId="ungrouped" isEmpty={items.length === 0}>
                              <td colSpan={isLevel1User ? 9 : 8} className="py-2 px-3 bg-slate-700/30">
                                <div className="flex items-center gap-2">
                                  <button
                                    onClick={() => toggleGroupCollapsed('ungrouped')}
                                    className="p-1 hover:bg-slate-600 rounded"
                                  >
                                    <svg 
                                      className={`w-4 h-4 text-slate-400 transition-transform ${collapsedGroups['ungrouped'] ? '' : 'rotate-90'}`} 
                                      fill="none" 
                                      stroke="currentColor" 
                                      viewBox="0 0 24 24"
                                    >
                                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
                                    </svg>
                                  </button>
                                  <svg className="w-4 h-4 text-slate-500" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 8h14M5 8a2 2 0 110-4h14a2 2 0 110 4M5 8v10a2 2 0 002 2h10a2 2 0 002-2V8m-9 4h4" />
                                  </svg>
                                  <span className="flex-1 text-slate-400 font-medium text-sm">Ungrouped</span>
                                  <span className="text-slate-500 text-xs">({total})</span>
                                  {loading && (
                                    <span className="text-purple-400 text-xs animate-pulse">Loading...</span>
                                  )}
                                </div>
                              </td>
                            </DroppableGroupZone>
                          )}
                          
                          {/* Group Items - Only rendered when expanded and loaded */}
                          {!collapsedGroups[group.id] && loading && (
                            <tr>
                              <td colSpan={isLevel1User ? 9 : 8} className="py-4 px-6 text-center">
                                <div className="flex items-center justify-center gap-2 text-purple-400">
                                  <svg className="w-5 h-5 animate-spin" fill="none" viewBox="0 0 24 24">
                                    <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                                    <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                                  </svg>
                                  <span>Loading {total} items...</span>
                                </div>
                              </td>
                            </tr>
                          )}
                          {!collapsedGroups[group.id] && !loading && items.map((item) => (
                            <SortableTableRow
                              key={item.id}
                              item={item}
                              onEdit={openEditModal}
                              onActivate={handleActivate}
                              onDeactivate={handleDeactivate}
                              onArchive={handleArchive}
                              onRestore={handleRestore}
                              onDelete={handleDelete}
                              onDuplicate={handleDuplicateContent}
                              showActions={isLevel1User}
                            />
                          ))}
                        </React.Fragment>
                      ))}
                    </tbody>
                  </SortableContext>
                </table>
              </DndContext>
              
              {/* Create Group Section */}
              <div className="mt-6 flex items-center gap-2 p-3 bg-slate-700/30 rounded-lg border border-dashed border-slate-600">
                <svg className="w-5 h-5 text-slate-500" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 6v6m0 0v6m0-6h6m-6 0H6" />
                </svg>
                <input
                  type="text"
                  value={newGroupName}
                  onChange={(e) => setNewGroupName(e.target.value)}
                  onKeyDown={(e) => e.key === 'Enter' && createGroup()}
                  className="flex-1 px-3 py-1.5 bg-slate-700 border border-slate-600 rounded text-white text-sm placeholder-slate-400"
                  placeholder="Enter new group name..."
                />
                <button
                  onClick={createGroup}
                  disabled={creatingGroup || !newGroupName.trim()}
                  className="px-4 py-1.5 bg-purple-600 hover:bg-purple-700 disabled:bg-slate-600 disabled:cursor-not-allowed text-white text-sm rounded transition-colors"
                >
                  {creatingGroup ? 'Creating...' : 'Create Group'}
                </button>
              </div>
              
              <p className="text-xs text-slate-500 mt-3 flex items-center gap-1">
                <svg className="w-3.5 h-3.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 8h16M4 16h16" />
                </svg>
                Drag rows to reorder or move between groups. Changes are saved automatically.
              </p>
            </div>
          ) : (
            <div className="text-center py-12">
              <div className="w-20 h-20 bg-slate-700 rounded-full flex items-center justify-center mx-auto mb-4">
                <svg className="w-10 h-10 text-slate-500" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M20 13V6a2 2 0 00-2-2H6a2 2 0 00-2 2v7m16 0v5a2 2 0 01-2 2H6a2 2 0 01-2-2v-5m16 0h-2.586a1 1 0 00-.707.293l-2.414 2.414a1 1 0 01-.707.293h-3.172a1 1 0 01-.707-.293l-2.414-2.414A1 1 0 006.586 13H4" />
                </svg>
              </div>
              <h3 className="text-lg font-semibold text-white mb-2">No {getTypeLabel(activeTab)} yet</h3>
              <p className="text-slate-400 mb-6">Start adding content to the Explore screen.</p>
              <button onClick={openCreateModal} className="inline-flex items-center gap-2 px-6 py-3 bg-purple-600 hover:bg-purple-700 text-white rounded-lg transition-colors">
                <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 6v6m0 0v6m0-6h6m-6 0H6" />
                </svg>
                Add First {activeTab === 'advertisement' ? 'Advertisement' : activeTab === 'event' ? 'Event' : activeTab === 'deal' ? 'Deal' : 'Local Item'}
              </button>
            </div>
          )}
        </div>
      </div>

      {/* Modals */}
      {showCreateModal && (
        <ContentFormModal 
          formData={formData}
          setFormData={setFormData}
          categories={categories}
          regions={regions}
          countries={countries}
          onClose={() => setShowCreateModal(false)}
          onSave={handleCreateContent}
          saving={saving}
          isEdit={false}
          googleMapsConfigured={googleMapsConfigured}
        />
      )}
      {showEditModal && (
        <ContentFormModal 
          formData={formData}
          setFormData={setFormData}
          categories={categories}
          regions={regions}
          countries={countries}
          onClose={() => { setShowEditModal(false); setSelectedContent(null); }}
          onSave={handleUpdateContent}
          saving={saving}
          isEdit={true}
          googleMapsConfigured={googleMapsConfigured}
        />
      )}
      {showMobileDetailModal && (
        <MobileDetailModal 
          content={selectedContent}
          onClose={() => { setShowMobileDetailModal(false); setSelectedContent(null); }}
        />
      )}

      {/* Backup Modal */}
      {showBackupModal && (
        <div className="fixed inset-0 bg-black/70 flex items-center justify-center z-50 p-4">
          <div className="bg-slate-800 rounded-xl max-w-md w-full shadow-2xl">
            <div className="p-6 border-b border-slate-700">
              <h2 className="text-xl font-bold text-white flex items-center gap-2">
                <svg className="w-6 h-6 text-emerald-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 7H5a2 2 0 00-2 2v9a2 2 0 002 2h14a2 2 0 002-2V9a2 2 0 00-2-2h-3m-1 4l-3 3m0 0l-3-3m3 3V4" />
                </svg>
                Create Backup
              </h2>
            </div>
            <div className="p-6 space-y-4">
              <div className="bg-slate-900/50 rounded-lg p-4">
                <p className="text-slate-300">This will create a backup of:</p>
                <ul className="list-disc list-inside text-slate-400 mt-2 space-y-1 text-sm">
                  <li>{systemStatus.content_count} content items</li>
                  <li>{systemStatus.groups_count} groups</li>
                </ul>
              </div>
              <p className="text-slate-400 text-sm">Only the 5 most recent backups are retained.</p>
            </div>
            <div className="p-6 border-t border-slate-700 flex gap-3 justify-end">
              <button
                onClick={() => setShowBackupModal(false)}
                className="px-4 py-2 text-slate-300 hover:text-white transition-colors"
              >
                Cancel
              </button>
              <button
                onClick={handleBackup}
                disabled={backupLoading || systemStatus.is_empty}
                className="px-4 py-2 bg-emerald-600 hover:bg-emerald-700 disabled:bg-slate-600 disabled:cursor-not-allowed text-white rounded-lg transition-colors flex items-center gap-2"
              >
                {backupLoading ? (
                  <>
                    <svg className="w-4 h-4 animate-spin" fill="none" viewBox="0 0 24 24">
                      <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                      <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                    </svg>
                    Creating...
                  </>
                ) : 'Create Backup'}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Clear Content Modal */}
      {showClearModal && (
        <div className="fixed inset-0 bg-black/70 flex items-center justify-center z-50 p-4">
          <div className="bg-slate-800 rounded-xl max-w-md w-full shadow-2xl">
            <div className="p-6 border-b border-slate-700">
              <h2 className="text-xl font-bold text-white flex items-center gap-2">
                <svg className="w-6 h-6 text-red-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z" />
                </svg>
                Clear All Content
              </h2>
            </div>
            <div className="p-6 space-y-4">
              <div className="bg-red-900/20 border border-red-700/50 rounded-lg p-4">
                <p className="text-red-300 font-medium">⚠️ Destructive Action</p>
                <p className="text-slate-400 mt-2 text-sm">
                  This will permanently delete all {systemStatus.content_count} content items and {systemStatus.groups_count} groups.
                </p>
              </div>
              <p className="text-slate-400 text-sm">
                Make sure you have created a backup first if you want to restore this content later.
              </p>
              <p className="text-amber-400 text-sm">
                You will be asked to type &quot;CLEAR&quot; to confirm this action.
              </p>
            </div>
            <div className="p-6 border-t border-slate-700 flex gap-3 justify-end">
              <button
                onClick={() => setShowClearModal(false)}
                className="px-4 py-2 text-slate-300 hover:text-white transition-colors"
              >
                Cancel
              </button>
              <button
                onClick={handleClear}
                disabled={backupLoading || systemStatus.is_empty}
                className="px-4 py-2 bg-red-600 hover:bg-red-700 disabled:bg-slate-600 disabled:cursor-not-allowed text-white rounded-lg transition-colors flex items-center gap-2"
              >
                {backupLoading ? (
                  <>
                    <svg className="w-4 h-4 animate-spin" fill="none" viewBox="0 0 24 24">
                      <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                      <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                    </svg>
                    Clearing...
                  </>
                ) : 'Clear All Content'}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Restore Modal */}
      {showRestoreModal && (
        <div className="fixed inset-0 bg-black/70 flex items-center justify-center z-50 p-4 overflow-y-auto">
          <div className="bg-slate-800 rounded-xl max-w-2xl w-full shadow-2xl my-8">
            <div className="p-6 border-b border-slate-700">
              <h2 className="text-xl font-bold text-white flex items-center gap-2">
                <svg className="w-6 h-6 text-teal-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 4v5h.582m15.356 2A8.001 8.001 0 004.582 9m0 0H9m11 11v-5h-.581m0 0a8.003 8.003 0 01-15.357-2m15.357 2H15" />
                </svg>
                Restore from Backup
              </h2>
            </div>
            <div className="p-6 space-y-4 max-h-[60vh] overflow-y-auto">
              {/* Backup List */}
              {backups.length === 0 ? (
                <p className="text-slate-400 text-center py-8">No backups available</p>
              ) : (
                <div className="space-y-2">
                  <p className="text-slate-400 text-sm mb-3">Select a backup to restore:</p>
                  {backups.map(backup => (
                    <div 
                      key={backup.id}
                      className={`p-4 rounded-lg border cursor-pointer transition-colors ${
                        selectedBackupId === backup.id 
                          ? 'bg-teal-900/30 border-teal-500' 
                          : 'bg-slate-900/50 border-slate-700 hover:border-slate-500'
                      }`}
                      onClick={() => handleAnalyzeRestore(backup.id)}
                    >
                      <div className="flex justify-between items-start">
                        <div>
                          <p className="text-white font-medium">
                            {new Date(backup.created_at).toLocaleString()}
                          </p>
                          <p className="text-slate-400 text-sm">
                            {backup.content_count} content items • {backup.groups_count} groups
                          </p>
                          <p className="text-slate-500 text-xs mt-1">
                            Created by: {backup.created_by}
                          </p>
                        </div>
                        <button
                          onClick={(e) => { e.stopPropagation(); handleDeleteBackup(backup.id); }}
                          className="p-1.5 text-slate-500 hover:text-red-400 transition-colors"
                          title="Delete backup"
                        >
                          <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16" />
                          </svg>
                        </button>
                      </div>
                    </div>
                  ))}
                </div>
              )}

              {/* Analysis Results */}
              {restoreLoading && (
                <div className="flex items-center justify-center py-8">
                  <svg className="w-8 h-8 animate-spin text-teal-400" fill="none" viewBox="0 0 24 24">
                    <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                    <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                  </svg>
                </div>
              )}

              {restoreAnalysis && !restoreLoading && (
                <div className="space-y-4 mt-4 pt-4 border-t border-slate-700">
                  <h3 className="text-white font-semibold">Analysis Results</h3>
                  
                  {/* Summary */}
                  <div className="grid grid-cols-2 gap-3">
                    <div className="bg-slate-900/50 p-3 rounded-lg">
                      <p className="text-slate-400 text-xs">Backup contains</p>
                      <p className="text-white font-medium">{restoreAnalysis.file_content_count} content, {restoreAnalysis.file_groups_count} groups</p>
                    </div>
                    <div className="bg-slate-900/50 p-3 rounded-lg">
                      <p className="text-slate-400 text-xs">Current system</p>
                      <p className="text-white font-medium">{restoreAnalysis.current_content_count} content, {restoreAnalysis.current_groups_count} groups</p>
                    </div>
                  </div>

                  {/* Analysis breakdown */}
                  <div className="bg-slate-900/50 p-4 rounded-lg space-y-2">
                    <div className="flex items-center gap-2 text-green-400 text-sm">
                      <span className="w-2 h-2 bg-green-400 rounded-full"></span>
                      {restoreAnalysis.new_content_count} new items (will be added)
                    </div>
                    <div className="flex items-center gap-2 text-blue-400 text-sm">
                      <span className="w-2 h-2 bg-blue-400 rounded-full"></span>
                      {restoreAnalysis.id_match_content_count} ID matches (same ID exists)
                    </div>
                    {restoreAnalysis.fingerprint_conflicts.length > 0 && (
                      <div className="flex items-center gap-2 text-amber-400 text-sm">
                        <span className="w-2 h-2 bg-amber-400 rounded-full"></span>
                        {restoreAnalysis.fingerprint_conflicts.length} content conflicts (same content, different ID)
                      </div>
                    )}
                  </div>

                  {/* Fingerprint Conflicts */}
                  {restoreAnalysis.fingerprint_conflicts.length > 0 && (
                    <div className="space-y-3">
                      <h4 className="text-amber-400 font-medium text-sm flex items-center gap-2">
                        <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z" />
                        </svg>
                        Content Conflicts - Resolve each:
                      </h4>
                      {restoreAnalysis.fingerprint_conflicts.map((conflict, idx) => (
                        <div key={idx} className="bg-amber-900/20 border border-amber-700/50 rounded-lg p-4">
                          <p className="text-white font-medium">&quot;{conflict.import_title}&quot;</p>
                          <p className="text-slate-400 text-xs mt-1">Type: {conflict.import_type} • {conflict.match_reason}</p>
                          <div className="mt-3 flex flex-wrap gap-2">
                            {['keep_system', 'use_import', 'keep_both'].map(action => (
                              <label key={action} className="flex items-center gap-2 cursor-pointer">
                                <input
                                  type="radio"
                                  name={`conflict-${conflict.import_id}-${conflict.system_id}`}
                                  checked={conflictResolutions[`${conflict.import_id}|${conflict.system_id}`] === action}
                                  onChange={() => setConflictResolutions(prev => ({
                                    ...prev,
                                    [`${conflict.import_id}|${conflict.system_id}`]: action
                                  }))}
                                  className="text-teal-500"
                                />
                                <span className="text-slate-300 text-sm">
                                  {action === 'keep_system' ? 'Keep current' : action === 'use_import' ? 'Use backup' : 'Keep both'}
                                </span>
                              </label>
                            ))}
                          </div>
                        </div>
                      ))}
                    </div>
                  )}

                  {/* Merge Mode */}
                  <div className="space-y-3">
                    <h4 className="text-white font-medium text-sm">Merge Strategy:</h4>
                    <div className="grid grid-cols-2 gap-2">
                      {[
                        { value: 'add_new_only', label: 'Add New Only', desc: 'Only add new items, skip existing' },
                        { value: 'smart_merge', label: 'Smart Merge', desc: 'Add new + apply conflict choices above' },
                        { value: 'force_update', label: 'Force Update', desc: 'Add new + overwrite all matches' },
                        { value: 'replace_all', label: 'Replace All', desc: 'Clear system, restore everything' }
                      ].map(mode => (
                        <label 
                          key={mode.value}
                          className={`p-3 rounded-lg border cursor-pointer transition-colors ${
                            mergeMode === mode.value 
                              ? 'bg-teal-900/30 border-teal-500' 
                              : 'bg-slate-900/50 border-slate-700 hover:border-slate-500'
                          }`}
                        >
                          <input
                            type="radio"
                            name="mergeMode"
                            value={mode.value}
                            checked={mergeMode === mode.value}
                            onChange={(e) => setMergeMode(e.target.value)}
                            className="sr-only"
                          />
                          <p className="text-white text-sm font-medium">{mode.label}</p>
                          <p className="text-slate-400 text-xs mt-1">{mode.desc}</p>
                        </label>
                      ))}
                    </div>
                  </div>
                </div>
              )}
            </div>
            <div className="p-6 border-t border-slate-700 flex gap-3 justify-end">
              <button
                onClick={() => setShowRestoreModal(false)}
                className="px-4 py-2 text-slate-300 hover:text-white transition-colors"
              >
                Cancel
              </button>
              <button
                onClick={handleRestore2}
                disabled={restoreLoading || !selectedBackupId || !restoreAnalysis}
                className="px-4 py-2 bg-teal-600 hover:bg-teal-700 disabled:bg-slate-600 disabled:cursor-not-allowed text-white rounded-lg transition-colors"
              >
                {restoreLoading ? 'Processing...' : 'Restore'}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Import Modal */}
      {showImportModal && (
        <div className="fixed inset-0 bg-black/70 flex items-center justify-center z-50 p-4 overflow-y-auto">
          <div className="bg-slate-800 rounded-xl max-w-2xl w-full shadow-2xl my-8">
            <div className="p-6 border-b border-slate-700">
              <h2 className="text-xl font-bold text-white flex items-center gap-2">
                <svg className="w-6 h-6 text-indigo-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 16v1a3 3 0 003 3h10a3 3 0 003-3v-1m-4-8l-4-4m0 0L8 8m4-4v12" />
                </svg>
                Import Content
              </h2>
            </div>
            <div className="p-6 space-y-4 max-h-[60vh] overflow-y-auto">
              {/* File Upload */}
              {!importFile && (
                <div className="border-2 border-dashed border-slate-600 rounded-xl p-8 text-center">
                  <svg className="w-12 h-12 mx-auto text-slate-500 mb-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M7 16a4 4 0 01-.88-7.903A5 5 0 1115.9 6L16 6a5 5 0 011 9.9M15 13l-3-3m0 0l-3 3m3-3v12" />
                  </svg>
                  <p className="text-slate-400 mb-4">Select an exported ZIP file to import</p>
                  <label className="cursor-pointer inline-flex items-center gap-2 px-4 py-2 bg-indigo-600 hover:bg-indigo-700 text-white rounded-lg transition-colors">
                    <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15.172 7l-6.586 6.586a2 2 0 102.828 2.828l6.414-6.586a4 4 0 00-5.656-5.656l-6.415 6.585a6 6 0 108.486 8.486L20.5 13" />
                    </svg>
                    Choose File
                    <input
                      type="file"
                      accept=".zip"
                      onChange={handleImportFileSelect}
                      className="hidden"
                    />
                  </label>
                </div>
              )}

              {/* Loading */}
              {importLoading && (
                <div className="flex items-center justify-center py-8">
                  <svg className="w-8 h-8 animate-spin text-indigo-400" fill="none" viewBox="0 0 24 24">
                    <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                    <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                  </svg>
                </div>
              )}

              {/* Analysis Results */}
              {importAnalysis && !importLoading && (
                <div className="space-y-4">
                  {/* Version Warning */}
                  {importAnalysis.version_warning && (
                    <div className="bg-amber-900/30 border border-amber-600 p-3 rounded-lg">
                      <div className="flex items-center gap-2 text-amber-400 text-sm">
                        <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z" />
                        </svg>
                        <span className="font-medium">Version Mismatch</span>
                      </div>
                      <p className="text-amber-300 text-sm mt-1">{importAnalysis.version_warning}</p>
                      <p className="text-amber-300/70 text-xs mt-1">
                        File version: {importAnalysis.import_version} → Current version: {importAnalysis.current_version}
                      </p>
                    </div>
                  )}
                  
                  {/* Validation Errors */}
                  {importAnalysis.validation_errors && importAnalysis.validation_errors.length > 0 && (
                    <div className="bg-red-900/30 border border-red-600 p-3 rounded-lg">
                      <div className="flex items-center gap-2 text-red-400 text-sm">
                        <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8v4m0 4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
                        </svg>
                        <span className="font-medium">{importAnalysis.validation_errors.length} Validation Error(s)</span>
                      </div>
                      <div className="mt-2 max-h-32 overflow-y-auto space-y-1">
                        {importAnalysis.validation_errors.slice(0, 5).map((err, idx) => (
                          <div key={idx} className="text-red-300 text-xs">
                            • {err.type === 'content' ? err.title : err.name}: {err.errors.join(', ')}
                          </div>
                        ))}
                        {importAnalysis.validation_errors.length > 5 && (
                          <p className="text-red-300/70 text-xs">...and {importAnalysis.validation_errors.length - 5} more</p>
                        )}
                      </div>
                    </div>
                  )}
                  
                  {/* File Info */}
                  <div className="flex items-center justify-between bg-slate-900/50 p-4 rounded-lg">
                    <div className="flex items-center gap-3">
                      <svg className="w-8 h-8 text-indigo-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
                      </svg>
                      <div>
                        <p className="text-white font-medium">{importFile?.name}</p>
                        <p className="text-slate-400 text-sm">
                          Exported: {importAnalysis.manifest?.exported_at ? new Date(importAnalysis.manifest.exported_at).toLocaleString() : 'Unknown'}
                          {importAnalysis.manifest?.version && ` • v${importAnalysis.manifest.version}`}
                        </p>
                      </div>
                    </div>
                    <button
                      onClick={() => { setImportFile(null); setImportAnalysis(null); }}
                      className="p-2 text-slate-400 hover:text-white transition-colors"
                    >
                      <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                      </svg>
                    </button>
                  </div>

                  {/* Summary */}
                  <div className="grid grid-cols-2 gap-3">
                    <div className="bg-slate-900/50 p-3 rounded-lg">
                      <p className="text-slate-400 text-xs">File contains</p>
                      <p className="text-white font-medium">{importAnalysis.file_content_count} content, {importAnalysis.file_groups_count} groups</p>
                    </div>
                    <div className="bg-slate-900/50 p-3 rounded-lg">
                      <p className="text-slate-400 text-xs">Current system</p>
                      <p className="text-white font-medium">{importAnalysis.current_content_count} content, {importAnalysis.current_groups_count} groups</p>
                    </div>
                  </div>

                  {/* Analysis breakdown */}
                  <div className="bg-slate-900/50 p-4 rounded-lg space-y-2">
                    <div className="flex items-center gap-2 text-green-400 text-sm">
                      <span className="w-2 h-2 bg-green-400 rounded-full"></span>
                      {importAnalysis.new_content_count} new items (will be added)
                    </div>
                    <div className="flex items-center gap-2 text-blue-400 text-sm">
                      <span className="w-2 h-2 bg-blue-400 rounded-full"></span>
                      {importAnalysis.id_match_content_count} ID matches (same ID exists)
                    </div>
                    {importAnalysis.fingerprint_conflicts.length > 0 && (
                      <div className="flex items-center gap-2 text-amber-400 text-sm">
                        <span className="w-2 h-2 bg-amber-400 rounded-full"></span>
                        {importAnalysis.fingerprint_conflicts.length} content conflicts (same content, different ID)
                      </div>
                    )}
                    {importAnalysis.validation_errors && importAnalysis.validation_errors.length > 0 && (
                      <div className="flex items-center gap-2 text-red-400 text-sm">
                        <span className="w-2 h-2 bg-red-400 rounded-full"></span>
                        {importAnalysis.validation_errors.length} items will be skipped (validation failed)
                      </div>
                    )}
                  </div>

                  {/* Fingerprint Conflicts */}
                  {importAnalysis.fingerprint_conflicts.length > 0 && (
                    <div className="space-y-3">
                      <h4 className="text-amber-400 font-medium text-sm flex items-center gap-2">
                        <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z" />
                        </svg>
                        Content Conflicts - Resolve each:
                      </h4>
                      {importAnalysis.fingerprint_conflicts.map((conflict, idx) => (
                        <div key={idx} className="bg-amber-900/20 border border-amber-700/50 rounded-lg p-4">
                          <p className="text-white font-medium">&quot;{conflict.import_title}&quot;</p>
                          <p className="text-slate-400 text-xs mt-1">Type: {conflict.import_type} • {conflict.match_reason}</p>
                          <div className="mt-3 flex flex-wrap gap-2">
                            {['keep_system', 'use_import', 'keep_both'].map(action => (
                              <label key={action} className="flex items-center gap-2 cursor-pointer">
                                <input
                                  type="radio"
                                  name={`import-conflict-${conflict.import_id}-${conflict.system_id}`}
                                  checked={conflictResolutions[`${conflict.import_id}|${conflict.system_id}`] === action}
                                  onChange={() => setConflictResolutions(prev => ({
                                    ...prev,
                                    [`${conflict.import_id}|${conflict.system_id}`]: action
                                  }))}
                                  className="text-indigo-500"
                                />
                                <span className="text-slate-300 text-sm">
                                  {action === 'keep_system' ? 'Keep current' : action === 'use_import' ? 'Use import' : 'Keep both'}
                                </span>
                              </label>
                            ))}
                          </div>
                        </div>
                      ))}
                    </div>
                  )}

                  {/* Merge Mode */}
                  <div className="space-y-3">
                    <h4 className="text-white font-medium text-sm">Merge Strategy:</h4>
                    <div className="grid grid-cols-2 gap-2">
                      {[
                        { value: 'add_new_only', label: 'Add New Only', desc: 'Only add new items, skip existing' },
                        { value: 'smart_merge', label: 'Smart Merge', desc: 'Add new + apply conflict choices above' },
                        { value: 'force_update', label: 'Force Update', desc: 'Add new + overwrite all matches' },
                        { value: 'replace_all', label: 'Replace All', desc: 'Clear system, import everything' }
                      ].map(mode => (
                        <label 
                          key={mode.value}
                          className={`p-3 rounded-lg border cursor-pointer transition-colors ${
                            mergeMode === mode.value 
                              ? 'bg-indigo-900/30 border-indigo-500' 
                              : 'bg-slate-900/50 border-slate-700 hover:border-slate-500'
                          }`}
                        >
                          <input
                            type="radio"
                            name="importMergeMode"
                            value={mode.value}
                            checked={mergeMode === mode.value}
                            onChange={(e) => setMergeMode(e.target.value)}
                            className="sr-only"
                          />
                          <p className="text-white text-sm font-medium">{mode.label}</p>
                          <p className="text-slate-400 text-xs mt-1">{mode.desc}</p>
                        </label>
                      ))}
                    </div>
                  </div>
                </div>
              )}
            </div>
            <div className="p-6 border-t border-slate-700 flex gap-3 justify-end">
              <button
                onClick={() => setShowImportModal(false)}
                className="px-4 py-2 text-slate-300 hover:text-white transition-colors"
              >
                Cancel
              </button>
              <button
                onClick={handleImport}
                disabled={importLoading || !importFile || !importAnalysis}
                className="px-4 py-2 bg-indigo-600 hover:bg-indigo-700 disabled:bg-slate-600 disabled:cursor-not-allowed text-white rounded-lg transition-colors"
              >
                {importLoading ? 'Processing...' : 'Import'}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Logs Modal */}
      {showLogsModal && (
        <div className="fixed inset-0 bg-black/70 flex items-center justify-center z-50 p-4">
          <div className="bg-slate-800 rounded-xl border border-slate-700 w-full max-w-4xl max-h-[85vh] flex flex-col">
            <div className="p-5 border-b border-slate-700 flex items-center justify-between">
              <h2 className="text-xl font-bold text-white flex items-center gap-2">
                <svg className="w-6 h-6 text-slate-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
                </svg>
                Data Management Logs
              </h2>
              <button
                onClick={() => setShowLogsModal(false)}
                className="p-2 text-slate-400 hover:text-white transition-colors"
              >
                <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                </svg>
              </button>
            </div>
            
            <div className="p-5 overflow-y-auto flex-1">
              {logsLoading ? (
                <div className="flex items-center justify-center py-12">
                  <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-white"></div>
                </div>
              ) : dataLogs.length === 0 ? (
                <div className="text-center py-12">
                  <svg className="w-16 h-16 mx-auto text-slate-600 mb-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
                  </svg>
                  <p className="text-slate-400">No import/restore logs found</p>
                </div>
              ) : (
                <div className="space-y-3">
                  {dataLogs.map((log, idx) => (
                    <div key={idx} className="bg-slate-900/50 rounded-lg p-4 border border-slate-700">
                      <div className="flex items-center justify-between mb-3">
                        <div className="flex items-center gap-3">
                          <span className={`px-2 py-1 rounded text-xs font-medium ${
                            log.log_type === 'import' 
                              ? 'bg-indigo-600 text-white' 
                              : 'bg-teal-600 text-white'
                          }`}>
                            {log.log_type === 'import' ? 'IMPORT' : 'RESTORE'}
                          </span>
                          <span className="text-slate-400 text-sm">
                            {log.timestamp ? new Date(log.timestamp).toLocaleString() : 'Unknown'}
                          </span>
                        </div>
                        <span className="text-slate-500 text-sm">
                          by {log.imported_by || log.restored_by || 'Unknown'}
                        </span>
                      </div>
                      
                      {/* Stats */}
                      {log.stats && (
                        <div className="grid grid-cols-4 gap-2 mb-3">
                          <div className="bg-slate-800 rounded p-2 text-center">
                            <p className="text-green-400 font-semibold">{log.stats.content_added || 0}</p>
                            <p className="text-slate-500 text-xs">Added</p>
                          </div>
                          <div className="bg-slate-800 rounded p-2 text-center">
                            <p className="text-blue-400 font-semibold">{log.stats.content_updated || 0}</p>
                            <p className="text-slate-500 text-xs">Updated</p>
                          </div>
                          <div className="bg-slate-800 rounded p-2 text-center">
                            <p className="text-slate-400 font-semibold">{log.stats.content_skipped || 0}</p>
                            <p className="text-slate-500 text-xs">Skipped</p>
                          </div>
                          <div className="bg-slate-800 rounded p-2 text-center">
                            <p className="text-red-400 font-semibold">{log.stats.content_validation_failed || 0}</p>
                            <p className="text-slate-500 text-xs">Failed</p>
                          </div>
                        </div>
                      )}
                      
                      {/* Log details */}
                      {(log.import_log || log.restore_log) && (
                        <details className="text-sm">
                          <summary className="text-slate-400 cursor-pointer hover:text-white">
                            View details
                          </summary>
                          <div className="mt-2 space-y-2 pl-4 border-l-2 border-slate-700">
                            {(log.import_log?.skipped_content || log.restore_log?.skipped_content || []).length > 0 && (
                              <div>
                                <p className="text-slate-500 text-xs mb-1">Skipped Content:</p>
                                {(log.import_log?.skipped_content || log.restore_log?.skipped_content).slice(0, 5).map((item, i) => (
                                  <p key={i} className="text-slate-400 text-xs">• {item.title}: {item.reason}</p>
                                ))}
                              </div>
                            )}
                            {(log.import_log?.validation_failures || log.restore_log?.validation_failures || []).length > 0 && (
                              <div>
                                <p className="text-red-400 text-xs mb-1">Validation Failures:</p>
                                {(log.import_log?.validation_failures || log.restore_log?.validation_failures).slice(0, 5).map((item, i) => (
                                  <p key={i} className="text-red-300 text-xs">• {item.title || item.name}: {item.errors?.join(', ')}</p>
                                ))}
                              </div>
                            )}
                            {(log.import_log?.conflicts_resolved || log.restore_log?.conflicts_resolved || []).length > 0 && (
                              <div>
                                <p className="text-amber-400 text-xs mb-1">Conflicts Resolved:</p>
                                {(log.import_log?.conflicts_resolved || log.restore_log?.conflicts_resolved).slice(0, 5).map((item, i) => (
                                  <p key={i} className="text-amber-300 text-xs">• {item.title}: {item.action}</p>
                                ))}
                              </div>
                            )}
                          </div>
                        </details>
                      )}
                    </div>
                  ))}
                </div>
              )}
            </div>
            
            <div className="p-4 border-t border-slate-700 flex justify-between">
              <button
                onClick={handleClearLogs}
                disabled={dataLogs.length === 0}
                className="px-4 py-2 bg-red-600 hover:bg-red-700 disabled:bg-slate-600 disabled:cursor-not-allowed text-white text-sm rounded-lg transition-colors"
              >
                Clear All Logs
              </button>
              <button
                onClick={() => setShowLogsModal(false)}
                className="px-4 py-2 text-slate-300 hover:text-white transition-colors"
              >
                Close
              </button>
            </div>
          </div>
        </div>
      )}

      {/* CSV Import Modal */}
      {showCsvImportModal && (
        <div className="fixed inset-0 bg-black/70 flex items-center justify-center z-50 p-4">
          <div className="bg-slate-800 rounded-xl border border-slate-700 w-full max-w-3xl max-h-[85vh] flex flex-col">
            <div className="p-5 border-b border-slate-700 flex items-center justify-between">
              <h2 className="text-xl font-bold text-white flex items-center gap-2">
                <svg className="w-6 h-6 text-green-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 16v1a3 3 0 003 3h10a3 3 0 003-3v-1m-4-8l-4-4m0 0L8 8m4-4v12" />
                </svg>
                Import CSV - {activeTab.charAt(0).toUpperCase() + activeTab.slice(1)}
              </h2>
              <button
                onClick={() => setShowCsvImportModal(false)}
                className="p-2 text-slate-400 hover:text-white transition-colors"
              >
                <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                </svg>
              </button>
            </div>
            
            <div className="p-5 overflow-y-auto flex-1">
              {/* File Upload Area */}
              {!csvImportFile && (
                <div className="border-2 border-dashed border-slate-600 rounded-lg p-8 text-center">
                  <svg className="w-12 h-12 mx-auto text-slate-500 mb-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M7 16a4 4 0 01-.88-7.903A5 5 0 1115.9 6L16 6a5 5 0 011 9.9M15 13l-3-3m0 0l-3 3m3-3v12" />
                  </svg>
                  <p className="text-slate-300 mb-2">Select a CSV file to import</p>
                  <p className="text-slate-500 text-sm mb-4">Only content for "{activeTab}" type will be imported</p>
                  <label className="inline-block px-4 py-2 bg-indigo-600 hover:bg-indigo-700 text-white rounded-lg cursor-pointer transition-colors">
                    Choose File
                    <input
                      type="file"
                      accept=".csv"
                      onChange={handleCsvFileSelect}
                      className="hidden"
                    />
                  </label>
                </div>
              )}
              
              {/* Loading State */}
              {csvImportFile && csvImportLoading && (
                <div className="text-center py-12">
                  <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-white mx-auto mb-4"></div>
                  <p className="text-slate-300">Analyzing CSV file...</p>
                </div>
              )}
              
              {/* Analysis Results */}
              {csvImportFile && csvImportAnalysis && !csvImportLoading && (
                <div className="space-y-4">
                  {/* File Info */}
                  <div className="bg-slate-900/50 rounded-lg p-4 border border-slate-700">
                    <div className="flex items-center justify-between">
                      <div className="flex items-center gap-3">
                        <svg className="w-8 h-8 text-green-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
                        </svg>
                        <div>
                          <p className="text-white font-medium">{csvImportFile.name}</p>
                          <p className="text-slate-400 text-sm">{(csvImportFile.size / 1024).toFixed(1)} KB</p>
                        </div>
                      </div>
                      <button
                        onClick={() => {
                          setCsvImportFile(null);
                          setCsvImportAnalysis(null);
                        }}
                        className="text-slate-400 hover:text-white transition-colors"
                      >
                        <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                        </svg>
                      </button>
                    </div>
                  </div>
                  
                  {/* Summary Stats */}
                  <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
                    <div className="bg-slate-900/50 rounded-lg p-3 border border-slate-700 text-center">
                      <p className="text-2xl font-bold text-white">{csvImportAnalysis.total_rows}</p>
                      <p className="text-slate-400 text-sm">Total Rows</p>
                    </div>
                    <div className="bg-slate-900/50 rounded-lg p-3 border border-green-700/50 text-center">
                      <p className="text-2xl font-bold text-green-400">{csvImportAnalysis.rows_to_create}</p>
                      <p className="text-slate-400 text-sm">To Create</p>
                    </div>
                    <div className="bg-slate-900/50 rounded-lg p-3 border border-blue-700/50 text-center">
                      <p className="text-2xl font-bold text-blue-400">{csvImportAnalysis.rows_to_update}</p>
                      <p className="text-slate-400 text-sm">To Update</p>
                    </div>
                    <div className="bg-slate-900/50 rounded-lg p-3 border border-red-700/50 text-center">
                      <p className="text-2xl font-bold text-red-400">{csvImportAnalysis.rows_with_errors}</p>
                      <p className="text-slate-400 text-sm">Errors</p>
                    </div>
                  </div>
                  
                  {/* Additional Info */}
                  {(csvImportAnalysis.rows_skipped_wrong_type > 0 || 
                    csvImportAnalysis.rows_requiring_geocoding > 0 || 
                    csvImportAnalysis.groups_to_create?.length > 0) && (
                    <div className="bg-slate-900/50 rounded-lg p-4 border border-slate-700 space-y-2">
                      {csvImportAnalysis.rows_skipped_wrong_type > 0 && (
                        <div className="flex items-center gap-2 text-amber-400">
                          <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z" />
                          </svg>
                          <span className="text-sm">{csvImportAnalysis.rows_skipped_wrong_type} rows have wrong content type (will be skipped)</span>
                        </div>
                      )}
                      {csvImportAnalysis.rows_requiring_geocoding > 0 && (
                        <div className="flex items-center gap-2 text-blue-400">
                          <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17.657 16.657L13.414 20.9a1.998 1.998 0 01-2.827 0l-4.244-4.243a8 8 0 1111.314 0z" />
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 11a3 3 0 11-6 0 3 3 0 016 0z" />
                          </svg>
                          <span className="text-sm">{csvImportAnalysis.rows_requiring_geocoding} rows will be auto-geocoded</span>
                        </div>
                      )}
                      {csvImportAnalysis.groups_to_create?.length > 0 && (
                        <div className="flex items-center gap-2 text-purple-400">
                          <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 11H5m14 0a2 2 0 012 2v6a2 2 0 01-2 2H5a2 2 0 01-2-2v-6a2 2 0 012-2m14 0V9a2 2 0 00-2-2M5 11V9a2 2 0 012-2m0 0V5a2 2 0 012-2h6a2 2 0 012 2v2M7 7h10" />
                          </svg>
                          <span className="text-sm">New groups: {csvImportAnalysis.groups_to_create.join(', ')}</span>
                        </div>
                      )}
                    </div>
                  )}
                  
                  {/* Errors List */}
                  {csvImportAnalysis.errors?.length > 0 && (
                    <div className="bg-red-900/20 rounded-lg p-4 border border-red-700/50">
                      <h3 className="text-red-400 font-medium mb-2 flex items-center gap-2">
                        <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8v4m0 4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
                        </svg>
                        Validation Errors ({csvImportAnalysis.errors.length})
                      </h3>
                      <div className="max-h-40 overflow-y-auto space-y-1">
                        {csvImportAnalysis.errors.slice(0, 20).map((err, idx) => (
                          <p key={idx} className="text-red-300 text-sm">
                            Row {err.row}: {err.field ? `[${err.field}] ` : ''}{err.message}
                          </p>
                        ))}
                        {csvImportAnalysis.errors.length > 20 && (
                          <p className="text-red-400 text-sm italic">...and {csvImportAnalysis.errors.length - 20} more errors</p>
                        )}
                      </div>
                    </div>
                  )}
                  
                  {/* Warnings List */}
                  {csvImportAnalysis.warnings?.length > 0 && (
                    <div className="bg-amber-900/20 rounded-lg p-4 border border-amber-700/50">
                      <h3 className="text-amber-400 font-medium mb-2 flex items-center gap-2">
                        <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z" />
                        </svg>
                        Warnings ({csvImportAnalysis.warnings.length})
                      </h3>
                      <div className="max-h-32 overflow-y-auto space-y-1">
                        {csvImportAnalysis.warnings.slice(0, 10).map((warn, idx) => (
                          <p key={idx} className="text-amber-300 text-sm">
                            Row {warn.row}: {warn.message}
                          </p>
                        ))}
                      </div>
                    </div>
                  )}
                  
                  {/* Preview Table */}
                  {csvImportAnalysis.preview?.length > 0 && (
                    <div className="bg-slate-900/50 rounded-lg p-4 border border-slate-700">
                      <h3 className="text-white font-medium mb-3">Preview (first {csvImportAnalysis.preview.length} valid rows)</h3>
                      <div className="overflow-x-auto">
                        <table className="w-full text-sm">
                          <thead>
                            <tr className="text-slate-400 border-b border-slate-700">
                              <th className="text-left py-2 px-2">Row</th>
                              <th className="text-left py-2 px-2">Title</th>
                              <th className="text-left py-2 px-2">Status</th>
                              <th className="text-left py-2 px-2">Action</th>
                              <th className="text-left py-2 px-2">Group</th>
                            </tr>
                          </thead>
                          <tbody>
                            {csvImportAnalysis.preview.map((row, idx) => (
                              <tr key={idx} className="border-b border-slate-800">
                                <td className="py-2 px-2 text-slate-400">{row.row_num}</td>
                                <td className="py-2 px-2 text-white truncate max-w-[200px]">{row.title}</td>
                                <td className="py-2 px-2">
                                  <span className={`px-2 py-0.5 rounded text-xs ${
                                    row.status === 'active' ? 'bg-green-600 text-white' :
                                    row.status === 'draft' ? 'bg-slate-600 text-white' :
                                    'bg-amber-600 text-white'
                                  }`}>
                                    {row.status}
                                  </span>
                                </td>
                                <td className="py-2 px-2">
                                  <span className={`px-2 py-0.5 rounded text-xs ${
                                    row.is_update ? 'bg-blue-600 text-white' : 'bg-green-600 text-white'
                                  }`}>
                                    {row.is_update ? 'Update' : 'Create'}
                                  </span>
                                </td>
                                <td className="py-2 px-2 text-slate-300 truncate max-w-[120px]">{row.group_name || '-'}</td>
                              </tr>
                            ))}
                          </tbody>
                        </table>
                      </div>
                    </div>
                  )}
                </div>
              )}
            </div>
            
            {/* Footer */}
            <div className="p-4 border-t border-slate-700 flex justify-end gap-3">
              <button
                onClick={() => setShowCsvImportModal(false)}
                className="px-4 py-2 text-slate-300 hover:text-white transition-colors"
              >
                Cancel
              </button>
              <button
                onClick={handleCsvImport}
                disabled={csvImportLoading || !csvImportFile || !csvImportAnalysis || csvImportAnalysis.valid_rows === 0}
                className="px-4 py-2 bg-green-600 hover:bg-green-700 disabled:bg-slate-600 disabled:cursor-not-allowed text-white rounded-lg transition-colors flex items-center gap-2"
              >
                {csvImportLoading ? (
                  <>
                    <svg className="w-4 h-4 animate-spin" fill="none" viewBox="0 0 24 24">
                      <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                      <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                    </svg>
                    Importing...
                  </>
                ) : (
                  <>
                    <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 16v1a3 3 0 003 3h10a3 3 0 003-3v-1m-4-8l-4-4m0 0L8 8m4-4v12" />
                    </svg>
                    Import {csvImportAnalysis?.valid_rows || 0} Rows
                  </>
                )}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* CSV Export Modal */}
      {showCsvExportModal && (() => {
        const tabGroups = groups.filter(g => g.type === activeTab && g.id !== 'ungrouped');
        const ungroupedGroup = groups.find(g => g.id === 'ungrouped' && g.type === activeTab);
        const hasUngrouped = ungroupedGroup && (ungroupedGroup.item_count || 0) > 0;
        const allGroupIds = [...tabGroups.map(g => g.id), ...(hasUngrouped ? ['ungrouped'] : [])];
        const allSelected = allGroupIds.length > 0 && allGroupIds.every(id => selectedExportGroups.includes(id));
        
        // Calculate selected item count from group.item_count
        const selectedItemCount = selectedExportGroups.reduce((total, gId) => {
          if (gId === 'ungrouped') return total + (ungroupedGroup?.item_count || 0);
          const g = tabGroups.find(g => g.id === gId);
          return total + (g?.item_count || 0);
        }, 0);
        
        return (
        <div className="fixed inset-0 bg-black/70 flex items-center justify-center z-50 p-4">
          <div className="bg-slate-800 rounded-xl border border-slate-700 w-full max-w-md">
            <div className="p-5 border-b border-slate-700 flex items-center justify-between">
              <h2 className="text-xl font-bold text-white flex items-center gap-2">
                <svg className="w-6 h-6 text-blue-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 16v1a3 3 0 003 3h10a3 3 0 003-3v-1m-4-4l-4 4m0 0l-4-4m4 4V4" />
                </svg>
                Export to CSV
              </h2>
              <button
                onClick={() => setShowCsvExportModal(false)}
                className="p-2 text-slate-400 hover:text-white transition-colors"
              >
                <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                </svg>
              </button>
            </div>
            
            <div className="p-5 space-y-4">
              {/* Content Type */}
              <div className="flex items-center justify-between">
                <span className="text-slate-400 text-sm">Content Type</span>
                <span className="text-white font-medium text-sm capitalize px-3 py-1 bg-purple-600/30 rounded-lg">
                  {activeTab === 'local' ? 'Local Items' : 
                   activeTab === 'deal' ? 'Deals & Offers' :
                   activeTab === 'event' ? 'Events & Expos' : 'Advertisements'}
                </span>
              </div>
              
              {/* Group Selection */}
              <div className="bg-slate-900/50 rounded-lg border border-slate-700">
                <div className="px-4 py-3 border-b border-slate-700 flex items-center justify-between">
                  <h3 className="text-white font-medium text-sm">Select Groups to Export</h3>
                  <button
                    onClick={() => {
                      if (allSelected) {
                        setSelectedExportGroups([]);
                      } else {
                        setSelectedExportGroups([...allGroupIds]);
                      }
                    }}
                    className="text-xs text-purple-400 hover:text-purple-300 font-medium transition-colors"
                    data-testid="csv-export-select-all"
                  >
                    {allSelected ? 'Deselect All' : 'Select All'}
                  </button>
                </div>
                <div className="max-h-60 overflow-y-auto p-2 space-y-1">
                  {allGroupIds.length === 0 ? (
                    <p className="text-slate-500 text-sm text-center py-3">No groups in this tab</p>
                  ) : (
                    <>
                      {tabGroups.map(group => {
                        const itemCount = group.item_count || 0;
                        const isChecked = selectedExportGroups.includes(group.id);
                        return (
                          <label
                            key={group.id}
                            className={`flex items-center gap-3 px-3 py-2.5 rounded-lg cursor-pointer transition-colors ${
                              isChecked ? 'bg-purple-600/15' : 'hover:bg-slate-700/50'
                            }`}
                          >
                            <input
                              type="checkbox"
                              checked={isChecked}
                              onChange={() => {
                                setSelectedExportGroups(prev => 
                                  prev.includes(group.id) 
                                    ? prev.filter(id => id !== group.id)
                                    : [...prev, group.id]
                                );
                              }}
                              className="w-4 h-4 rounded bg-slate-700 border-slate-600 text-purple-600 focus:ring-purple-500"
                            />
                            <span className="text-white text-sm flex-1 truncate">{group.name}</span>
                            <span className="text-slate-500 text-xs">{itemCount} item{itemCount !== 1 ? 's' : ''}</span>
                          </label>
                        );
                      })}
                      {hasUngrouped && (
                        <label
                          className={`flex items-center gap-3 px-3 py-2.5 rounded-lg cursor-pointer transition-colors ${
                            selectedExportGroups.includes('ungrouped') ? 'bg-purple-600/15' : 'hover:bg-slate-700/50'
                          }`}
                        >
                          <input
                            type="checkbox"
                            checked={selectedExportGroups.includes('ungrouped')}
                            onChange={() => {
                              setSelectedExportGroups(prev => 
                                prev.includes('ungrouped') 
                                  ? prev.filter(id => id !== 'ungrouped')
                                  : [...prev, 'ungrouped']
                              );
                            }}
                            className="w-4 h-4 rounded bg-slate-700 border-slate-600 text-purple-600 focus:ring-purple-500"
                          />
                          <span className="text-slate-400 text-sm italic flex-1">Ungrouped</span>
                          <span className="text-slate-500 text-xs">
                            {ungroupedGroup?.item_count || 0} item{(ungroupedGroup?.item_count || 0) !== 1 ? 's' : ''}
                          </span>
                        </label>
                      )}
                    </>
                  )}
                </div>
              </div>
              
              {/* Selected summary */}
              <div className="flex items-center justify-between text-sm">
                <span className="text-slate-400">
                  {selectedExportGroups.length} of {allGroupIds.length} group{allGroupIds.length !== 1 ? 's' : ''} selected
                </span>
                <span className="text-white font-medium">{selectedItemCount} item{selectedItemCount !== 1 ? 's' : ''} to export</span>
              </div>
              
              {/* File Info */}
              <div className="bg-slate-900/50 rounded-lg p-3 border border-slate-700">
                <div className="flex items-center gap-3">
                  <div className="p-2 bg-green-600/20 rounded-lg">
                    <svg className="w-5 h-5 text-green-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
                    </svg>
                  </div>
                  <div>
                    <p className="text-white text-sm font-medium">
                      explore_{activeTab}_{new Date().toISOString().split('T')[0]}.csv
                    </p>
                    <p className="text-slate-400 text-xs">CSV file with UTF-8 encoding</p>
                  </div>
                </div>
              </div>
            </div>
            
            {/* Footer */}
            <div className="p-4 border-t border-slate-700 flex justify-end gap-3">
              <button
                onClick={() => setShowCsvExportModal(false)}
                className="px-4 py-2 text-slate-300 hover:text-white transition-colors"
              >
                Cancel
              </button>
              <button
                onClick={() => {
                  setShowCsvExportModal(false);
                  handleCsvExport();
                }}
                disabled={csvExportLoading || selectedExportGroups.length === 0}
                className="px-4 py-2 bg-blue-600 hover:bg-blue-700 disabled:bg-slate-600 disabled:cursor-not-allowed text-white rounded-lg transition-colors flex items-center gap-2"
                data-testid="csv-export-confirm-btn"
              >
                {csvExportLoading ? (
                  <>
                    <svg className="w-4 h-4 animate-spin" fill="none" viewBox="0 0 24 24">
                      <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                      <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                    </svg>
                    Exporting...
                  </>
                ) : (
                  <>
                    <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 16v1a3 3 0 003 3h10a3 3 0 003-3v-1m-4-4l-4 4m0 0l-4-4m4 4V4" />
                    </svg>
                    Export CSV
                  </>
                )}
              </button>
            </div>
          </div>
        </div>
        );
      })()}
    </div>
  );
};

export default ExploreHubPage;
